/* ============================================================================
   לוגיקת הדומיין — חולצה מ-rental-dashboard-v16.jsx ללא שינוי התנהגותי,
   פרט לשעון (ראו refreshClock למטה). משותפת ל-/web ול-/server כדי ששני
   הצדדים יחשבו חובות, מחזורי חיוב ותקופות שכירות בדיוק אותו הדבר.

   אין לייבא כאן שום דבר מהדפדפן — הקובץ הזה חייב לרוץ גם ב-Node.
   ============================================================================ */

const MONTHS = ["ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני", "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"];
const MONTHS_SHORT = ["ינו", "פבר", "מרץ", "אפר", "מאי", "יונ", "יול", "אוג", "ספט", "אוק", "נוב", "דצמ"];
const CATS = ["תחזוקה", "ועד בית", "ארנונה", "ביטוח", "תיווך", "הלוואה", "אחר"];
/* האייקון של כל סעיף עבר ל-web/src/theme.js — כאן נשאר רק מה שהשרת צריך.
   ה-color נשאר כי הדוחות והגרפים משתמשים בו כמפתח נתונים ולא רק לתצוגה. */
const BILLS = [
  { k: "vaad", label: "ועד בית", color: "#6B7F8C" },
  { k: "arnona", label: "ארנונה", color: "#7A5C8E" },
  { k: "municipal", label: "היטל עירוני", color: "#8E6C5C" },
  { k: "gas", label: "גז", color: "#C2622B" },
  { k: "elec", label: "חשמל", color: "#B5822B" },
  // מים וביוב הם חשבון אחד של תאגיד המים ותעריף אחד (waterSewageRate).
  // "sewage" נשאר כדי שנתונים ישנים שנרשמו בו בנפרד לא ייעלמו.
  { k: "water", label: "מים וביוב", color: "#2C7A99" },
  { k: "sewage", label: "ביוב (ישן)", color: "#4A6572" },
];

/* השוואת טקסט חופשי: מסיר גרשיים ופיסוק ומאחד רווחים. מוגדר כאן ולא
   למטה כי טבלאות הכינויים נבנות ממנו בזמן טעינת המודול. */
const norm = (t) => String(t || "").replace(/["'\u05f3\u05f4,.\-]/g, " ").replace(/\s+/g, " ").trim().toLowerCase();

/* ============================================================================
   אמצעי התשלום.

   בעל הנכס מסמן כל חודש שהתקבל — אבל "התקבל" לבדו לא מספיק כשמגיעים
   להתחשבנות: שכר דירה מגיע בהוראת קבע או בשיק, חשבונות הצריכה בביט,
   בפייבוקס או במזומן, וועד הבית בהוראת קבע או בהעברה. בלי לרשום איך,
   אי אפשר להצליב מול דף הבנק, ומזומן נעלם בלי זכר.

   האמצעי נשמר על התשלום עצמו ולא על הנכס, כי הוא משתנה מחודש לחודש.
   ברירות המחדל בהגדרות הן מה שהופך את הסימון לחזרה של לחיצה אחת.
   ============================================================================ */
const PAY_METHODS = [
  { k: "standing", label: "הוראת קבע", short: "ה״ק", traceable: true },
  { k: "transfer", label: "העברה בנקאית", short: "העברה", traceable: true },
  { k: "bit", label: "ביט", short: "ביט", traceable: true },
  { k: "paybox", label: "פייבוקס", short: "פייבוקס", traceable: true },
  { k: "check", label: "שיק", short: "שיק", traceable: true },
  { k: "cash", label: "מזומן", short: "מזומן", traceable: false },
  { k: "card", label: "אשראי", short: "אשראי", traceable: true },
  { k: "other", label: "אחר", short: "אחר", traceable: false },
];

const PAY_METHOD_BY_KEY = Object.fromEntries(PAY_METHODS.map((x) => [x.k, x]));
const methodLabel = (k) => PAY_METHOD_BY_KEY[k]?.label || "";

/* איך המשתמש והעוזר קוראים לזה בפועל.

   המפתחות עוברים norm בעצמם ולא רק הקלט: norm מחליף גרש ברווח, ולכן
   "צ׳ק" הופך ל-"צ ק" ומפתח גולמי כמו "צק" לעולם לא היה נתפס. */
const METHOD_ALIASES_RAW = {
  "הוראת קבע": "standing", "הוק": "standing", "הק": "standing", "הוראה": "standing",
  standing_order: "standing", "standing order": "standing", "הוראת קבע בנקאית": "standing",
  "העברה": "transfer", "העברה בנקאית": "transfer", "העברה לחשבון": "transfer",
  "בנק": "transfer", transfer: "transfer", "זיכוי": "transfer", wire: "transfer",
  "ביט": "bit", bit: "bit",
  "פייבוקס": "paybox", paybox: "paybox", "פיי בוקס": "paybox",
  "שיק": "check", "צק": "check", "צ׳ק": "check", check: "check", cheque: "check", "המחאה": "check",
  // ברבים — "12 שיקים", "שקים" (כמו שמקלידים בטלפון), "צ׳קים".
  "שיקים": "check", "שקים": "check", "שק": "check", "צקים": "check", "צ׳קים": "check", "שיקים חודשיים": "check", checks: "check",
  "מזומן": "cash", cash: "cash", "כסף": "cash",
  "אשראי": "card", "כרטיס אשראי": "card", card: "card", "כרטיס": "card",
  "אחר": "other", other: "other",
};

const METHOD_ALIASES = Object.fromEntries(
  Object.entries(METHOD_ALIASES_RAW).map(([k, v]) => [norm(k), v]),
);

/* שלוש קבוצות ולא אחת, כי בפועל גובים אותן אחרת: שכר דירה, ועד בית,
   ושאר חשבונות הצריכה. */
const methodGroup = (kind) => (kind === "rent" ? "rent" : kind === "vaad" ? "vaad" : "bills");

/**
 * האמצעי שיירשם בפועל: מה שנבחר, אחרת מה שכבר היה רשום, אחרת ברירת המחדל.
 *
 * שמירה על הקיים חשובה — עדכון סכום של חשבון שכבר סומן כשולם לא אמור
 * להחליף בשקט את אמצעי התשלום שנרשם בו.
 */
function payMethodOf(picked, settings, kind, existing = null, apt = null) {
  const clean = (v) => {
    const n = String(v || "").trim().toLowerCase();
    if (PAY_METHOD_BY_KEY[n]) return n;
    return METHOD_ALIASES[norm(v)] || null;
  };
  return clean(picked) || clean(existing) || groupMethodOf(apt, settings, kind);
}

/**
 * אמצעי התשלום של שכר הדירה לשכירות נתונה: מה שסוכם מול הדייר (payMethodRent
 * על התקופה), ואם לא סוכם — ברירת המחדל מההגדרות. rentChecks (מספר השיקים
 * כשמשלמים בשיקים) יושב לידו; כשהוא 0 החוזה כותב שיק לכל חודש.
 */
const rentMethodOf = (apt, settings) => groupMethodOf(apt, settings, "rent");

/* השדה שעל התקופה לכל קבוצה — מה שסוכם מול הדייר הזה. */
const GROUP_METHOD_FIELD = { rent: "payMethodRent", vaad: "payMethodVaad", bills: "payMethodBills" };

/**
 * האמצעי המוסכם לשכירות נתונה: מה שסוכם מול הדייר (על התקופה, משוקף לנכס),
 * ואם לא סוכם — ברירת המחדל מההגדרות. עד 24.8.2026 הסיכום פר-דייר היה
 * קיים רק לשכר הדירה; עכשיו גם לחשבונות הצריכה ולוועד הבית.
 */
function groupMethodOf(apt, settings, kind) {
  const own = apt?.[GROUP_METHOD_FIELD[methodGroup(kind)]];
  return PAY_METHOD_BY_KEY[own] ? own : defaultMethod(settings, kind);
}

/** אמצעי ברירת המחדל לקבוצה, מההגדרות. */
function defaultMethod(settings, kind) {
  const s = settings || {};
  const g = methodGroup(kind);
  const picked = g === "rent" ? s.payMethodRent : g === "vaad" ? s.payMethodVaad : s.payMethodBills;
  return PAY_METHOD_BY_KEY[picked] ? picked : (g === "bills" ? "bit" : "standing");
}

/* סעיפים בסכום קבוע חודשי שנקבע על הנכס, להבדיל מסעיפים לפי מונה.
   לכל אחד דגל הפעלה משלו, כי ברוב השכירויות בישראל הדייר משלם ארנונה
   ישירות לרשות ולא דרך בעל הנכס — ולכן ברירת המחדל היא כבוי. */
const FIXED_BILLS = [
  { k: "vaad", label: "ועד בית", amountField: "vaad", activeField: "vaadActive" },
  { k: "arnona", label: "ארנונה", amountField: "arnona", activeField: "arnonaActive" },
  { k: "municipal", label: "היטל עירוני", amountField: "municipal", activeField: "municipalActive" },
];

/** האם סעיף קבוע פעיל בנכס — יש לו סכום והוא לא כובה. */
const fixedBillOn = (apt, k) => {
  const f = FIXED_BILLS.find((x) => x.k === k);
  if (!f) return false;
  return apt?.[f.activeField] === true && Number(apt?.[f.amountField] || 0) > 0;
};

/* ============================================================================
   מי משלם למי על כל סעיף צריכה.

   קודם זו הייתה החלטה אחת לכל הנכס, נגזרת מסוגו: "יחידת דיור" = הכול דרך
   המשכיר, "דירה" = הכול ישירות מול הספקים. בפועל התמהיל מעורב כמעט תמיד —
   חשמל ישירות מול חברת החשמל אבל מים דרך מונה משנה של המשכיר, או גז
   בבלונים בזמן שהשאר על שם השוכר. לכן ההחלטה כאן היא לכל סעיף בנפרד.

   שלושה מצבים:
     "landlord" — משולם למשכיר. החשבון מגיע אליו והוא גובה מהשוכר לפי
                  מונה או בסכום קבוע. זה המצב היחיד שבו נוצר חיוב.
     "direct"   — השוכר רושם את עצמו אצל הספק ומשלם לו ישירות. המונה
                  מתועד אצלנו — כניסה, יציאה וקריאות שוטפות — אבל התיעוד
                  אינו למטרת חיוב אלא כראיה למצב המונה במסירה.
     "none"     — אין חיוב על הסעיף. אין מונה ואין מה לגבות: או שהעלות
                  כלולה בדמי השכירות, או שהסעיף לא קיים בנכס הזה.

   ההבחנה בין "direct" ל-"none" אינה קוסמטית. ב-direct יש צד שלישי שיכול
   לנתק את המושכר בגלל חוב, ולכן החוזה חייב לומר שהשוכר מעביר את החשבון
   על שמו, מה קורה אם לא שילם, ומה מבטיח את זה. ב-none אין חוב ואין
   ספק — וסעיף התחייבות כזה בחוזה הוא סתם רעש שמבלבל את שני הצדדים.
   ============================================================================ */

const UTILITY_MODES = [
  { k: "landlord", label: "משולם לי", hint: "קריאת מונה וחיוב חודשי" },
  { k: "direct", label: "ישירות לספק", hint: "השוכר רשום אצל הספק; המונה לתיעוד בלבד" },
  { k: "none", label: "ללא חיוב", hint: "אין מונה ואין גבייה" },
];

const UTILITY_MODE_KEYS = UTILITY_MODES.map((m) => m.k);

const UTILITIES = [
  { k: "elec", label: "חשמל", supplier: "חברת החשמל", meter: "elec", unit: 'קוט"ש' },
  { k: "water", label: "מים וביוב", supplier: "תאגיד המים והביוב", meter: "water", unit: 'מ"ק' },
  { k: "gas", label: "גז", supplier: "ספק הגז", meter: "gas", unit: 'מ"ק' },
  // ארנונה אינה נמדדת במונה — היא נקבעת על ידי הרשות לפי שטח וסיווג.
  { k: "arnona", label: "ארנונה", supplier: "הרשות המקומית", meter: null, unit: "" },
];

const UTILITY_BY_KEY = Object.fromEntries(UTILITIES.map((u) => [u.k, u]));

/** מפתח הסעיף לפי מונה — הקריאות מדברות ב-elec/water/gas. */
const utilityOfMeter = (meter) => UTILITIES.find((u) => u.meter === meter) || null;

/**
 * "direct" או "landlord" לסעיף נתון.
 *
 * ברירת המחדל נגזרת ממה שהיה נכון לפני שהשדה קיים, כדי שנכסים ותיקים
 * ימשיכו להתנהג בדיוק כמו קודם: ארנונה לפי דגל הגבייה שלה, והשאר לפי
 * סוג הנכס.
 */
function utilityMode(apt, k) {
  const chosen = apt?.utilities?.[k];
  if (UTILITY_MODE_KEYS.includes(chosen)) return chosen;
  if (k === "arnona") return fixedBillOn(apt, "arnona") ? "landlord" : "direct";
  // ועד בית: בלי ברירת מחדל ל"ישירות" — מסמכים ישנים בלי ועד פעיל פשוט
  // אין להם ועד, לא שהשוכר משלם אותו ישירות. "direct" מגיע רק מבחירה
  // מפורשת באשף (utilities.vaad), שנתפסת כבר למעלה.
  if (k === "vaad") return fixedBillOn(apt, "vaad") ? "landlord" : "none";
  return propertyType(apt).metered ? "landlord" : "direct";
}

const paysDirect = (apt, k) => utilityMode(apt, k) === "direct";

/** הסעיפים שהשוכר משלם ישירות לספק — לניסוח החוזה ולתצוגה. */
const directUtilities = (apt) => UTILITIES.filter((u) => paysDirect(apt, u.k));

/**
 * האם יש בכלל מונה לעקוב אחריו.
 *
 * "ללא חיוב" הוא גם "בלי מונה": אין מה לתעד ואין מה להשוות מולו, ולכן
 * הסעיף נעלם מהמסכים ומהאשף במקום להישאר שדה ריק שמבלבל.
 */
const tracksMeter = (apt, k) => {
  const u = UTILITY_BY_KEY[k];
  return !!(u && u.meter && utilityMode(apt, k) !== "none");
};

/**
 * האם נוצר חיוב חודשי על הסעיף הזה.
 *
 * שלוש סיבות לא לחייב: השוכר משלם ישירות לספק, אין חיוב בכלל, או שאין
 * תעריף (גז בבלונים למשל). קריאת מונה נשמרת בשניים הראשונים — היא
 * התיעוד — אבל בלי חיוב.
 */
function billsUtility(apt, settings, k) {
  if (utilityMode(apt, k) !== "landlord") return false;
  const u = UTILITY_BY_KEY[k];
  if (!u) return false;
  if (!u.meter) return fixedBillOn(apt, k);
  return rateFor(apt, settings, u.meter) > 0;
}

/** אותו דבר, כשמה שביד הוא מפתח המונה ולא מפתח הסעיף. */
function billsMeter(apt, settings, meter) {
  const u = utilityOfMeter(meter);
  return u ? billsUtility(apt, settings, u.k) : false;
}

const pad = (n) => String(n).padStart(2, "0");
const nis = (n) => "₪" + Math.round(n || 0).toLocaleString("he-IL");
const mKey = (y, m) => `${y}-${pad(m)}`;
const localDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/* השעון נקבע מחדש בכל בקשה בשרת (תהליך ארוך-חיים) ופעם אחת בדפדפן.
   התאריך מחושב לפי השעון המקומי ולא ב-UTC — בישראל התאריך ב-UTC כבר
   אתמול בין חצות לשלוש לפנות בוקר, וזה מזיז את בדיקות מועד התשלום. */
let now = new Date();
let CUR_Y = now.getFullYear(), CUR_M = now.getMonth() + 1;
let CUR_KEY = mKey(CUR_Y, CUR_M);
let TODAY = localDay(now);

function refreshClock(at = new Date()) {
  now = at;
  CUR_Y = now.getFullYear();
  CUR_M = now.getMonth() + 1;
  CUR_KEY = mKey(CUR_Y, CUR_M);
  TODAY = localDay(now);
  return TODAY;
}
const rowKey = (id, mk) => `${id}|${mk}`;
const uid = () => Math.random().toString(36).slice(2, 9);
const prevMonth = (mk) => { const [y, m] = mk.split("-").map(Number); return m === 1 ? mKey(y - 1, 12) : mKey(y, m - 1); };
const monthLabel = (mk) => { const [y, m] = mk.split("-").map(Number); return `${MONTHS[m - 1]} ${y}`; };
const heDate = (d) => (d ? new Date(d).toLocaleDateString("he-IL") : "—");

/* ============================================================================
   קלט תאריך בסדר יום/חודש/שנה.

   <input type="date"> מציג את התאריך לפי הגדרת השפה של הדפדפן ולא לפי
   השפה של הדף: דפדפן שמוגדר אנגלית מראה 08/16/2026 גם ב-lang="he", ואין
   תכונה שמשנה את זה. משתמש שרואה חודש במקום יום מזין תאריך שגוי בלי לדעת.

   לכן ההקלדה עוברת דרך שדה טקסט משלנו, והפונקציות כאן הן התרגום בין מה
   שרואים לבין מה שנשמר. הערך השמור נשאר ISO תמיד.
   ============================================================================ */

/** "2026-08-16" → "16/08/2026" */
const dmy = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

/**
 * "16/08/2026" → "2026-08-16", או null אם אינו תאריך שלם וחוקי.
 * מקבל גם 16.8.26 ו-16-8-2026 — אנשים מקלידים את שלושתם.
 */
const parseDmy = (text) => {
  const parts = String(text || "").split(/[/.\-\s]+/).filter(Boolean);
  if (parts.length !== 3) return null;

  const [d, mo, rawY] = parts.map((p) => parseInt(p, 10));
  if (![d, mo, rawY].every(Number.isFinite)) return null;

  const y = rawY < 100 ? 2000 + rawY : rawY;
  if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 1900 || y > 2200) return null;

  // 31/02 נבלע על ידי Date ומוחזר כ-03/03. בדיקת חזרה חוסמת את זה.
  const iso = `${y}-${pad(mo)}-${pad(d)}`;
  const back = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(back.getTime()) || back.getDate() !== d || back.getMonth() + 1 !== mo) return null;
  return iso;
};
const addYears = (d, n) => { const x = new Date(d); x.setFullYear(x.getFullYear() + n); x.setDate(x.getDate() - 1); return x.toISOString().slice(0, 10); };

const emptyItem = () => ({ amount: 0, paid: false, paidAmount: 0 });
const emptyBill = () => ({
  elecRead: null, waterRead: null, elecPhoto: null, waterPhoto: null,
  elec: emptyItem(), water: emptyItem(), sewage: emptyItem(), vaad: emptyItem(),
  gas: emptyItem(), arnona: emptyItem(), municipal: emptyItem(),
});
/* ששולם בפועל על סעיף — תומך בתשלום חלקי ובתשלום עודף */
const baseItemPaid = (it) => (it ? (it.paidAmount != null ? Number(it.paidAmount) : (it.paid ? Number(it.amount || 0) : 0)) : 0);
/* חודש עם 2 דיירים (החלפה באמצע חודש) מחזיק את התקופה השנייה תחת
   entries, בלי לגעת בשדות הרגילים של הראשונה. itemPaid/itemDue מסכמים
   את שתיהן, כדי שדוחות וכרטיסי חוב יראו את הסכום האמיתי בלי לדעת
   שקיים entries בכלל — ובלי entries הן מתנהגות בדיוק כמו היום. */
const itemPaid = (it) => baseItemPaid(it) + (it?.entries || []).reduce((s, e) => s + baseItemPaid(e), 0);
const itemAmount = (it) => Number(it?.amount || 0) + (it?.entries || []).reduce((s, e) => s + Number(e.amount || 0), 0);
const itemDue = (it) => itemAmount(it) - itemPaid(it);
/* מבודד את הפלח של תקופה ספציפית מתוך רשומה שעשויה להכיל entries של
   יותר מתקופה אחת — לוח הגבייה קורא לזה כדי להציג לכל שורה רק את מה
   ששייך לתקופה שלה. isPrimary נקבע מול tenancyForMonth אצל הקורא. */
const entryFor = (record, tenancyId, isPrimary) => {
  if (!record) return null;
  /* גם התקופה הראשית מקבלת רשומה מבודדת: entries שייכים לתקופה השנייה,
     ו-itemPaid/itemDue מסכמים אותם — בלי ההפשטה הזו שורת התקופה הראשית
     הייתה מציגה את מה שהתקופה השנייה שילמה/חייבת כאילו הוא שלה. */
  if (isPrimary) {
    if (!record.entries) return record;
    const { entries, ...rest } = record;
    return rest;
  }
  return (record.entries || []).find((e) => e.tenancyId === tenancyId) || null;
};
/* החשבונות של חודש הם תשלום אחד מבחינת הדייר; החלוקה לסעיפים היא
   הנהלת החשבונות של בעל הנכס. לכן עודף על סעיף אחד (חשמל ששולם 130 ואחר
   כך עודכן ל-65) מקזז סעיף אחר באותו חודש — אחרת הדייר "חייב" 140 על
   מים אחרי ששילם 150 על חשבונות של 225. מול שכר הדירה אין קיזוז: ערוץ
   תשלום אחר, והפרדה מפורשת בכרטיס החוב. */
const billsDue = (b) => Math.max(0, BILLS.reduce((t, x) => t + itemDue(b[x.k]), 0));
/** כמה כבר התקבל על החשבונות בחודש — לביטול סימון ולתצוגה. */
const billsPaidTotal = (b) => BILLS.reduce((t, x) => t + itemPaid(b[x.k]), 0);
const rentPaidOf = (d, aptId, mk_, tenancyId) => {
  const rec = d.payments[rowKey(aptId, mk_)];
  if (tenancyId === undefined) {
    return Number(rec?.amount || 0) + (rec?.entries || []).reduce((s, e) => s + Number(e.amount || 0), 0);
  }
  const isPrimary = tenancyForMonth(d, aptId, mk_)?.id === tenancyId;
  return Number(entryFor(rec, tenancyId, isPrimary)?.amount || 0);
};
const rentDue = (a, d, mk_) => Math.max(0, Number(a.rent || 0) - rentPaidOf(d, a.id, mk_));

const defTariffs = () => ({
  elecRate: 0.65, waterSewageRate: 16, gasFixed: 0,
  waterRate: 0, sewageRate: 0,   // ישן — נשמר לנתונים קיימים בלבד
  // פיקדון ברירת מחדל: 12 חודשי שכירות. חשבונות קיימים שמרו את הערך
  // שלהם בהגדרות ואינם מושפעים — זו ברירת המחדל לחשבון חדש בלבד.
  // פיקדון ברירת מחדל 5 חודשי שכירות (12 הפיקו "ביטחונות ₪62,400" בחוזה — 23.8.2026).
  lateFeePct: 0, indexPct: 0, depositMonths: 5,
  paymentDay: 1,   // יום תשלום שכר דירה — ברירת מחדל לנכס חדש
  billsDay: 15,    // יום תשלום חשבונות — ברירת מחדל
  billsCycle: 1,   // 1 = חודשי, 2 = דו-חודשי
  gasRate: 0,      // ₪ למ"ק גז; 0 = חיוב קבוע לפי gasFixed
  ownerName: "",   // בעל הנכס — מופיע בחוזה ובפניות הדייר
  ownerPhone: "", ownerId: "", ownerAddress: "", ownerEmail: "", ownerSignature: null,
  payBank: "", payBranch: "", payAccount: "", payBit: "", ownerSignatureId: null,
  waterRateAbove: 0,   // תעריף מעבר למכסה
  waterQuota: 0,       // מ"ק לנפש לחודש
  securityCheck: 0, paintDeposit: 0, cleaningDeposit: 0,
  noticeMonths: 3, lateVacateFee: 500,
  /* אומדן מס על שכר דירה (rentTax). תקרת הפטור החודשית למגורים —
     5,654 ₪ בשנים 2024–2026; רשות המיסים מעדכנת אותה בתחילת כל שנה,
     ולכן היא ניתנת לשינוי בהגדרות ודרך העוזר (set_tariff). שיעור המס
     השולי הוא של בעל הנכס: מינימום 31% להכנסה פסיבית מתחת לגיל 60,
     לרוב 10% מגיל 60 ומעלה. */
  taxCeiling: 5654, taxMarginalRate: 31,
  /* סעיפי בית אופציונליים בחוזה. הנוסח המקורי כלל אותם קשיח — שמירת
     שבת, איסור מנגל על הדשא, מערכת הגברה בחצר — כללי בית של נכס אחד
     שהוטבעו בחוזה של כל משתמש. ברירת המחדל כאן היא "כן" כדי שחוזים
     קיימים לא ישתנו; חשבון חדש נפתח בלעדיהם (ראו emptyDoc בשרת). */
  clauseShabbat: true, clauseYard: true, houseRules: "",
  /* נוסח חוזה בכתיבה חופשית — ברירת מחדל. ריק כברירת מחדל, ולכן "חוזה
     בכתיבה חופשית" ממשיך להתחיל מדף ריק בדיוק כמו היום עד שמישהו ממלא
     כאן משהו. לא נוגע בחוזה שכבר קיים או ב-customText שכבר נשמר. */
  contractTemplate: "",
  // ברירות המחדל לסימון תשלום — ראו PAY_METHODS.
  payMethodRent: "standing", payMethodBills: "bit", payMethodVaad: "standing",
  /* אימות לפני חתימת הדייר על החוזה: "none" (ברירת מחדל — כמו תמיד),
     "tenantId" או "phone" — הדייר מקליד את הת"ז/הטלפון שלו, והמערכת
     בודקת התאמה מלאה למה שרשום (intake.tenantId / phone) לפני שהחתימה
     מתקבלת. ברירת המחדל none שומרת על ההתנהגות הקיימת לכל חשבון קיים. */
  contractSignVerify: "none",
});

/* ---------------- אחסון תמונות ---------------- */
function seed() {
  const mk = (id, name) => ({
    id, name, rooms: "", tenant: "", phone: "", rent: 0, vaad: 0,
    leaseStart: "", leaseEnd: "", notes: "", history: [], contracts: [],
    rentDay: 1, billsDay: 15, billsCycle: 1, billsAnchor: 1,
    propertyType: "unit", authority: "", vaadActive: false, contents: "", inventory: "", rentSteps: [],
    arnona: 0, arnonaActive: false, municipal: 0, municipalActive: false, municipalLabel: "",
  });
  return {
    apartments: [
      mk("u1", "יחידת דיור 1, מושב אחיהוד"),
      mk("u2", "יחידת דיור 2, מושב אחיהוד"),
      mk("u3", "יחידת דיור 3, מושב אחיהוד"),
      mk("u4", "יחידת דיור 4, מושב אחיהוד"),
      mk("u5", "יחידת דיור 5, מושב אחיהוד"),
      mk("h1", "דירה להשכרה, חריש"),
    ],
    payments: {}, bills: {}, media: [], expenses: [], readings: [], paymentReports: [], settings: defTariffs(),
  };
}

/**
 * איפוס החשבון: מוחק דיירים, תשלומים, חשבונות, קריאות, חוזים, הוצאות
 * ותיעוד — ומשאיר את ההגדרות ואת הנכסים כקליפות ריקות (שם, חדרים, סוג,
 * רשות, הערות).
 *
 * עד כאן "איפוס" החזיר את seed() — ששה נכסים קשיחים של מי שכתב את
 * הגרסה הראשונה, ודרס גם את פרטי בעל הנכס, החתימה ופרטי הבנק. במוצר
 * עם חשבונות זו תקלה: משתמש בחיפה היה מקבל "יחידת דיור 1, מושב אחיהוד".
 */
function resetDocument(d) {
  const shell = (a) => ({
    id: a.id, name: a.name || "", rooms: a.rooms || "", notes: a.notes || "",
    propertyType: a.propertyType || "unit", authority: a.authority || "",
    // סעיף השבת הוא תכונה של הנכס, לא של הדייר — נשמר גם באיפוס.
    clauseShabbat: a.clauseShabbat,
    tenant: "", phone: "", rent: 0, vaad: 0, vaadActive: false,
    leaseStart: "", leaseEnd: "", history: [], contracts: [], rentSteps: [],
    rentDay: 1, billsDay: 15, billsCycle: 1, billsAnchor: 1, contents: "",
    arnona: 0, arnonaActive: false, municipal: 0, municipalActive: false, municipalLabel: "",
    portalEnabled: a.portalEnabled,
  });
  return migrate({
    ...seed(),
    apartments: (d?.apartments || []).map(shell),
    settings: { ...defTariffs(), ...(d?.settings || {}) },
    tenancies: [], journal: [],
  });
}

/* ============================================================================
   ביטחונות.

   קטלוג של סוגי ביטחון, ולכל שכירות בחירה מרובה מתוכו. הביטחונות נסגרים
   מול כל דייר בנפרד — אחד נותן ערבות בנקאית, אחר שיק פתוח ושלישי שלושה
   חודשי פיקדון — ולכן הם שדה של תקופת השכירות ולא הגדרה גלובלית.

   ההגדרות ב-settings נשארו כברירת מחדל לשכירות חדשה ולתאימות לאחור.

   unit קובע מה נשאל: "months" מחושב כמכפלה של שכר הדירה, "amount" הוא
   סכום בשקלים. expiry רלוונטי רק לערבות בנקאית, שפוקעת בתאריך.
   ============================================================================ */

const SECURITIES = [
  {
    k: "depositMonths", label: "פיקדון (חודשי שכירות)", unit: "months",
    legacy: "depositMonths",
    clause: (v) => `פיקדון בסך ${nis(v.amount)} (${v.months} חודשי שכירות), שיוחזר בתום השכירות בקיזוז חובות ונזקים אם יהיו.`,
  },
  {
    k: "bankGuarantee", label: "ערבות בנקאית", unit: "amount", expiry: true,
    clause: (v) => `ערבות בנקאית אוטונומית בסך ${nis(v.amount)}${v.expiry ? `, בתוקף עד ${heDate(v.expiry)}` : ""}, הניתנת למימוש על ידי המשכיר בהתאם לתנאי ההסכם.`,
  },
  {
    k: "damageCheck", label: "שיק פיקדון לנזקים", unit: "amount",
    clause: (v) => `שיק פיקדון על סך ${nis(v.amount)} להבטחת תיקון נזקים למושכר, שיופקד רק כנגד נזק שנגרם בפועל ולאחר שהוצגה לשוכר הצעת מחיר.`,
  },
  {
    k: "securityCheck", label: "שיק ביטחון פתוח", unit: "amount",
    legacy: "securityCheck",
    clause: (v) => `שיק ביטחון פתוח על סך ${nis(v.amount)}, שיופקד רק אם הפר השוכר את ההסכם ולא תיקן את ההפרה בתוך 30 ימים מקבלת הודעה בכתב.`,
  },
  {
    /* ביטחון ייעודי לסעיפי צריכה שהשוכר משלם ישירות לספק. הוא נפרד משיק
       הביטחון הפתוח בכוונה: הוא נפדה רק כנגד חוב לספק שהוצגה עליו
       אסמכתה, ולא כנגד כל הפרה. */
    k: "utilityCheck", label: "שיק ביטחון לחשבונות צריכה", unit: "amount",
    clause: (v) => `שיק ביטחון על סך ${nis(v.amount)} להבטחת תשלום חשבונות הצריכה שהשוכר משלם ישירות לספקים (חשמל, מים וביוב, גז, ארנונה), שיופקד רק כנגד חוב שהמשכיר נדרש לשלמו בפועל וכנגד הצגת אסמכתה.`,
  },
  {
    k: "promissoryNote", label: "שטר חוב", unit: "amount",
    clause: (v) => `שטר חוב על סך ${nis(v.amount)}, חתום על ידי השוכר והערבים.`,
  },
  {
    k: "paintDeposit", label: "פיקדון צבע", unit: "amount",
    legacy: "paintDeposit",
    clause: (v) => `שיק פיקדון על סך ${nis(v.amount)} עבור תיקוני צבע בתום ההסכם.`,
  },
  {
    k: "cleaningDeposit", label: "פיקדון ניקיון", unit: "amount",
    legacy: "cleaningDeposit",
    clause: (v) => `שיק פיקדון על סך ${nis(v.amount)} עבור ניקיון המושכר בתום ההסכם.`,
  },
];

const SECURITY_BY_KEY = Object.fromEntries(SECURITIES.map((x) => [x.k, x]));

/** ברירת המחדל לשכירות חדשה, נגזרת מההגדרות הגלובליות. */
function defaultSecurities(settings = {}) {
  const out = [];
  for (const spec of SECURITIES) {
    if (!spec.legacy) continue;
    const v = Number(settings[spec.legacy] || 0);
    if (v <= 0) continue;
    out.push(spec.unit === "months" ? { k: spec.k, months: v } : { k: spec.k, amount: v });
  }
  return out;
}

/**
 * הביטחונות שבתוקף לשכירות נתונה, מנורמלים לתצוגה ולחוזה.
 *
 * מערך ריק הוא בחירה מפורשת של "בלי ביטחונות" ולא "לא הוגדר" — ולכן
 * undefined נופל לברירת המחדל, ו-[] לא.
 */
function securityList(apt, settings = {}) {
  const chosen = Array.isArray(apt?.securities)
    ? apt.securities
    : defaultSecurities(settings);

  const rent = Number(apt?.rent) || 0;

  return chosen
    .map((sel) => {
      const spec = SECURITY_BY_KEY[sel.k];
      if (!spec) return null;
      const months = spec.unit === "months" ? Number(sel.months) || 0 : null;
      const amount = spec.unit === "months"
        ? Math.round(rent * months)
        : Math.round(Number(sel.amount) || 0);
      return { k: spec.k, label: spec.label, unit: spec.unit, months, amount, expiry: sel.expiry || "" };
    })
    .filter((v) => v && (v.amount > 0 || v.months > 0));
}

/** סך כל הביטחונות בשקלים — לתצוגה בכרטיס הנכס. */
const securityTotal = (apt, settings) =>
  securityList(apt, settings).reduce((t, v) => t + v.amount, 0);

/* אותיות לסעיפי משנה בחוזה. הגרסה הקודמת קידדה א/ב/ג/ד קשיח, ולכן
   כשהסעיף הראשון לא חל המספור התחיל ב-ב׳ — מסמך משפטי עם מספור שבור. */
const HE_LETTERS = ["א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט", "י"];

/** שורות סעיף הביטחונות בחוזה, ממוספרות לפי מה שנבחר בפועל. */
function securityClauseLines(apt, settings) {
  return securityList(apt, settings).map((v, i) => {
    const spec = SECURITY_BY_KEY[v.k];
    return `   ${HE_LETTERS[i] || i + 1}. ${spec.clause(v)}`;
  });
}

/* ---------------- פעולות ---------------- */
/* האם הדירה מושכרת בחודש נתון — לפי תאריכי החוזה. חודשים שלפני
   כניסת הדייר אינם נחשבים חוב ואינם מסומנים באדום בלוח הגבייה. */
function activeIn(a, y, m) {
  if (!a.tenant) return false;
  /* השוואת מחרוזות ISO, לא אובייקטי Date: new Date("2026-08-31") מפורש
     כ-UTC, בעוד new Date(y, m, 0) נבנה בזמן מקומי — בישראל (UTC+2/3)
     ההפרש הזיז כניסה ב-31 לחודש אל "אחרי" סוף אותו חודש, וחודש הכניסה
     כולו נעלם מהחיוב ומלוחות הגבייה. */
  const mk_ = `${y}-${String(m).padStart(2, "0")}`;
  const last = `${mk_}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
  if (a.leaseStart && a.leaseStart > last) return false;
  const end = tenancyEnd(a);
  if (end && end < `${mk_}-01`) return false;
  return true;
}

/**
 * היום האחרון שבו התקופה באמת הייתה בתוקף.
 *
 * close_tenancy מקצר היום את leaseEnd לתאריך היציאה בפועל — אבל מסמכים
 * שנסגרו לפני כן נשארו עם תאריך החוזה המקורי ו-closedAt מוקדם ממנו, וכל
 * חודש שביניהם נספר כחוב. כאן שני המקרים מתלכדים בלי לגעת במסמכים.
 */
function tenancyEnd(t) {
  if (!t) return null;
  const end = t.leaseEnd || null;
  if (t.closed && t.closedAt && (!end || t.closedAt < end)) return t.closedAt;
  return end;
}

/* מועדי תשלום ומחזור חיוב — לכל נכס בנפרד, עם נפילה לברירת המחדל */

/**
 * התעריפים שבתוקף בתאריך נתון.
 *
 * הבסיס: מה שהוגדר בנכס גובר על ברירת המחדל הכללית. מעליו rateSteps —
 * היסטוריית שינויי תעריף, שכל רשומה בה היא { from, elec?, waterSewage?, gas? }
 * ומחליפה רק את מה שהיא מציינת.
 *
 * עד כאן rateSteps נכתב ב-update_apartment ואיש לא קרא אותו: שלוש פונקציות
 * התעריף התעלמו מהתאריך גם כשהקוראים העבירו אותו, ולכן שינוי תעריף באמצע
 * חוזה הוחל רטרואקטיבית על כל הקריאות הישנות. כאן זה מקבל תוקף אמיתי.
 */
function ratesAt(a, s, date = TODAY) {
  const out = {
    elec: Number(a?.rates?.elec) || Number(s?.elecRate) || 0.65,
    waterSewage: Number(a?.rates?.waterSewage) || Number(s?.waterSewageRate) || 16,
    gas: Number(a?.rates?.gas) || Number(s?.gasRate) || 0,
  };
  const steps = (a?.rateSteps || [])
    .filter((x) => x && x.from && x.from <= date)
    .sort((x, y) => (x.from < y.from ? -1 : 1));

  for (const st of steps) {
    for (const k of ["elec", "waterSewage", "gas"]) {
      if (st[k] != null && st[k] !== "") out[k] = Number(st[k]);
    }
  }
  return out;
}

const rateElec = (a, s, date) => ratesAt(a, s, date || TODAY).elec;
const rateWS = (a, s, date) => ratesAt(a, s, date || TODAY).waterSewage;
const rateGas = (a, s, date) => ratesAt(a, s, date || TODAY).gas;
const rateFor = (a, s, meter, date) =>
  (meter === "elec" ? rateElec(a, s, date) : meter === "gas" ? rateGas(a, s, date) : rateWS(a, s, date));

const rentDayOf = (a, s) => Number(a?.rentDay) || Number(s?.paymentDay) || 1;
const billsDayOf = (a, s) => Number(a?.billsDay) || Number(s?.billsDay) || 15;
const billsCycleOf = (a, s) => Number(a?.billsCycle) || Number(s?.billsCycle) || 1;
const billsAnchorOf = (a) => Number(a?.billsAnchor) || 1;
/* בחיוב דו-חודשי רק חלק מהחודשים הם חודשי גבייה */
function isBillingMonth(a, s, mk_) {
  const cyc = billsCycleOf(a, s);
  if (cyc === 1) return true;
  const m = Number(String(mk_).split("-")[1]);
  return ((m - billsAnchorOf(a)) % cyc + cyc) % cyc === 0;
}
function nextBillingMonth(a, s, mk_) {
  let k = mk_;
  for (let i = 0; i < 13; i++) { k = nextMonth(k); if (isBillingMonth(a, s, k)) return k; }
  return null;
}

/**
 * חודש החיוב שרלוונטי עכשיו.
 *
 * בגבייה חודשית זה פשוט החודש הנוכחי. בגבייה דו-חודשית מחצית החודשים
 * אינם חודשי חיוב, וכרטיס הנכס היה מציג בהם אפס — כאילו אין חוב — בזמן
 * שהחשבון מהחודש שעבר עדיין פתוח. לכן חוזרים אחורה אל חודש החיוב האחרון.
 */
function activeBillMonth(a, s, mk_ = CUR_KEY) {
  let k = mk_;
  for (let i = 0; i < 13; i++) {
    if (isBillingMonth(a, s, k)) return k;
    k = prevMonth(k);
  }
  return mk_;
}

/**
 * סך החוב הפתוח על חשבונות הצריכה בנכס, על פני כל החודשים.
 *
 * זה המספר שבעל הנכס באמת רוצה לראות על הכרטיס: לא "כמה החשבון של
 * החודש" אלא "כמה הדייר חייב לי על חשבונות". חודש שלא שולם נשאר בסכום.
 */
function billsOutstanding(d, aptId) {
  const apt = (d?.apartments || []).find((a) => a.id === aptId);
  if (!apt) return 0;
  /* דרך debtStatement (לכל תקופה עם דייר בנפרד) ולא סריקה ישירה של
     d.bills: כך גם סעיף קבוע (ועד בית/ארנונה/היטל) שהתקופה מגדירה
     כפעיל נספר גם בלי רשומת חשבון מפורשת לחודש — אותה סיבה כמו התיקון
     ב-debtStatement עצמה, ואותה הדרגה לכל תקופה משתמשת בהגדרות שלה. */
  const withTenant = tenanciesOf(d, aptId).filter((t) => t.tenant);
  if (!withTenant.length) return debtStatement(d, apt, CUR_KEY).billsDue;
  return withTenant.reduce((sum, t) => (
    debtStillOwed(t) ? sum + debtStatement(d, apt, CUR_KEY, { tenancyId: t.id }).billsDue : sum
  ), 0);
}

/** החודשים שיש בהם חוב פתוח על חשבונות, מהישן לחדש. */
function unpaidBillMonths(d, aptId) {
  return Object.entries(d?.bills || {})
    .filter(([key, b]) => {
      const [id, mk_] = String(key).split("|");
      return id === aptId && billsDue(b) > 0 && monthDebtOwed(d, aptId, mk_);
    })
    .map(([key]) => String(key).split("|")[1])
    .sort();
}

/** האם חוב של חודש נתון עדיין נחשב פתוח — לא אם התקופה שלו נסגרה כ"אבוד"/"ויתור". */
function monthDebtOwed(d, aptId, mk_) {
  const t = tenancyForMonth(d, aptId, mk_);
  return !t || debtStillOwed(t);
}

/* ============================================================================
   כרטיס החוב של הדייר.

   עד כאן כל מסך הסתכל על חודש אחד: החשבון של אוגוסט, התשלום של אוגוסט.
   אבל דייר שלא שילם ביולי חייב בספטמבר גם את יולי, וההודעה שנשלחת אליו
   חייבת להראות את שניהם ואת הסכום המצטבר — אחרת היא מציגה חוב קטן ממה
   שהוא באמת, והדייר משלם את החודש הנוכחי וממשיך לגרור את הישן.

   שני כללים שקובעים את הנכונות של כל מה שנבנה מעל:

   1. החוב שייך לתקופת השכירות, לא לנכס. דייר חדש שנכנס לדירה שקודמו
      עזב אותה עם חוב לא יורש אותו — tenancyForMonth מסננת את זה. בלי
      הסינון הזה היינו שולחים לדייר חדש דרישת תשלום על חודשים שלפני
      שהוא בכלל הגיע.

   2. שכר הדירה מחושב לפי מה שהיה בתוקף באותו חודש (effectiveRent) ולא
      לפי הסכום של היום. חוזה עם מדרגות שכר דירה היה מציג חוב שגוי על
      כל החודשים שלפני המדרגה האחרונה.
   ============================================================================ */

/** האם התקופה הזו היא זו שאחראית לחודש הנתון. */
function monthBelongsTo(d, aptId, mk_, tenancyId) {
  if (!tenancyId) return true;
  return tenancyForMonth(d, aptId, mk_)?.id === tenancyId;
}

/**
 * כל מה שנצרך ולא שולם, חודש-חודש, עד לחודש שנבחר ועד בכלל.
 *
 * @param {object} d      המסמך
 * @param {object} apt    הנכס (המסונכרן, עם פרטי התקופה הפעילה)
 * @param {string} upTo   החודש האחרון שנכלל, ברירת מחדל החודש הנוכחי
 * @param {object} [opts] tenancyId — לצמצום לתקופה מסוימת. ברירת המחדל
 *                        היא התקופה שבתוקף היום; null מבטל את הסינון
 *                        ומחזיר את כל ההיסטוריה של הנכס (לדוחות).
 */
function debtStatement(d, apt, upTo = CUR_KEY, opts = {}) {
  const aptId = apt?.id;
  const empty = {
    months: [], arrears: [], current: null,
    rentDue: 0, billsDue: 0, total: 0, arrearsTotal: 0, since: null,
  };
  if (!d || !aptId) return empty;

  /* ברירת המחדל: התקופה שבתוקף היום, ואם הנכס פנוי — האחרונה שהייתה.
     דייר שעזב עם חוב עדיין חייב אותו, ולא נכון להעלים אותו מהמסך רק
     כי הדירה התפנתה. ברגע שנכנס דייר חדש החוב הישן יורד מהכרטיס שלו
     ונשאר בדוח החובות ובהיסטוריה של התקופה. */
  const lastWithTenant = tenanciesOf(d, aptId).filter((t) => t.tenant).slice(-1)[0] || null;
  const tenancyId = "tenancyId" in opts
    ? opts.tenancyId
    : (currentTenancy(d, aptId)?.id || lastWithTenant?.id || null);

  /* apt המסונכרן נושא את התאריכים של התקופה הפעילה. כשמסתכלים על תקופה
     אחרת — בדוח או בהיסטוריה — צריך את התאריכים ואת שכר הדירה שלה. */
  const ten = tenancyId ? (d.tenancies || []).find((t) => t.id === tenancyId) : null;
  const scope = ten ? { ...apt, ...ten, id: aptId } : apt;

  /* נקודת ההתחלה: תחילת התקופה, או החודש הישן ביותר שיש עליו רישום.
     חשבון שנרשם לפני מועד הכניסה (קריאת מונה בכניסה, למשל) לא ייעלם. */
  const recorded = [
    ...Object.keys(d.bills || {}),
    ...Object.keys(d.payments || {}),
  ]
    .filter((k) => String(k).split("|")[0] === aptId)
    .map((k) => String(k).split("|")[1])
    .filter(Boolean);

  const leaseFrom = scope.leaseStart ? String(scope.leaseStart).slice(0, 7) : null;
  const candidates = [...recorded, leaseFrom].filter(Boolean).filter((m) => m <= upTo);
  if (!candidates.length) return empty;

  let mk_ = candidates.sort()[0];
  const months = [];

  // תקרה: חוזה ארוך במיוחד או תאריך שגוי לא יתקעו את הלולאה.
  for (let guard = 0; guard < 240 && mk_ <= upTo; guard++, mk_ = nextMonth(mk_)) {
    if (!monthBelongsTo(d, aptId, mk_, tenancyId)) continue;

    const [y, m] = mk_.split("-").map(Number);
    const lived = activeIn(scope, y, m);

    const rentCharged = lived ? Number(effectiveRent(scope, mk_)) || 0 : 0;
    const rentPaid = tenancyId ? rentPaidOf(d, aptId, mk_, tenancyId) : rentPaidOf(d, aptId, mk_);

    const b = d.bills?.[rowKey(aptId, mk_)] || null;
    /* סעיפים קבועים (ועד בית, ארנונה, היטל) שהתקופה מגדירה כפעילים
       צפויים מדי חודש בדיוק כמו שכר דירה, גם בלי רשומת חשבון מפורשת
       לחודש הזה — רשומה מפורשת (סכום שהשתנה החודש, למשל) גוברת על
       הצפי. בלעדי זה, ועד בית שטרם "נקבע" ידנית לא נחשב חוב בכלל,
       בסתירה למה שלוחות הגבייה כבר מציגים כפתוח. */
    const effBill = { ...(b || {}) };
    if (lived) {
      FIXED_BILLS.forEach(({ k, amountField, activeField }) => {
        if (Number(effBill[k]?.amount || 0) > 0) return;
        if (scope[activeField] === false) return;
        const amt = effectiveFixedBill(scope, mk_, amountField);
        if (amt > 0) effBill[k] = { ...(effBill[k] || {}), amount: amt };
      });
    }
    /* חשבון החודש כפי שהוא נראה *לתקופה הזו*: כל מה שנגזר ממנו למטה
       (לחיוב, ששולם, חוב, פילוח לפי אמצעי, והרשומה שנחשפת ב-record)
       נקרא ממנו ולא מ-effBill הגולמי — אחרת החלק של התקופה השנייה
       באותו חודש דולף לכרטיס הזה.
       tenancyId, כשקיים, כבר עבר את מסנן monthBelongsTo למעלה — כלומר
       החודש הזה שייך לו כתקופה ראשית, ולכן isPrimary=true תמיד כאן.
       ללא tenancyId (מצב "דוחות") resolvedBill הוא effBill עצמו, ולכן
       הסכום נשאר משולב בכוונה — בדיוק כמו היום. */
    const resolvedBill = tenancyId
      ? Object.fromEntries(BILLS.map(({ k }) => [k, entryFor(effBill[k], tenancyId, true)]))
      : effBill;
    const billsCharged = BILLS.reduce((t, x) => t + itemAmount(resolvedBill[x.k]), 0);
    const billsPaid = billsPaidTotal(resolvedBill);

    /* תשלום עודף בסעיף אחד לא מקזז חוב בסעיף אחר, ולכן החוב לחודש הוא
       סכום החובות ולא ההפרש בין הכולל למה ששולם. אחרת דייר שהעביר
       סכום עגול היה נראה כמי שסגר חשבון שלא נגע בו. */
    const rentOwed = Math.max(0, rentCharged - rentPaid);
    const billsOwed = Math.max(0, billsDue(resolvedBill));
    const due = rentOwed + billsOwed;

    if (!rentCharged && !billsCharged && !rentPaid && !billsPaid) continue;

    /* איך התקבל הכסף באותו חודש. שכר דירה וחשבונות מגיעים לרוב בשני
       אמצעים שונים, ולכן זו רשימה ולא ערך אחד. משמש להצלבה מול הבנק. */
    const byMethod = {};
    const note = (m, amount) => {
      if (!m || !(amount > 0)) return;
      byMethod[m] = (byMethod[m] || 0) + amount;
    };
    note(d.payments?.[rowKey(aptId, mk_)]?.method, rentPaid);
    for (const { k } of BILLS) note(resolvedBill[k]?.method, itemPaid(resolvedBill[k]));

    months.push({
      month: mk_,
      rent: { charged: rentCharged, paid: rentPaid, due: rentOwed },
      bills: { charged: billsCharged, paid: billsPaid, due: billsOwed, record: resolvedBill },
      charged: rentCharged + billsCharged,
      paid: rentPaid + billsPaid,
      due,
      methods: Object.entries(byMethod).map(([k, amount]) => ({ k, amount })),
    });
  }

  const owing = months.filter((x) => x.due > 0);
  const current = months.find((x) => x.month === upTo) || null;
  const arrears = owing.filter((x) => x.month !== upTo);

  return {
    months,
    arrears,
    current,
    arrearsTotal: arrears.reduce((t, x) => t + x.due, 0),
    rentDue: owing.reduce((t, x) => t + x.rent.due, 0),
    billsDue: owing.reduce((t, x) => t + x.bills.due, 0),
    total: owing.reduce((t, x) => t + x.due, 0),
    since: owing.length ? owing[0].month : null,
  };
}

/** סך החוב של הדייר הנוכחי — שכר דירה וחשבונות יחד. */
const debtTotal = (d, apt, upTo = CUR_KEY) => debtStatement(d, apt, upTo).total;

/**
 * החוב של הדייר הנוכחי בלבד — null כשהנכס פנוי.
 *
 * debtStatement בלי tenancyId נופל לתקופה האחרונה כשאין דייר, וזה נכון
 * לכרטיס החוב (מישהו צריך לראות את החוב של מי שעזב) — אבל בדוח החובות
 * אותו חוב הופיע פעמיים: פעם כ"ללא דייר" ופעם על שם הדייר שעזב. כאן
 * הגבול מפורש: יש דייר נוכחי או אין.
 */
function currentDebt(d, apt, upTo = CUR_KEY) {
  const cur = currentTenancy(d, apt?.id);
  if (!cur) return null;
  return { tenancy: cur, statement: debtStatement(d, apt, upTo, { tenancyId: cur.id }) };
}

/* ---------------- שורות פירוט לכרטיסי הדשבורד ----------------
   לחיצה על כרטיס בדשבורד (חוב שכר דירה, חשבונות פתוחים, נגבה החודש...)
   פותחת רשימת נכסים + הסכום הרלוונטי לכל אחד. כל פונקציה כאן מחזירה
   [{aptId, name, tenant, amount}], ממוינת מהגדול לקטן, בלי נכסים בסכום
   0 — כדי שהרשימה תמיד תתאים למספר שכבר מוצג בכרטיס עצמו. tenant הוא
   הדייר הנוכחי של הנכס (ריק בנכס פנוי) — ברשימה מציגים אותו, לא רק את
   שם הנכס, כי זה מה שבעל הנכס מזהה קודם. */

/** חוב שכר דירה של הדייר הנוכחי בכל נכס — אותו חישוב כמו stats.debt. */
function rentDebtRows(d) {
  return (d.apartments || [])
    .filter((a) => a.tenant)
    .map((a) => {
      const payDay = rentDayOf(a, d.settings);
      const months = currentDebt(d, a, CUR_KEY)?.statement.months || [];
      const amount = months.reduce((s, x) => {
        if (x.month === CUR_KEY && now.getDate() < payDay) return s;
        return s + x.rent.due;
      }, 0);
      return { aptId: a.id, name: a.name, tenant: a.tenant || "", amount };
    })
    .filter((x) => x.amount > 0)
    .sort((x, y) => y.amount - x.amount);
}

/** חשבונות פתוחים (כל הסעיפים, כל החודשים) לכל נכס. */
function openBillsRows(d) {
  return (d.apartments || [])
    .map((a) => ({ aptId: a.id, name: a.name, tenant: a.tenant || "", amount: billsOutstanding(d, a.id) }))
    .filter((x) => x.amount > 0)
    .sort((x, y) => y.amount - x.amount);
}

/** סכום פתוח לפי מפתח סעיפים ספציפי (למשל רק ועד בית, או רק צריכה) של
    הדייר הנוכחי, על פני כל החודשים — לא רק החודש הנוכחי. עובר דרך
    currentDebt/debtStatement (כמו rentDebtRows) ולא קורא bills גולמי,
    כדי לכלול גם סעיף קבוע (ועד בית/ארנונה) שהתקופה מגדירה כפעיל בלי
    שנקבעה לו רשומת חשבון מפורשת לחודש — בדיוק כמו כרטיס החוב. */
function tenantBillDebtRows(d, keys) {
  return (d.apartments || [])
    .filter((a) => a.tenant)
    .map((a) => {
      const months = currentDebt(d, a, CUR_KEY)?.statement.months || [];
      const amount = months.reduce((s, x) => s + keys.reduce((t, k) => t + itemDue(x.bills.record?.[k]), 0), 0);
      return { aptId: a.id, name: a.name, tenant: a.tenant || "", amount };
    })
    .filter((x) => x.amount > 0)
    .sort((x, y) => y.amount - x.amount);
}

/** חוב ועד בית פתוח (כל החודשים), לכל נכס. */
const vaadDebtRows = (d) => tenantBillDebtRows(d, ["vaad"]);

/** חוב חשבונות צריכה פתוח — חשמל, מים, גז, ביוב (כל החודשים), לכל נכס. */
const consDebtRows = (d) => tenantBillDebtRows(d, ["elec", "water", "gas", "sewage"]);

/** שכר דירה צפוי החודש — נכסים מושכרים בלבד, לפי שכר הדירה של היום. */
function rentExpectedRows(d, mk_ = CUR_KEY) {
  return (d.apartments || [])
    .filter((a) => a.tenant)
    .map((a) => ({ aptId: a.id, name: a.name, tenant: a.tenant || "", amount: Number(a.rent || 0) }))
    .filter((x) => x.amount > 0)
    .sort((x, y) => y.amount - x.amount);
}

/** שכר דירה שהתקבל בפועל החודש, לכל נכס מושכר. */
function rentCollectedRows(d, mk_ = CUR_KEY) {
  return (d.apartments || [])
    .filter((a) => a.tenant)
    .map((a) => ({ aptId: a.id, name: a.name, tenant: a.tenant || "", amount: rentPaidOf(d, a.id, mk_) }))
    .filter((x) => x.amount > 0)
    .sort((x, y) => y.amount - x.amount);
}

/** ועד בית שנגבה החודש, לכל נכס. */
function vaadCollectedRows(d, mk_ = CUR_KEY) {
  return (d.apartments || [])
    .map((a) => ({ aptId: a.id, name: a.name, tenant: a.tenant || "", amount: itemPaid(d.bills[rowKey(a.id, mk_)]?.vaad) }))
    .filter((x) => x.amount > 0)
    .sort((x, y) => y.amount - x.amount);
}

/** חשבונות צריכה (חשמל, מים, גז, ביוב) ששולמו החודש, לכל נכס. */
function consPaidRows(d, mk_ = CUR_KEY) {
  const CONS = ["elec", "water", "gas", "sewage"];
  return (d.apartments || [])
    .map((a) => {
      const b = d.bills[rowKey(a.id, mk_)];
      return { aptId: a.id, name: a.name, tenant: a.tenant || "", amount: CONS.reduce((s, k) => s + itemPaid(b?.[k]), 0) };
    })
    .filter((x) => x.amount > 0)
    .sort((x, y) => y.amount - x.amount);
}

/** הוצאות שנרשמו החודש, מקובצות לפי נכס. */
function expensesRows(d, mk_ = CUR_KEY) {
  const byApt = {};
  (d.expenses || []).filter((e) => e.month === mk_).forEach((e) => {
    byApt[e.aptId] = (byApt[e.aptId] || 0) + Number(e.amount || 0);
  });
  return (d.apartments || [])
    .map((a) => ({ aptId: a.id, name: a.name, tenant: a.tenant || "", amount: byApt[a.id] || 0 }))
    .filter((x) => x.amount > 0)
    .sort((x, y) => y.amount - x.amount);
}

/** מאחדת כמה רשימות [{aptId,...,amount}] לרשימה אחת — סוכמת amount לכל
    נכס, ומשמרת name/tenant. עוזרת ל-totalCollectedRows/totalDebtRows
    להישאר עקביות זו עם זו. */
function mergeRowsByApt(...lists) {
  const byApt = {};
  lists.flat().forEach((x) => {
    byApt[x.aptId] = { aptId: x.aptId, name: x.name, tenant: x.tenant, amount: (byApt[x.aptId]?.amount || 0) + x.amount };
  });
  return Object.values(byApt).filter((x) => x.amount > 0).sort((x, y) => y.amount - x.amount);
}

/** סה״כ שנגבה החודש (שכר דירה + ועד בית + צריכה יחד), לכל נכס. */
function totalCollectedRows(d, mk_ = CUR_KEY) {
  return mergeRowsByApt(rentCollectedRows(d, mk_), vaadCollectedRows(d, mk_), consPaidRows(d, mk_));
}

/** סה״כ חוב פתוח (שכר דירה + ועד בית + צריכה יחד), לכל נכס. */
function totalDebtRows(d) {
  return mergeRowsByApt(rentDebtRows(d), vaadDebtRows(d), consDebtRows(d));
}

/**
 * חובות שנשארו פתוחים מתקופות שהסתיימו — לדוח, לכרטיס החוב ולכרטיס הנכס.
 * תקופה שסומנה בסגירה כ"חוב אבוד" או "ויתור" אינה חוב פתוח ולא מופיעה
 * כאן (ראו debtResolution ב-close_tenancy); "חוב של הדייר", או תקופה
 * ישנה בלי סימון, נשארת.
 */
function pastDebts(d, apt, upTo = CUR_KEY) {
  return pastTenancies(d, apt?.id)
    .filter((t) => t.tenant && debtStillOwed(t))
    .map((t) => ({ tenancy: t, statement: debtStatement(d, apt, upTo, { tenancyId: t.id }) }))
    .filter((x) => x.statement.total > 0);
}

/** תקופות סגורות שהחוב בהן נמחק או נמחל — לסיכום נפרד בדוח. */
function resolvedDebts(d, apt, upTo = CUR_KEY) {
  return pastTenancies(d, apt?.id)
    .filter((t) => t.tenant && !debtStillOwed(t))
    .map((t) => ({ tenancy: t, statement: debtStatement(d, apt, upTo, { tenancyId: t.id }) }))
    .filter((x) => x.statement.total > 0);
}

const DEBT_RESOLUTIONS = [
  { k: "tenant", label: "חוב של הדייר", hint: "נשאר פתוח על שמו — בדוח החובות ובכרטיס הנכס" },
  { k: "lost", label: "חוב אבוד", hint: "לא ייגבה; נרשם כהפסד" },
  { k: "waived", label: "ויתור של בעל הנכס", hint: "נמחל לדייר; נרשם כוויתור" },
];
const debtStillOwed = (t) => !t?.debtResolution || t.debtResolution.kind === "tenant" || !DEBT_RESOLUTIONS.some((x) => x.k === t.debtResolution.kind);

/**
 * פילוח מה שנגבה לפי אמצעי תשלום, לטווח חודשים.
 *
 * זה מה שמאפשר להצליב מול דף הבנק: הוראות הקבע וההעברות אמורות להופיע
 * שם, ביט ופייבוקס בדוחות שלהם, ומזומן לא מופיע בשום מקום חוץ מכאן.
 */
function methodBreakdown(d, { aptIds = null, from = null, to = null } = {}) {
  const totals = {};
  const bump = (m, amount) => {
    if (!(amount > 0)) return;
    const k = PAY_METHOD_BY_KEY[m] ? m : "unknown";
    totals[k] = (totals[k] || 0) + amount;
  };
  const inRange = (id, mk_) =>
    (!aptIds || aptIds.includes(id)) && (!from || mk_ >= from) && (!to || mk_ <= to);

  for (const [key, p] of Object.entries(d?.payments || {})) {
    const [id, mk_] = String(key).split("|");
    if (inRange(id, mk_)) bump(p?.method, Number(p?.amount || 0));
  }
  for (const [key, b] of Object.entries(d?.bills || {})) {
    const [id, mk_] = String(key).split("|");
    if (!inRange(id, mk_)) continue;
    for (const { k } of BILLS) bump(b?.[k]?.method, itemPaid(b?.[k]));
  }

  const rows = Object.entries(totals)
    .map(([k, amount]) => ({
      k,
      label: k === "unknown" ? "לא נרשם" : methodLabel(k),
      traceable: k === "unknown" ? false : !!PAY_METHOD_BY_KEY[k]?.traceable,
      amount,
    }))
    .sort((a, b) => b.amount - a.amount);

  return { rows, total: rows.reduce((t, r) => t + r.amount, 0) };
}
const nextMonth = (mk_) => { const [y, m] = mk_.split("-").map(Number); return m === 12 ? mKey(y + 1, 1) : mKey(y, m + 1); };
/* מה עוד חסר כדי שהנכס יהיה מוגדר במלואו — משמש גם את העוזר וגם את כרטיס הנכס.
   d הוא אופציונלי: איתו, נכס פנוי שכבר נסגר בו דייר לעתיד לא מסומן "חסר דייר" —
   החוזה חתום, רק הכניסה עוד לא הגיעה. */
function missingFor(a, d = null) {
  const m = [];
  if (!a.tenant) {
    if (d && upcomingTenancies(d, a.id).length) return m;
    m.push({ label: "דייר", tpl: "בוא נכניס דייר" });
    return m;
  }
  if (!a.rent) m.push({ label: "שכר דירה", tpl: "נקבע שכר דירה" });
  if (!a.phone) m.push({ label: "טלפון", tpl: "נוסיף טלפון" });
  if (!a.leaseStart || !a.leaseEnd) m.push({ label: "תאריכי חוזה", tpl: "נעדכן תאריכי חוזה" });
  if (!a.vaad && a.vaadActive !== false) m.push({ label: "ועד בית", tpl: "נזין ועד בית" });
  if (!(a.contracts || []).length) m.push({ label: "חוזה", tpl: "ניצור חוזה" });
  return m;
}

const rentOverdue = (a, s, payments) => activeIn(a, CUR_Y, CUR_M) && !payments[rowKey(a.id, CUR_KEY)] && now.getDate() > rentDayOf(a, s);


/* נתונים ישנים: הופכים קריאות שנשמרו בתוך החשבונות לרשומות קריאה */
function migrate(d) {
  if (!d) return d;
  if (!d.settings) d.settings = defTariffs();
  d.settings = { ...defTariffs(), ...d.settings };
  if (!d.settings.waterSewageRate) d.settings.waterSewageRate = (Number(d.settings.waterRate) || 0) + (Number(d.settings.sewageRate) || 0) || 16;
  if (!d.settings.elecRate) d.settings.elecRate = 0.65;
  Object.values(d.bills || {}).forEach((b) => {
    BILLS.forEach(({ k }) => {
      if (!b[k]) { b[k] = emptyItem(); return; }
      if (b[k].paidAmount == null) b[k].paidAmount = b[k].paid ? Number(b[k].amount || 0) : 0;
    });
  });
  (d.apartments || []).forEach((a) => {
    if (!a.intake) a.intake = {};
    if (!a.propertyType) a.propertyType = "unit";
    if (a.vaadActive === undefined) a.vaadActive = Number(a.vaad || 0) > 0;
    /* מסמכים שנוצרו לפני שהסעיפים העירוניים קיימים: כבוי, לא "לא מוגדר".
       ברירת מחדל דלוקה הייתה מוסיפה חיוב שהדייר מעולם לא הסכים לו. */
    if (a.arnona === undefined) a.arnona = 0;
    if (a.arnonaActive === undefined) a.arnonaActive = false;
    if (a.municipal === undefined) a.municipal = 0;
    if (a.municipalActive === undefined) a.municipalActive = false;
    if (a.municipalLabel === undefined) a.municipalLabel = "";
    /* מסמכים ישנים לא נוגעים: securities שאינו מוגדר נופל לברירת המחדל
       מההגדרות, כלומר בדיוק ההתנהגות שהייתה. רק מערך פגום מנוקה. */
    if (a.securities !== undefined && !Array.isArray(a.securities)) delete a.securities;
    if (a.contents === undefined) a.contents = "";
    /* רשימת הציוד של הנכס עצמה — מקרר, תנור, מזגנים. היא שייכת לנכס
       ולא לדייר: אותו מקרר נשאר שם גם אחרי שהדייר מתחלף. עד כאן היא
       הייתה רק שדה של תקופת השכירות (contents), ולכן דייר חדש התחיל
       מרשימה ריקה וכל אחד הקליד אותה מחדש. מסמכים קיימים מקבלים כאן
       את מה שכבר רשום אצלם. */
    if (a.inventory === undefined) a.inventory = a.contents || "";
    if (!Array.isArray(a.rentSteps)) a.rentSteps = [];
    if (!a.rates) a.rates = {};
    if (!Array.isArray(a.guarantors)) a.guarantors = [];
    if (!a.accessCode) a.accessCode = accessCode(a);
    if (a.portalEnabled === undefined) a.portalEnabled = true;
    // רשומות שווי הנכס לאורך זמן — { id, date, amount, notes }.
    if (!Array.isArray(a.values)) a.values = [];
  });

  if (!Array.isArray(d.journal)) d.journal = [];
  // הלוואות ומשכנתאות — רשימה ברמת החשבון; הלוואה יכולה להיות מקושרת לנכס.
  if (!Array.isArray(d.loans)) d.loans = [];
  // הוצאות קבועות (ועד בית, ארנונה, הלוואה...) — ראו materializeRecurringExpenses.
  if (!Array.isArray(d.recurringExpenses)) d.recurringExpenses = [];

  /* המרה חד-פעמית: הדייר הנוכחי ורשומות ההיסטוריה הופכים לתקופות שכירות */
  if (!Array.isArray(d.tenancies)) {
    const out = [];
    (d.apartments || []).forEach((a) => {
      (a.history || []).forEach((h) => {
        out.push({
          ...newTenancy(a.id, { tenant: h.tenant, rent: h.rent, leaseStart: h.from || "", leaseEnd: h.to || "" }),
          closed: true, closedAt: h.to || null,
        });
      });
      if (a.tenant) {
        const t = newTenancy(a.id, a);
        TEN_FIELDS.forEach((k) => { if (a[k] !== undefined) t[k] = a[k]; });
        out.push(t);
      }
    });
    d.tenancies = out;
  }
  if (!Array.isArray(d.readings)) {
    const out = [];
    Object.entries(d.bills || {}).forEach(([k, b]) => {
      const [aptId, mk_] = k.split("|");
      if (b.elecRead != null) out.push({ id: uid(), aptId, meter: "elec", value: b.elecRead, date: `${mk_}-15`, photoId: b.elecPhoto || null, note: "" });
      if (b.waterRead != null) out.push({ id: uid(), aptId, meter: "water", value: b.waterRead, date: `${mk_}-15`, photoId: b.waterPhoto || null, note: "" });
    });
    d.readings = out.sort((a, b) => (a.date < b.date ? -1 : 1));
  }
  if (!Array.isArray(d.paymentReports)) d.paymentReports = [];

  /* החלפות שנעשו לפני 25.8.2026 השאירו את התקופה הקודמת פתוחה לצד
     המחליף — והחובות שלה נדבקו לדייר החדש. אותו כלל ש-change_tenant
     אוכף היום מוחל כאן על מסמכים קיימים: תקופה שסיומה חופף תקופה
     מאוחרת יותר באותו נכס נסגרת יום לפני כניסת המחליף. contractEnd
     שומר את תאריך החוזה המקורי; מחוקות לא נוגעים בהן. אידמפוטנטי. */
  {
    const byApt = {};
    (d.tenancies || []).forEach((t) => {
      if (!t.deleted && t.tenant && t.leaseStart) (byApt[t.aptId] = byApt[t.aptId] || []).push(t);
    });
    Object.values(byApt).forEach((list) => {
      list.sort((a, b) => (a.leaseStart < b.leaseStart ? -1 : a.leaseStart > b.leaseStart ? 1 : 0));
      for (let i = 0; i < list.length - 1; i++) {
        const t = list[i], next = list[i + 1];
        const end = tenancyEnd(t);
        if (end && end < next.leaseStart) continue;   // כבר מסתיימת לפני המחליף
        const s = new Date(`${next.leaseStart}T00:00:00`);
        s.setDate(s.getDate() - 1);
        const dayBefore = `${s.getFullYear()}-${pad(s.getMonth() + 1)}-${pad(s.getDate())}`;
        t.closed = true;
        t.closedAt = dayBefore;
        t.contractEnd = t.contractEnd || t.leaseEnd || null;
        t.leaseEnd = dayBefore;
      }
    });
  }
  materializeRecurringExpenses(d);
  return syncApartments(d);
}

/** מפתחות "YYYY-MM" מ-from עד to, כולל שני הקצוות. */
function monthKeysBetween(from, to) {
  if (!from || !to) return [];
  const [fy, fm] = String(from).split("-").map(Number);
  const [ty, tm] = String(to).split("-").map(Number);
  if (!fy || !fm || !ty || !tm) return [];
  const out = [];
  let y = fy, m = fm;
  while (y < ty || (y === ty && m <= tm)) {
    out.push(mKey(y, m));
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return out;
}

/* הוצאות קבועות (ועד בית, ארנונה, הלוואה עם תאריך סיום...) — במקום
   להזין כל חודש ידנית, כל תבנית ב-d.recurringExpenses מייצרת אוטומטית
   רשומת הוצאה רגילה לכל חודש מ-startMonth ועד היום (או endMonth אם
   הגיע). הבדיקה לפי recurringId+aptId+month מבטיחה אידמפוטנטיות —
   הרצה חוזרת (בכל טעינה) לא יוצרת כפילויות. עריכה או מחיקה של התבנית
   לא נוגעות ברשומות שכבר נוצרו — הן הופכות לרשומות רגילות ועצמאיות
   ברגע שנוצרו, בדיוק כמו הוצאה שהוזנה ידנית. */
function materializeRecurringExpenses(d) {
  if (!Array.isArray(d.recurringExpenses) || !d.recurringExpenses.length) return d;
  d.expenses = d.expenses || [];
  const existing = new Set(d.expenses.filter((e) => e.recurringId)
    .map((e) => `${e.recurringId}|${e.aptId || ""}|${e.month}`));
  d.recurringExpenses.forEach((r) => {
    const aptIds = r.aptIds && r.aptIds.length ? r.aptIds : [""];
    const share = Math.round((Number(r.amount) || 0) / aptIds.length);
    const to = r.endMonth && r.endMonth < CUR_KEY ? r.endMonth : CUR_KEY;
    monthKeysBetween(r.startMonth, to).forEach((m) => {
      aptIds.forEach((aptId) => {
        const key = `${r.id}|${aptId}|${m}`;
        if (existing.has(key)) return;
        d.expenses.unshift({ id: uid(), aptId, month: m, cat: r.cat || "אחר", amount: share, desc: r.desc || "", recurringId: r.id });
        existing.add(key);
      });
    });
  });
  return d;
}

/* ============ תקופות שכירות (tenancies) ============
   כל דייר הוא רשומה עצמאית עם תקופה משלו. חוזה שנחתם בנובמבר לתקופה
   שמתחילה בינואר נשמר כתקופה "עתידית", בעוד הדייר הנוכחי ממשיך להיות
   הפעיל. שדות הדייר שעל הנכס הם שיקוף של התקופה הפעילה היום, כדי
   שכל המסכים ימשיכו לעבוד — מקור האמת הוא רשימת התקופות. */

const TEN_FIELDS = ["tenant", "phone", "rent", "rentSteps", "leaseStart", "leaseEnd",
  "rentDay", "billsDay", "billsCycle", "billsAnchor", "vaad", "vaadActive",
  "arnona", "arnonaActive", "municipal", "municipalActive", "municipalLabel",
  "securities", "rates", "rateSteps",
  /* חיוב יחסי לחודש הראשון — כשהכניסה אינה ב-1 לחודש. ברירת המחדל
     (undefined/false) היא חודש מלא, כמו תמיד; true מחייב רק את הימים
     שבהם הדייר גר בפועל באותו חודש. תלוי בהסכמה מול הדייר, ולכן שדה
     של התקופה ולא הגדרה גלובלית. */
  "prorateFirstMonth",
  /* סכום מפורש לחודש הראשון — גובר גם על החישוב היחסי וגם על חודש
     מלא. משמש כשסוכם בעל פה סכום אחר מהחישוב המדויק לפי ימים (למשל
     סכום עגול). null/undefined = אין חריגה, ממשיכים לפי prorateFirstMonth. */
  "firstMonthAmount",
  /* אותו רעיון עבור ועד בית, ארנונה והיטל עירוני — לכל סעיף קבוע יכול
     להיות חיוב יחסי לחודש הראשון או ויתור מפורש עליו, בנפרד משכר
     הדירה ובנפרד זה מזה. */
  "vaadProrateFirstMonth", "vaadFirstMonthAmount",
  "arnonaProrateFirstMonth", "arnonaFirstMonthAmount",
  "municipalProrateFirstMonth", "municipalFirstMonthAmount",
  /* איך משלמים שכר דירה ובכמה שיקים — מסוכם מול כל דייר, ולכן שדה של
     התקופה. undefined/null = ברירת המחדל מההגדרות. */
  "payMethodRent", "rentChecks",
  // וכך גם חשבונות הצריכה וועד הבית — לכל דייר האמצעי שסוכם מולו.
  "payMethodBills", "payMethodVaad",
  // מי משלם למי על כל סעיף צריכה — נקבע מול דייר, ולכן שדה של התקופה.
  "utilities",
  "contents", "intake", "guarantors", "contracts",
  /* בקשות הארכה שהדייר שלח מהאזור האישי. שייכות לתקופה בדיוק כמו
     החוזים: דייר חדש לא יורש בקשה של קודמו. מסמכים ישנים בלי השדה
     נקראים כרשימה ריקה. */
  "extensionRequests"];

const newTenancy = (aptId, src = {}) => ({
  id: uid(), aptId,
  tenant: src.tenant || "", phone: src.phone || "",
  rent: Number(src.rent) || 0, rentSteps: src.rentSteps || [],
  leaseStart: src.leaseStart || TODAY, leaseEnd: src.leaseEnd || "",
  prorateFirstMonth: src.prorateFirstMonth === true,
  firstMonthAmount: src.firstMonthAmount != null && src.firstMonthAmount !== "" ? Number(src.firstMonthAmount) : null,
  rentDay: Number(src.rentDay) || 1, billsDay: Number(src.billsDay) || 15,
  billsCycle: Number(src.billsCycle) || 1, billsAnchor: Number(src.billsAnchor) || 1,
  vaad: Number(src.vaad) || 0, vaadActive: src.vaadActive !== false,
  arnona: Number(src.arnona) || 0, arnonaActive: src.arnonaActive === true,
  municipal: Number(src.municipal) || 0, municipalActive: src.municipalActive === true,
  municipalLabel: src.municipalLabel || "",
  /* undefined = השתמש בברירת המחדל הגלובלית. מערך ריק = בחירה מפורשת
     של "בלי ביטחונות", ולכן אסור להחליף אותו בברירת מחדל. */
  securities: Array.isArray(src.securities) ? src.securities : undefined,
  rates: src.rates || {}, rateSteps: src.rateSteps || [], contents: src.contents || "",
  utilities: src.utilities || {},
  intake: src.intake || {}, guarantors: src.guarantors || [], contracts: src.contracts || [],
  extensionRequests: Array.isArray(src.extensionRequests) ? src.extensionRequests : [],
  closed: false, closedAt: null, closeNotes: "", depositReturned: null,
});

/* deleted מסונן כאן ולא בכל אתר קריאה בנפרד: כל מי ששואל על תקופות
   הנכס — הכרטיס, החוזה, הגבייה, syncApartments — עובר דרך הפונקציה הזו,
   ותקופה שנמחקה לא אמורה לצוץ באף אחד מהם. */
const tenanciesOf = (d, aptId) => (d.tenancies || [])
  .filter((t) => t.aptId === aptId && !t.deleted)
  .sort((a, b) => (a.leaseStart < b.leaseStart ? -1 : a.leaseStart > b.leaseStart ? 1 : 0));

/** התקופות שנמחקו אך ההיסטוריה שלהן נשמרה — לארכיון ולשחזור. */
const deletedTenancies = (d, aptId = null) => (d.tenancies || [])
  .filter((t) => t.deleted && (aptId === null || t.aptId === aptId))
  .sort((a, b) => (a.deletedAt < b.deletedAt ? 1 : -1));

/* התקופה שבתוקף בתאריך נתון: התחילה, לא נסגרה, וטרם הסתיימה */
function tenancyAt(d, aptId, date = TODAY) {
  const list = tenanciesOf(d, aptId).filter((t) => t.tenant && !t.closed && t.leaseStart <= date);
  const live = list.filter((t) => !t.leaseEnd || t.leaseEnd >= date);
  return (live.length ? live : list)[list.length ? list.length - 1 : 0] || live[live.length - 1] || null;
}
const currentTenancy = (d, aptId) => tenancyAt(d, aptId, TODAY);

/**
 * התקופה שאליה נכתבים עדכונים.
 *
 * currentTenancy לבדה לא מספיקה: כשמכניסים דייר שנכנס בעוד חודש אין
 * תקופה *פעילה*, ולכן כל מה שהאשף קבע — ועד בית, מי משלם למי, ביטחונות,
 * מועדי חיוב — נכתב לנכס בלבד ונמחק בטעינה הבאה על ידי syncApartments.
 * כאן הסדר הוא: הפעילה, אחרת הקרובה שעוד לא התחילה, אחרת האחרונה שלא
 * נסגרה. null רק כשאין בכלל תקופה לכתוב אליה.
 */
function targetTenancy(d, aptId) {
  const cur = currentTenancy(d, aptId);
  if (cur) return cur;

  const upcoming = upcomingTenancies(d, aptId);
  if (upcoming.length) return upcoming[0];

  const open = tenanciesOf(d, aptId).filter((t) => !t.closed);
  return open[open.length - 1] || null;
}
const upcomingTenancies = (d, aptId) => tenanciesOf(d, aptId).filter((t) => t.tenant && !t.closed && t.leaseStart > TODAY);
const pastTenancies = (d, aptId) => tenanciesOf(d, aptId).filter((t) => t.closed);

/* התקופה ששייכת לחודש נתון — לייחוס תשלומים, חשבונות והכנסות */
function tenancyForMonth(d, aptId, mk_) {
  const [y, m] = mk_.split("-").map(Number);
  const first = `${mk_}-01`;
  /* יום אחרון אמיתי של החודש, לא 28 קבוע — דייר שנכנס ב-29,30,31
     (למשל 31 באוגוסט) היה נופל מחוץ לטווח ולא משויך לחודש שלו בכלל:
     לא שכר דירה, לא ועד, לא צריכה — התא בלוח הגבייה נשאר ריק לגמרי. */
  const last = `${mk_}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
  const list = tenanciesOf(d, aptId).filter((t) => t.tenant);
  const hit = list.filter((t) => {
    const end = tenancyEnd(t);
    return t.leaseStart <= last && (!end || end >= first);
  });
  /* חודש שההחלפה קרתה בו שייך לתקופה היוצאת — מי שגר בו ראשון. עד
     25.8.2026 החודש נפל על התקופה החדשה (hit האחרונה), ולכן חובות,
     חשבונות וקריאות של הדייר הקודם הופיעו על שם הדייר הנכנס בכרטיס,
     בלוחות ובאזור האישי שלו. hit ממוין לפי leaseStart, ולכן [0] היא
     הוותיקה מבין החופפות; כשאין חפיפה אין שינוי. */
  return hit[0] || null;
}

/* משכפל את פרטי התקופה הפעילה אל הנכס, כדי שהמסכים הקיימים ימשיכו לעבוד */
function syncApartments(d) {
  const apts = d.apartments.map((a) => {
    const t = currentTenancy(d, a.id);
    /* נכס בלי תקופה פעילה: אין דייר, אבל החוזים והערבים שכבר נרשמו עליו
       נשארים. עד שהתקופה נפתחת אין לאן להעביר אותם, ואיפוס כאן היה מוחק
       חתימת ערב שנקלטה רגע קודם. */
    if (!t) {
      return {
        ...a, tenant: "", phone: "",
        guarantors: a.guarantors || [], contracts: a.contracts || [],
        inventory: a.inventory || "",
      };
    }
    const patch = {};
    TEN_FIELDS.forEach((k) => { patch[k] = t[k]; });
    return { ...a, ...patch };
  });
  return { ...d, apartments: sortApartments(apts, d.settings) };
}

/**
 * סדר הנכסים בכל המערכת.
 *
 * "manual" (ברירת המחדל) — סדר המערך כפי שנקבע בהגדרות.
 * "billsDay" — לפי יום תשלום החשבונות, המוקדם קודם; פנויות בסוף; שוויון
 * נשבר לפי הסדר הידני. הסידור רץ כאן, ב-syncApartments, כדי שיחול על
 * טעינה ועל כל פעולה — ולא רק ברגע שנבחר בהגדרות: דייר שיום החשבונות
 * שלו השתנה עובר מקום מעצמו.
 */
const APT_SORTS = ["manual", "billsDay"];
function sortApartments(apts, settings) {
  const mode = APT_SORTS.includes(settings?.aptSort) ? settings.aptSort : "manual";
  if (mode === "manual") return apts;
  return apts
    .map((a, i) => ({ a, i }))
    .sort((x, y) =>
      ((x.a.tenant ? 0 : 1) - (y.a.tenant ? 0 : 1))
      || (billsDayOf(x.a, settings) - billsDayOf(y.a, settings))
      || (x.i - y.i))
    .map((x) => x.a);
}

/**
 * החוזים שיש להציג לנכס.
 *
 * apt.contracts הוא שיקוף של התקופה *הפעילה*. בנכס שיש בו רק תקופה
 * עתידית (חוזה נחתם, הדייר נכנס בעוד חודש) הוא מחזיק עותק ישן שלא
 * מתעדכן — ולכן חוזה שנשלח לחתימה הוסיף להופיע כ"טיוטה". כאן המקור
 * הוא התקופה שאליה כותבים (פעילה → קרובה → אחרונה שלא נסגרה), ובלעדיה
 * מה שרשום על הנכס.
 */
function contractsOf(d, aptId) {
  const t = targetTenancy(d, aptId);
  if (t && (t.contracts || []).length) return t.contracts;
  const apt = (d.apartments || []).find((a) => a.id === aptId);
  return apt?.contracts || [];
}

/* ============ בקשות הארכה מהאזור האישי ============
   הדייר מבקש להאריך את החוזה; בעל הנכס מאשר (ואז נוצר נספח הארכה) או
   דוחה. הבקשה חיה על תקופת השכירות, כמו החוזים, והנכס משקף אותה.

   מצבים: pending → approved (עם contractId של הנספח) | declined (עם
   reason) | withdrawn (הדייר ביטל). בקשה אחת ממתינה לכל היותר. */

const EXTENSION_STATUS = {
  pending: "ממתינה להחלטת בעל הנכס",
  approved: "אושרה",
  declined: "נדחתה",
  withdrawn: "בוטלה על ידי הדייר",
};

/** היום שאחרי תאריך — תחילת תקופת ההארכה. */
const dayAfter = (d) => new Date(new Date(d).getTime() + 86400000).toISOString().slice(0, 10);

/** בקשות ההארכה של הנכס — מהתקופה שאליה כותבים, ובלעדיה מהנכס. */
function extensionRequestsOf(d, aptId) {
  const t = targetTenancy(d, aptId);
  if (t) return t.extensionRequests || [];
  const apt = (d.apartments || []).find((a) => a.id === aptId);
  return apt?.extensionRequests || [];
}

/** הבקשה שממתינה להחלטה, אם יש. */
const pendingExtensionRequest = (d, aptId) =>
  extensionRequestsOf(d, aptId).find((r) => r.status === "pending") || null;

/** כל הבקשות הממתינות בחשבון — למסך הסקירה ולהתראות. */
function pendingExtensionRequests(d) {
  return (d.apartments || []).flatMap((a) => {
    const r = pendingExtensionRequest(d, a.id);
    return r ? [{ apt: a, request: r }] : [];
  });
}

/**
 * כותב את רשימת הבקשות לתקופה (מקור האמת) ולנכס (השיקוף) — בתוך
 * applyAction, על העותק A. בלי הכתיבה לתקופה syncApartments היה מוחק
 * את הבקשה בטעינה הבאה, בדיוק כמו שקרה לערבים ולנספחי ההארכה בעבר.
 */
function writeExtensionRequests(A, aptId, tenancyId, list) {
  if (tenancyId) {
    A.tenancies = (A.tenancies || []).map((t) => (t.id === tenancyId ? { ...t, extensionRequests: list } : t));
  }
  A.apartments = A.apartments.map((a) => (a.id === aptId ? { ...a, extensionRequests: list } : a));
}

/**
 * כותב את רשימת הערבים למקום שממנו היא באמת נקראת.
 *
 * מקור האמת לערבים הוא תקופת השכירות ולא הנכס — הנכס רק משקף את התקופה
 * הפעילה (ראו syncApartments). כתיבה ישירה ל-apt.guarantors נראית עובדת
 * על המסך ונמחקת בטעינה הבאה, ולכן כל מי שמעדכן ערבים חייב לעבור כאן.
 */
function setGuarantors(d, aptId, list) {
  /* targetTenancy ולא currentTenancy: דייר שחתם על החוזה אך עוד לא הגיע
     יום הכניסה שלו (leaseStart עתידי) הוא בדיוק מתי שהערבים שלו נחתמים —
     currentTenancy ("מי גר כאן היום") לא רואה תקופה כזו בכלל, וכתיבה
     שנפלה כאן לענף "אין תקופה" הייתה נשמרת על הנכס. זה עבד כל עוד אף אחד
     לא בדק — אבל ברגע שהתקופה כן נהיית פעילה, syncApartments ממזג את
     guarantors שלה (שלא עודכן) במקום את מה שעל הנכס, וההערב/החתימה
     "נעלמים". אותו כלל בדיוק שכבר משמש כל כתיבה אחרת שהאשף מבצע. */
  const t = targetTenancy(d, aptId);
  if (!t) {
    // אין בכלל תקופה לכתוב אליה — נשמר על הנכס, ו-syncApartments כבר לא מוחק.
    return syncApartments({
      ...d,
      apartments: d.apartments.map((a) => (a.id === aptId ? { ...a, guarantors: list } : a)),
    });
  }
  return syncApartments({
    ...d,
    tenancies: d.tenancies.map((x) => (x.id === t.id ? { ...x, guarantors: list } : x)),
  });
}

/* קוד גישה לדייר — 6 ספרות, נגזר ממזהה הנכס אם לא הוגדר ידנית */
function accessCode(a) {
  if (a.accessCode) return String(a.accessCode);
  let h = 0;
  for (const ch of String(a.id)) h = (h * 31 + ch.charCodeAt(0)) % 1000000;
  return String(h).padStart(6, "0");
}

function findApt(list, q) {
  if (!q) return null;
  const s = norm(q);
  if (!s) return null;
  const exact = list.find((a) => a.id === String(q).trim()) || list.find((a) => norm(a.name) === s || norm(a.tenant) === s);
  if (exact) return exact;
  const partial = list.find((a) => norm(a.name).includes(s) || (a.tenant && norm(a.tenant).includes(s)) || s.includes(norm(a.name)));
  if (partial) return partial;
  // התאמה לפי מילים משותפות — "יחידה 3" מול "יחידת דיור 3, מושב אחיהוד"
  const words = s.split(" ").filter((w) => w.length > 1);
  const scored = list.map((a) => {
    const hay = norm(a.name) + " " + norm(a.tenant);
    return { a, score: words.filter((w) => hay.includes(w)).length };
  }).sort((x, y) => y.score - x.score);
  return scored[0] && scored[0].score > 0 ? scored[0].a : null;
}

/* שמות פעולות ושדות שהמודל עלול להמציא — ממופים לשמות שהאפליקציה מכירה */
/* ============ שווי נכסים והלוואות ============
   שווי הנכס נרשם כרשומות לאורך זמן (a.values) — "נכון לתאריך" ולא מספר
   בודד, כדי שאפשר יהיה לראות את ההתפתחות. ההלוואות (d.loans) הן רשימה
   ברמת החשבון: משכנתא יכולה לממן כמה נכסים, ולכן הקישור לנכס הוא רשות.

   כל החישובים כאן לפי לוח שפיצר (החזר חודשי קבוע) וללא הצמדה למדד —
   הצמדה ושינויי ריבית עתידיים אינם ידועים; יתרה ידנית שנמסרה גוברת
   על המחושבת בדיוק בגלל זה. */

/** ההחזר החודשי בלוח שפיצר. ריבית שנתית באחוזים, תקופה בחודשים. */
function loanMonthly(principal, annualPct, months) {
  const P = Number(principal) || 0;
  const n = Math.max(1, Math.round(Number(months) || 0));
  const r = (Number(annualPct) || 0) / 100 / 12;
  if (!P) return 0;
  if (!r) return P / n;
  return (P * r) / (1 - Math.pow(1 + r, -n));
}

/** היתרה אחרי k תשלומים בלוח שפיצר. */
function loanBalanceAfter(principal, annualPct, months, k) {
  const P = Number(principal) || 0;
  const n = Math.max(1, Math.round(Number(months) || 0));
  const r = (Number(annualPct) || 0) / 100 / 12;
  const m = Math.min(Math.max(0, Math.round(Number(k) || 0)), n);
  if (!P) return 0;
  if (!r) return Math.max(0, P - (P / n) * m);
  const f = Math.pow(1 + r, m);
  return Math.max(0, P * f - (loanMonthly(P, annualPct, n) * (f - 1)) / r);
}

/** הפרש חודשים שלמים בין שני תאריכים (YYYY-MM או YYYY-MM-DD). */
function monthsBetween(from, to) {
  if (!from || !to) return 0;
  const [fy, fm] = String(from).split("-").map(Number);
  const [ty, tm] = String(to).split("-").map(Number);
  if (!fy || !fm || !ty || !tm) return 0;
  return Math.max(0, (ty - fy) * 12 + (tm - fm));
}

/**
 * מצב ההלוואה היום: החזר חודשי, כמה שולם, כמה נשאר ומה היתרה.
 * balance ידני (עם balanceDate) גובר על היתרה המחושבת — הוא כולל
 * הצמדות ושינויי ריבית שלוח שפיצר פשוט אינו רואה.
 */
function loanState(loan, today = TODAY) {
  const months = Math.max(1, Math.round(Number(loan.months) || 0));
  const paidMonths = Math.min(monthsBetween(loan.start, today), months);
  const remainingMonths = Math.max(0, months - paidMonths);
  const monthly = loanMonthly(loan.principal, loan.rate, months);
  const manual = loan.balance !== undefined && loan.balance !== null && loan.balance !== "";
  const balance = remainingMonths === 0 && !manual
    ? 0
    : manual ? Number(loan.balance) || 0
      : loanBalanceAfter(loan.principal, loan.rate, months, paidMonths);
  const totalRemaining = monthly * remainingMonths;
  return {
    monthly, paidMonths, remainingMonths, balance, totalRemaining,
    interestRemaining: Math.max(0, totalRemaining - balance),
    manualBalance: manual,
  };
}

/** הצעת מחזור: אותה יתרה, ריבית ותקופה אחרות — מה ההחזר ומה נחסך. */
function refinanceQuote(state, newRate, newMonths = null) {
  const m = Math.max(1, Math.round(Number(newMonths) || state.remainingMonths || 1));
  const monthly = loanMonthly(state.balance, newRate, m);
  const total = monthly * m;
  return {
    monthly, months: m, total,
    monthlySaving: state.monthly - monthly,
    totalSaving: state.totalRemaining - total,
  };
}

/** שווי עתידי בריבית דריבית שנתית. years יכול להיות שבור. */
const projectValue = (amount, annualPct, years) =>
  (Number(amount) || 0) * Math.pow(1 + (Number(annualPct) || 0) / 100, Math.max(0, Number(years) || 0));

/** רשומת השווי האחרונה של נכס (לפי תאריך), או null. */
const latestValue = (apt) => {
  const list = (apt?.values || []).filter((v) => v && v.amount != null);
  if (!list.length) return null;
  return [...list].sort((a, b) => String(a.date || "").localeCompare(String(b.date || ""))).at(-1);
};

/** ההלוואה המקושרת לנכס — הראשונה שנמצאת. הנכס עצמו לא שומר קישור;
    המשכנתא היא שמצביעה עליו (aptId), בדיוק כמו set_loan למעלה. */
const loanFor = (d, aptId) => (d.loans || []).find((l) => l.aptId === aptId) || null;

/** הון עצמי בזמן הרכישה: מחיר הרכישה פחות קרן המשכנתא שנלקחה נגדו.
    null (לא 0!) כשאין מחיר רכישה רשום — חוסר מידע שונה מהון עצמי אפס. */
function purchaseEquity(apt, loan) {
  if (apt?.purchasePrice == null) return null;
  return apt.purchasePrice - (loan?.principal || 0);
}

/** הון עצמי נוכחי: השווי האחרון שנרשם לנכס פחות יתרת המשכנתא הנוכחית.
    null כשאין שום רשומת שווי — אין ממה לחסר. */
function currentEquity(apt, loan) {
  const v = latestValue(apt);
  if (!v) return null;
  return v.amount - (loan ? loanState(loan).balance : 0);
}

const TYPE_ALIASES = {
  add_property: "add_apartment", create_apartment: "add_apartment", add_unit: "add_apartment", new_apartment: "add_apartment",
  delete_apartment: "remove_apartment", remove_property: "remove_apartment", delete_property: "remove_apartment",
  edit_apartment: "update_apartment", update_property: "update_apartment", set_apartment: "update_apartment",
  set_rent: "update_apartment", update_phone: "update_apartment", set_phone: "update_apartment", update_tenant: "update_apartment",
  set_tenant: "change_tenant", add_tenant: "change_tenant", replace_tenant: "change_tenant", move_in: "change_tenant",
  mark_rent_paid: "set_rent_paid", pay_rent: "set_rent_paid", set_payment: "set_rent_paid",
  delete_tenancy: "remove_tenancy", delete_tenant: "remove_tenancy",
  remove_tenant: "remove_tenancy",
  set_meter_reading: "set_meter", add_meter_reading: "set_meter", meter_reading: "set_meter",
  pay_bill: "set_bill", set_utility: "set_bill", set_bill_amount: "set_bill",
  mark_bills_paid: "set_bills_paid", bills_received: "set_bills_paid",
  pay_bills: "set_bills_paid", set_bills: "set_bills_paid",
  add_cost: "add_expense", new_expense: "add_expense",
  update_tariff: "set_tariff", set_rate: "set_tariff",
  renew_contract: "extend_contract", extend_lease: "extend_contract",
  // חוזה חדש/ראשון — חוזה מקורי, לא נספח. add_contract מופה בעבר ל-extend_contract
  // ודייר חדש קיבל "נספח הארכה" במקום חוזה.
  add_contract: "new_contract", create_contract: "new_contract", make_contract: "new_contract", sign_new_contract: "new_contract",
  delete_contract: "remove_contract", cancel_contract: "remove_contract",
  edit_contract: "set_contract_text", update_contract_text: "set_contract_text",
  // שווי נכס והלוואות
  set_value: "set_property_value", set_apartment_value: "set_property_value",
  property_value: "set_property_value", add_property_value: "set_property_value",
  delete_property_value: "remove_property_value",
  add_loan: "set_loan", update_loan: "set_loan", add_mortgage: "set_loan",
  set_mortgage: "set_loan", update_mortgage: "set_loan",
  delete_loan: "remove_loan", remove_mortgage: "remove_loan", delete_mortgage: "remove_loan",
  // הוצאות קבועות
  add_recurring_expense: "set_recurring_expense", update_recurring_expense: "set_recurring_expense", add_fixed_expense: "set_recurring_expense",
  delete_recurring_expense: "remove_recurring_expense", remove_fixed_expense: "remove_recurring_expense", delete_fixed_expense: "remove_recurring_expense",
};
const FIELD_ALIASES = {
  address: "name", apartment_name: "name", property: "apartment", apartmentId: "apartment", apartment_id: "apartment",
  unit: "apartment", propertyId: "apartment", aptId: "apartment", tenantName: "tenant", tenant_name: "tenant",
  tenantPhone: "phone", phoneNumber: "phone", phone_number: "phone", tel: "phone", mobile: "phone",
  rentAmount: "rent", monthlyRent: "rent", rent_amount: "rent", vaadBait: "vaad", committee: "vaad",
  lease_start: "leaseStart", lease_end: "leaseEnd", start_date: "from", end_date: "to",
  meterType: "meter", meter_type: "meter", billType: "bill", bill_type: "bill", utility: "bill",
  category: "cat", description: "desc", note: "notes",
  idNumber: "tenantId", id_number: "tenantId", tenant_id_number: "tenantId",
  partner_name: "partnerName", partner_id: "partnerId", partner_phone: "partnerPhone",
  payment_method: "method", paymentMethod: "method", via: "method", pay_method: "method",
  rent_day: "rentDay", rentPaymentDay: "rentDay", paymentDay: "rentDay", payment_day: "rentDay",
  bills_day: "billsDay", billsPaymentDay: "billsDay", billing_day: "billsDay",
  bills_cycle: "billsCycle", billingCycle: "billsCycle", billing_cycle: "billsCycle", cycle: "billsCycle",
};
const TARIFF_ALIASES = {
  "ביוב": "sewageRate", sewer: "sewageRate", sewage: "sewageRate",
  "חשמל": "elecRate", electricity: "elecRate", elec: "elecRate",
  "מים": "waterSewageRate", water: "waterSewageRate", "מים וביוב": "waterSewageRate",
  "גז": "gasFixed", gas: "gasFixed",
  "מדד": "indexPct", index: "indexPct",
  "איחור": "lateFeePct", "פיגור": "lateFeePct", late: "lateFeePct",
  "ביטחונות": "depositMonths", deposit: "depositMonths",
  billsday: "billsDay", bills_day: "billsDay", billspaymentday: "billsDay", billpaymentday: "billsDay",
  paymentday: "paymentDay", rentday: "paymentDay", rentpaymentday: "paymentDay",
  billscycle: "billsCycle", cycle: "billsCycle", billingcycle: "billsCycle",
};
const METER_ALIASES = { electricity: "elec", electric: "elec", power: "elec", "חשמל": "elec", water: "water", "מים": "water", "מים וביוב": "water", gas: "gas", "גז": "gas", cooking_gas: "gas", "בלון גז": "gas" };
const BILL_ALIASES = {
  "ארנונה": "arnona", arnona: "arnona", property_tax: "arnona", municipal_tax: "arnona",
  "היטל": "municipal", "היטל עירוני": "municipal", "מס עירוני": "municipal", levy: "municipal", electricity: "elec", electric: "elec", "חשמל": "elec", water: "water", "מים": "water", sewer: "sewage", "ביוב": "sewage", "גז": "gas", "ועד": "vaad", "ועד בית": "vaad", committee: "vaad", hoa: "vaad" };

function normalizeAction(act) {
  const o = {};
  if (act.__useNewTenancy) o.__useNewTenancy = true;
  if (act.tenancyId) o.tenancyId = act.tenancyId;
  Object.entries(act || {}).forEach(([k, v]) => { o[FIELD_ALIASES[k] || k] = v; });
  o.type = TYPE_ALIASES[o.type] || o.type;
  if (o.meter) o.meter = METER_ALIASES[String(o.meter).toLowerCase()] || o.meter;
  if (o.bill) o.bill = BILL_ALIASES[String(o.bill).toLowerCase()] || o.bill;
  if (o.tariff && !(o.tariff in defTariffs())) {
    const t = String(o.tariff).trim();
    o.tariff = TARIFF_ALIASES[t] || TARIFF_ALIASES[t.toLowerCase()] || String(t).replace("Rate", "").toLowerCase();
  }
  if (typeof o.paid === "string") o.paid = !["false", "no", "לא"].includes(o.paid.toLowerCase());
  if (o.month && /^\d{4}-\d{1}$/.test(o.month)) o.month = o.month.slice(0, 5) + "0" + o.month.slice(5);
  return o;
}

/* פרטי זהות שהעוזר שולח כשדות שטוחים (tenantId, partnerName...) מקופלים
   לתוך intake — המבנה שהחוזה, אשף הקליטה והאזור האישי כבר קוראים ממנו.
   act.intake שנשלח כאובייקט (הטופס הידני) ממשיך לעבוד כמו שהוא. */
const INTAKE_FLAT = ["tenantId", "email", "partnerName", "partnerId", "partnerPhone"];
const intakeOf = (act) => {
  const o = { ...(act.intake || {}) };
  INTAKE_FLAT.forEach((k) => {
    const v = act[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") o[k] = String(v).trim();
  });
  return o;
};

function applyAction(d, rawAct) {
  // נרמול גם כאן ולא רק ב-applyActionFocused: פעולה עם meter:"גז" או
  // type:"add_meter_reading" שהגיעה ישירות לכאן נכנסה קודם כשגויה בשקט.
  // normalizeAction אידמפוטנטית, ולכן הקריאה הכפולה לא משנה דבר.
  const act = normalizeAction(rawAct);
  const A = { ...d, apartments: [...d.apartments], payments: { ...d.payments }, bills: { ...d.bills }, expenses: [...d.expenses], media: [...(d.media || [])] };
  const month = act.month && /^\d{4}-\d{2}$/.test(act.month) ? act.month : CUR_KEY;
  const apt = findApt(A.apartments, act.apartment);
  const need = () => ({ data: d, log: `לא נמצאה דירה בשם "${act.apartment}"`, fail: true });

  switch (act.type) {
    case "add_apartment": {
      const newId = uid();
      A.apartments.push({
        id: newId, name: act.name || "דירה חדשה", rooms: act.rooms || "", tenant: act.tenant || "", phone: act.phone || "",
        rent: Number(act.rent) || 0, vaad: Number(act.vaad) || 0, leaseStart: act.leaseStart || "", leaseEnd: act.leaseEnd || "",
        notes: act.notes || "", history: [], contracts: [],
        rentDay: Number(act.rentDay) || 1, billsDay: Number(act.billsDay) || 15,
        billsCycle: Number(act.billsCycle) || 1, billsAnchor: Number(act.billsAnchor) || 1,
        propertyType: act.propertyType || "unit", authority: act.authority || "",
        vaadActive: !!act.vaad, contents: act.contents || "", inventory: act.inventory || act.contents || "", rentSteps: [],
        utilities: act.utilities && typeof act.utilities === "object" ? act.utilities : {},
      });
      return { data: A, log: `נוסף נכס: ${act.name || "דירה חדשה"}`, focus: newId };
    }
    case "remove_apartment": {
      if (!apt) return need();
      A.apartments = A.apartments.filter((a) => a.id !== apt.id);
      Object.keys(A.payments).forEach((k) => k.startsWith(apt.id + "|") && delete A.payments[k]);
      Object.keys(A.bills).forEach((k) => k.startsWith(apt.id + "|") && delete A.bills[k]);
      A.expenses = A.expenses.filter((e) => e.aptId !== apt.id);
      A.media = A.media.filter((m) => m.aptId !== apt.id);
      /* תקופות השכירות והקריאות נשארו כאן מאחור עד עכשיו — רשומות יתומות
         שמצביעות על נכס שלא קיים, ושהחזיקו בחיים גם את הקבצים שלהן. */
      A.tenancies = (A.tenancies || []).filter((t) => t.aptId !== apt.id);
      A.readings = (A.readings || []).filter((r) => r.aptId !== apt.id);
      /* הלוואה שמקושרת לנכס שנמחק לא נמחקת — החוב קיים גם בלי הנכס.
         רק הקישור מתנתק. */
      A.loans = (A.loans || []).map((l) => (l.aptId === apt.id ? { ...l, aptId: "" } : l));
      // הוצאה קבועה שמפוצלת בין כמה נכסים — הנכס שנמחק יורד מהרשימה,
      // התבנית עצמה (ושאר הנכסים שבה) נשארת.
      A.recurringExpenses = (A.recurringExpenses || []).map((r) => (
        (r.aptIds || []).includes(apt.id) ? { ...r, aptIds: r.aptIds.filter((id) => id !== apt.id) } : r
      ));
      return { data: A, log: `הוסר הנכס ${apt.name}` };
    }
    case "update_apartment": {
      if (!apt) return need();
      const p = {};
      ["name", "rooms", "tenant", "phone", "leaseStart", "leaseEnd", "notes", "propertyType", "authority", "contents", "inventory", "purchaseDate"].forEach((k) => { if (act[k] !== undefined) p[k] = act[k]; });
      // מחיר רכישה — עובדה היסטורית חד-פעמית, לא רשומת שווי. 0/ריק לא נכתב כ-0 בטעות: רק מספר חיובי אמיתי נשמר.
      if (act.purchasePrice !== undefined) p.purchasePrice = act.purchasePrice === "" || act.purchasePrice == null ? null : Number(act.purchasePrice) || 0;
      if (act.vaadActive !== undefined) p.vaadActive = !!act.vaadActive;
      if (act.arnona !== undefined) p.arnona = Number(act.arnona) || 0;
      if (act.arnonaActive !== undefined) p.arnonaActive = !!act.arnonaActive;
      if (act.municipal !== undefined) p.municipal = Number(act.municipal) || 0;
      if (act.municipalActive !== undefined) p.municipalActive = !!act.municipalActive;
      if (act.municipalLabel !== undefined) p.municipalLabel = String(act.municipalLabel || "");
      /* ביטחונות — מערך ריק הוא בחירה מפורשת של "בלי ביטחונות" ולכן חייב
         לעבור. מסננים לסוגים שבקטלוג: פעולה שמגיעה מהמודל לא אמורה
         להזריק מפתחות שאיש לא יודע להציג או לכתוב בחוזה. */
      if (Array.isArray(act.securities)) {
        p.securities = act.securities
          .filter((x) => x && SECURITY_BY_KEY[x.k])
          .map((x) => {
            const spec = SECURITY_BY_KEY[x.k];
            return spec.unit === "months"
              ? { k: x.k, months: Number(x.months) || 0 }
              : { k: x.k, amount: Number(x.amount) || 0, ...(x.expiry ? { expiry: String(x.expiry) } : {}) };
          });
      }
      /* איך משלמים — לשכירות הזו: שכר דירה, חשבונות צריכה וועד בית, כל
         אחד בנפרד. ערך לא מוכר, "" או null מוחקים את הקביעה והתקופה
         חוזרת לברירת המחדל מההגדרות. */
      for (const mk_ of ["payMethodRent", "payMethodBills", "payMethodVaad"]) {
        if (act[mk_] === undefined) continue;
        const raw = act[mk_] == null ? "" : String(act[mk_]).trim();
        p[mk_] = PAY_METHOD_BY_KEY[raw.toLowerCase()] ? raw.toLowerCase() : (METHOD_ALIASES[norm(raw)] || null);
      }
      if (act.rentChecks !== undefined) p.rentChecks = Math.max(0, Math.round(Number(act.rentChecks) || 0));
      /* ערבים — הרשימה המלאה מחליפה את הקיימת (כמו securities). שם חובה;
         מהעוזר החופשי הם מגיעים בלי id. נכנסים לסעיף הערבות ולנספח ב'. */
      if (Array.isArray(act.guarantors)) {
        p.guarantors = act.guarantors
          .filter((g) => g && String(g.name || "").trim())
          .map((g) => ({
            id: g.id || uid(), name: String(g.name || "").trim(), idNumber: String(g.idNumber || ""),
            phone: String(g.phone || ""), address: String(g.address || ""),
            idPhoto: g.idPhoto || null, signature: g.signature || null, signedAt: g.signedAt || null,
          }));
      }
      // שינוי תעריף נשמר כמדרגה מתוארכת, כדי שחישובי עבר לא ישתנו
      if (act.rates) {
        const curR = ratesAt(apt, A.settings);
        const changed = {};
        ["elec", "waterSewage", "gas"].forEach((k) => {
          const v = act.rates[k];
          if (v != null && v !== "" && Number(v) !== Number(curR[k])) changed[k] = Number(v);
        });
        const hadRates = apt.rates && Object.keys(apt.rates).some((k) => apt.rates[k]);
        if (!hadRates) p.rates = { ...(apt.rates || {}), ...act.rates };
        else if (Object.keys(changed).length) {
          const from = act.effectiveFrom || TODAY;
          p.rateSteps = [...(apt.rateSteps || []).filter((x) => x.from !== from), { from, ...changed }]
            .sort((x, y) => (x.from < y.from ? -1 : 1));
        }
      }
      /* utilities — ממוזג ולא נדרס: אשף אחד שואל רק על חשמל, ואין סיבה
         שהתשובה תמחק הסדר שכבר נקבע על המים. */
      if (act.utilities && typeof act.utilities === "object") {
        const merged = { ...(apt.utilities || {}) };
        for (const [uk, mode] of Object.entries(act.utilities)) {
          // ועד בית אינו ב-UTILITIES (הוא לא סעיף עם ספק חיצוני קבוע כמו
          // חשמל/מים/גז/ארנונה) אבל utilityMode() כבר מטפל בו כסעיף לכל
          // דבר — בלי היוצא-מן-הכלל הזה כל עדכון "מי משלם למי" על ועד
          // דרך update_apartment (למשל מכרטיס הדייר) נבלע בשקט.
          if (uk !== "vaad" && !UTILITY_BY_KEY[uk]) continue;
          if (UTILITY_MODE_KEYS.includes(mode)) merged[uk] = mode;
          else if (mode === null || mode === "") delete merged[uk];
        }
        p.utilities = merged;
      }
      // ערבים — מטופלים למעלה (נרמול, סינון שמות ריקים, id לכל ערב).
      if (Array.isArray(act.rentSteps)) p.rentSteps = act.rentSteps;
      ["vaad", "rentDay", "billsDay", "billsCycle", "billsAnchor"].forEach((k) => { if (act[k] !== undefined) p[k] = Number(act[k]); });
      if (act.prorateFirstMonth !== undefined) p.prorateFirstMonth = act.prorateFirstMonth === true;
      if (act.firstMonthAmount !== undefined) p.firstMonthAmount = act.firstMonthAmount == null || act.firstMonthAmount === "" ? null : Number(act.firstMonthAmount);
      // אותו חיוב יחסי לחודש הראשון, לכל סעיף קבוע (ועד/ארנונה/היטל) בנפרד.
      FIXED_BILLS.forEach(({ amountField }) => {
        const pKey = `${amountField}ProrateFirstMonth`, aKey = `${amountField}FirstMonthAmount`;
        if (act[pKey] !== undefined) p[pKey] = act[pKey] === true;
        if (act[aKey] !== undefined) p[aKey] = act[aKey] == null || act[aKey] === "" ? null : Number(act[aKey]);
      });
      /* תיקון טעות — הסכום היה שגוי מלכתחילה, ולכן הוא מחליף את
         המדרגה האחרונה במקום להוסיף אחת חדשה. בלי זה מי שתיקן הקלדה
         השאיר את כל החודשים הקודמים בסכום השגוי, ורק מכאן והלאה נכון. */
      /* עריכה של תקופה שאינה הפעילה (דייר עתידי, או כזה שכבר עזב):
         שכר הדירה נקבע עליה ישירות. מנגנון המדרגות מודד מול apt.rent,
         שהוא הסכום של הדייר *הנוכחי* — ומדרגה שנוצרת מהשוואה כזו הייתה
         משנה את החיובים של התקופה הלא נכונה. */
      const editingOther = act.tenancyId
        && act.tenancyId !== (currentTenancy(A, apt.id)?.id || null);

      if (act.rent !== undefined && editingOther) {
        p.rent = Number(act.rent);
      } else if (act.rent !== undefined && act.rentFix === true) {
        const steps = apt.rentSteps || [];
        p.rentSteps = steps.length
          ? steps.map((x, i) => (i === steps.length - 1 ? { ...x, rent: Number(act.rent) } : x))
          : [];
        p.rent = Number(act.rent);
      // שינוי שכר דירה נשמר כמדרגה מתוארכת ואינו מוחק את הסכום הקודם
      } else if (act.rent !== undefined && Number(act.rent) !== Number(apt.rent)) {
        const from = act.effectiveFrom || TODAY;
        const prevSteps = apt.rentSteps || [];

        /* מדרגה ראשונה — קודם מקבעים את הסכום הקודם.
           apt.rent תמיד מחזיק את הסכום העדכני, ולכן בלי המדרגה הזו
           rentAt לתאריך שלפני השינוי היה נופל אליו ומחזיר את הסכום
           החדש. התוצאה: העלאת שכר דירה מייקרת רטרואקטיבית את כל
           החודשים שקדמו לה, וכרטיס החוב מציג חוב שמעולם לא נוצר. */
        const baseline = prevSteps.length || !Number(apt.rent)
          ? []
          : [{ from: apt.leaseStart || "1970-01-01", rent: Number(apt.rent) }];

        p.rentSteps = [...baseline, ...prevSteps.filter((x) => x.from !== from), { from, rent: Number(act.rent) }]
          .sort((x, y) => (x.from < y.from ? -1 : 1));
        p.rent = Number(act.rent);
      } else if (act.rent !== undefined) p.rent = Number(act.rent);
      {
        const ip = intakeOf(act);
        if (Object.keys(ip).length) p.intake = { ...(apt.intake || {}), ...ip };
      }
      /* סעיף שמירת שבת ברמת הנכס — עוקף את ההגדרה הכללית בחוזים של הנכס
         הזה בלבד. נכס שלא נקבע לו דבר ממשיך לפי ההגדרות. */
      if (act.clauseShabbat === true || act.clauseShabbat === false) p.clauseShabbat = act.clauseShabbat;
      A.apartments = A.apartments.map((a) => (a.id === apt.id ? { ...a, ...p } : a));
      // שדות ששייכים לתקופת השכירות מתעדכנים גם ברשומת התקופה
      const tid = act.tenancyId || targetTenancy(A, apt.id)?.id;
      if (tid) {
        const tp = {};
        TEN_FIELDS.forEach((k) => { if (p[k] !== undefined) tp[k] = p[k]; });
        if (Object.keys(tp).length) A.tenancies = A.tenancies.map((t) => (t.id === tid ? { ...t, ...tp } : t));
      }
      return { data: A, log: `עודכנו פרטי ${apt.name}` };
    }
    case "change_tenant": {
      if (!apt) return need();
      const cur = currentTenancy(A, apt.id);
      // קביעת מועד סיום לתקופה הנוכחית — היא ממשיכה לפעול עד אז, ולא נמחקת
      if (cur && act.moveOut) {
        A.tenancies = (A.tenancies || []).map((t) => (t.id === cur.id ? { ...t, leaseEnd: act.moveOut } : t));
      }
      if (!act.tenant) {
        return { data: A, log: cur ? `נקבע מועד סיום ל${cur.tenant} ב-${heDate(act.moveOut || TODAY)}` : `${apt.name} פנויה`, focus: apt.id };
      }
      /* ערבים שנרשמו לפני שהתקופה נוצרה. באשף קליטת הדייר הם נשמרים על
         הנכס (אין עדיין תקופה לכתוב אליה), ו-syncApartments היה מוחק
         אותם ברגע שהתקופה נוצרה עם רשימה ריקה — כלומר כל ערב שהוקלד
         באשף נעלם בשמירה. act.guarantors גובר; אחרת נלקחים אלה שעל
         הנכס, אבל רק כשאין תקופה פעילה שהם שייכים לה. */
      /* ערב ששייך כבר לתקופה כלשהי הוא ההיסטוריה של אותה תקופה ולא
         "טיוטה" — אחרת הערב של הדייר היוצא היה הופך לערב של הנכנס,
         כי בנכס פנוי הרשימה שלו נשארת משתקפת על הנכס. */
      const known = new Set((A.tenancies || [])
        .filter((t) => t.aptId === apt.id)
        .flatMap((t) => (t.guarantors || []).map((g) => g.id)));
      const staged = Array.isArray(act.guarantors)
        ? act.guarantors
        : (apt.guarantors || []).filter((g) => g.id && !known.has(g.id));
      const t = newTenancy(apt.id, {
        ...act,
        rent: act.rent,
        leaseStart: act.leaseStart || TODAY,
        /* ועד בית וארנונה/היטל הם תכונות של הנכס כברירת מחדל — תקופה חדשה
           יורשת אותם מהנכס אלא אם הקליטה אמרה אחרת. בלי זה נכס שהוגדר לו
           ועד 250 הראה "עדיין חסר: ועד בית" מיד אחרי קליטת הדייר. */
        vaad: act.vaad !== undefined ? act.vaad : apt.vaad,
        vaadActive: act.vaadActive !== undefined ? act.vaadActive : apt.vaadActive,
        arnona: act.arnona !== undefined ? act.arnona : apt.arnona,
        arnonaActive: act.arnonaActive !== undefined ? act.arnonaActive : apt.arnonaActive,
        municipal: act.municipal !== undefined ? act.municipal : apt.municipal,
        municipalActive: act.municipalActive !== undefined ? act.municipalActive : apt.municipalActive,
        municipalLabel: act.municipalLabel !== undefined ? act.municipalLabel : apt.municipalLabel,
        guarantors: staged,
        // ת"ז ובן/בת זוג שנמסרו עם הקליטה — נכנסים ל-intake של התקופה.
        intake: intakeOf(act),
        // מטלטלין: מה שנמסר לדייר הזה, ואם לא נאמר — רשימת הציוד של הנכס.
        contents: act.contents !== undefined ? act.contents : (apt.inventory || ""),
      });
      /* דייר עתידי שכבר רשום לנכס: הרישום החדש לא נוגע בו, אבל היומן
         מצביע על הכפילות — קליטה כפולה היא כמעט תמיד טעות. המסכים
         שואלים אם להחליף לפני שמגיעים לכאן; זו רשת ביטחון לשאר
         המסלולים (הוראה חופשית בצ'אט, פעולות ישנות). */
      /* דייר מחליף סוגר את התקופה הקודמת ביום שלפני כניסתו. עד 25.8.2026
         התקופה הקודמת נשארה פתוחה, וההצללה של tenancyForMonth (האחרונה
         גוברת) הסתירה את זה — וכשהייחוס תוקן לחודש-של-הוותיקה, תקופה
         פתוחה שכזו הייתה ממשיכה "לבעול" גם את החודשים של המחליף. חוזה
         העבר נשמר ב-contractEnd; מועד מפורש (moveOut) גובר. */
      if (cur && !act.moveOut) {
        const start = new Date(`${t.leaseStart}T00:00:00`);
        start.setDate(start.getDate() - 1);
        const dayBefore = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
        const curEnd = tenancyEnd(cur);
        if (!curEnd || curEnd >= t.leaseStart) {
          A.tenancies = (A.tenancies || []).map((x) => (x.id === cur.id ? {
            ...x, closed: true, closedAt: dayBefore,
            contractEnd: x.contractEnd || x.leaseEnd || null,
            leaseEnd: dayBefore,
          } : x));
        }
      }
      const clash = upcomingTenancies(A, apt.id);
      A.tenancies = [...(A.tenancies || []), t];
      const future = t.leaseStart > TODAY;
      const clashNote = clash.length
        ? ` — שימו לב: ${clash.map((x) => `${x.tenant} כבר רשום/ה להיכנס ב-${heDate(x.leaseStart)}`).join("; ")}. להחלפה מחקו את הרישום המיותר במסך "תקופות שכירות"`
        : "";
      /* leaseStart מחודש שכבר חלף — הדייר גר כאן תקופה ורק עכשיו נרשם.
         debtStatement מחשבת חוב לכל חודש מאז leaseStart שאין לו רישום
         תשלום, ובלי לסמן ידנית מה שכבר שולם המערכת תראה חוב שלא קיים. */
      const backdated = !future && t.leaseStart.slice(0, 7) < CUR_KEY;
      const backdateNote = backdated
        ? ' — תאריך הכניסה בעבר: כדי שהחודשים שכבר שולמו לא יופיעו כחוב, סמנו אותם ב"לוחות גבייה" (אפשר להזין שם גם סכום שונה מהרגיל, אם מה שהתקבל בפועל היה שונה)'
        : "";
      return {
        data: A,
        log: (future
          ? `${t.tenant} נרשם/ה ל${apt.name} החל מ-${heDate(t.leaseStart)} (תקופה עתידית)`
          : `${t.tenant} נכנס/ה ל${apt.name}`) + clashNote + backdateNote,
        focus: apt.id, tenancyId: t.id,
      };
    }
    case "close_tenancy": {
      if (!apt) return need();
      const list = tenanciesOf(A, apt.id).filter((t) => !t.closed && t.tenant);
      const target = act.tenancyId ? list.find((t) => t.id === act.tenancyId) : list[0];
      if (!target) return { data: d, log: "לא נמצאה תקופת שכירות פתוחה", fail: true };
      /* תאריך היציאה בפועל הוא סוף התקופה — הנכס פנוי להשכרה מאותו יום,
         ושכר הדירה מפסיק להיצבר. תאריך החוזה המקורי נשמר ב-contractEnd
         (לחוזה, לתיעוד ולפתיחה מחדש). עד כאן leaseEnd נשאר תאריך החוזה,
         ודייר שעזב במרץ המשיך "לחייב" עד אוגוסט. */
      const exit = act.date || TODAY;
      const resolution = act.debtResolution && DEBT_RESOLUTIONS.some((x) => x.k === act.debtResolution.kind)
        ? {
          kind: act.debtResolution.kind,
          amount: Number(act.debtResolution.amount) || 0,
          date: exit,
          notes: String(act.debtResolution.notes || "").trim(),
        }
        : null;
      A.tenancies = A.tenancies.map((t) => (t.id === target.id ? {
        ...t, closed: true, closedAt: exit,
        contractEnd: t.contractEnd || t.leaseEnd || null,
        leaseEnd: exit,
        closeNotes: act.notes || "", depositReturned: act.depositReturned ?? null,
        ...(resolution ? { debtResolution: resolution } : {}),
      } : t));
      const resLabel = resolution ? ` · ${DEBT_RESOLUTIONS.find((x) => x.k === resolution.kind).label}` : "";
      return { data: A, log: `תקופת השכירות של ${target.tenant} נסגרה ב-${heDate(exit)}${resLabel}`, focus: apt.id };
    }
    /** שינוי ייעוד החוב של תקופה שכבר נסגרה — חוב של הדייר / אבוד / ויתור. */
    case "resolve_tenancy_debt": {
      if (!apt) return need();
      const target = tenanciesOf(A, apt.id).find((t) => t.id === act.tenancyId && t.closed);
      if (!target) return { data: d, log: "לא נמצאה תקופה סגורה", fail: true };
      const spec = DEBT_RESOLUTIONS.find((x) => x.k === act.kind);
      if (!spec) return { data: d, log: `ייעוד חוב לא מוכר: ${act.kind}`, fail: true };
      A.tenancies = A.tenancies.map((t) => (t.id === target.id ? {
        ...t,
        debtResolution: { kind: spec.k, amount: Number(act.amount) || Number(t.debtResolution?.amount) || 0, date: act.date || TODAY, notes: String(act.notes ?? t.debtResolution?.notes ?? "").trim() },
      } : t));
      return { data: A, log: `החוב של ${target.tenant} סומן: ${spec.label}`, focus: apt.id };
    }
    /**
     * מחיקת דייר.
     *
     * שני מצבים, וההבדל ביניהם הוא כספי ולא ויזואלי:
     *
     *   purge: false (ברירת מחדל) — הדייר יורד מהמסכים, אבל התשלומים,
     *     החשבונות והקריאות שלו נשארים. דוחות ההכנסה של השנים הקודמות
     *     ממשיכים להסתדר, ואפשר לשחזר את הרשומה.
     *
     *   purge: true — הכול נמחק: התקופה, התשלומים והחשבונות של החודשים
     *     שהיו שלו, הקריאות שנרשמו בתקופתו, החוזים והערבים. פעולה זו
     *     משנה למפרע את סיכומי ההכנסות, ולכן היא מפורשת ולא ברירת מחדל.
     *
     * הקבצים עצמם (צילומי מונה, ת"ז, חתימות) אינם נמחקים כאן — הם יורדים
     * בניקוי היתומים (POST /files/prune) אחרי שאיש כבר לא מפנה אליהם.
     */
    case "remove_tenancy": {
      if (!apt) return need();

      /* הרשימה הגולמית ולא tenanciesOf: זו מסננת תקופות שנמחקו, ובלי
         זה אי אפשר להמשיך ממחיקה ששמרה היסטוריה למחיקה לצמיתות. */
      const all = (A.tenancies || []).filter((t) => t.aptId === apt.id);
      const live = all.filter((t) => !t.deleted);
      const target = act.tenancyId
        ? all.find((t) => t.id === act.tenancyId)
        : (live.filter((t) => !t.closed && t.tenant)[0] || live[live.length - 1]);
      if (!target) return { data: d, log: "לא נמצאה תקופת שכירות למחיקה", fail: true };

      const purge = act.purge === true;
      const notes = String(act.notes || "").trim();
      const who = target.tenant || "דייר ללא שם";

      if (!purge) {
        A.tenancies = A.tenancies.map((t) => (t.id === target.id ? {
          ...t, deleted: true, deletedAt: TODAY, deleteNotes: notes,
          closed: true, closedAt: t.closedAt || TODAY,
        } : t));
        return {
          data: A,
          log: `${who} נמחק/ה מ${apt.name}; ההיסטוריה הכספית נשמרה${notes ? ` — ${notes}` : ""}`,
          focus: apt.id,
        };
      }

      /* מחיקה מלאה. גבול התקופה נקבע לפי התקופה הבאה ולא לפי leaseEnd
         בלבד: דייר שעזב באיחור משאיר קריאות אחרי תאריך הסיום הרשום,
         והן שלו. */
      const later = all
        .filter((t) => t.id !== target.id && t.leaseStart > target.leaseStart)
        .map((t) => t.leaseStart)
        .sort()[0] || null;

      const from = target.leaseStart || "0000-00-00";
      const until = later || target.leaseEnd || target.closedAt || "9999-99-99";
      const inSpan = (date) => date >= from && (later ? date < later : date <= until);

      // חודשים ששייכים לתקופה הזו בלבד — כדי לא לגעת בשכן שחולק חודש.
      let removedMonths = 0;
      for (const key of Object.keys(A.payments)) {
        const [aptId, mk_] = String(key).split("|");
        if (aptId !== apt.id) continue;
        if (tenancyForMonth(A, apt.id, mk_)?.id === target.id) { delete A.payments[key]; removedMonths++; }
      }
      for (const key of Object.keys(A.bills)) {
        const [aptId, mk_] = String(key).split("|");
        if (aptId !== apt.id) continue;
        if (tenancyForMonth(A, apt.id, mk_)?.id === target.id) delete A.bills[key];
      }

      const beforeReadings = (A.readings || []).length;
      A.readings = (A.readings || []).filter((r) => !(r.aptId === apt.id && inSpan(r.date)));
      const removedReadings = beforeReadings - A.readings.length;

      A.media = (A.media || []).filter((m) => !(m.aptId === apt.id && inSpan(m.date || from)));
      A.tenancies = A.tenancies.filter((t) => t.id !== target.id);

      return {
        data: A,
        log: `${who} נמחק/ה לצמיתות מ${apt.name} — ${removedMonths} חודשי גבייה ו-${removedReadings} קריאות${notes ? `. ${notes}` : ""}`,
        focus: apt.id,
      };
    }

    case "restore_tenancy": {
      if (!apt) return need();
      // tenanciesOf מסננת בדיוק את מה שמחפשים כאן, ולכן הרשימה הגולמית.
      const target = (A.tenancies || [])
        .find((t) => t.aptId === apt.id && t.id === act.tenancyId && t.deleted);
      if (!target) return { data: d, log: "לא נמצאה תקופה מחוקה לשחזור", fail: true };
      A.tenancies = A.tenancies.map((t) => (t.id === target.id
        ? { ...t, deleted: false, deletedAt: null, deleteNotes: "" }
        : t));
      return { data: A, log: `${target.tenant} שוחזר/ה ב${apt.name}`, focus: apt.id };
    }

    case "reopen_tenancy": {
      if (!apt) return need();
      const target = tenanciesOf(A, apt.id).find((t) => t.id === act.tenancyId);
      if (!target) return { data: d, log: "לא נמצאה תקופה", fail: true };
      A.tenancies = A.tenancies.map((t) => (t.id === target.id ? {
        ...t, closed: false, closedAt: null,
        // הסגירה קיצרה את leaseEnd לתאריך היציאה; פתיחה מחדש מחזירה את תאריך החוזה.
        leaseEnd: t.contractEnd || t.leaseEnd,
        debtResolution: undefined,
      } : t));
      return { data: A, log: `תקופת השכירות של ${target.tenant} נפתחה מחדש`, focus: apt.id };
    }
    case "set_rent_paid": {
      if (!apt) return need();
      const k = rowKey(apt.id, month);
      const primaryId = tenancyForMonth(A, apt.id, month)?.id;
      const isPrimary = !act.tenancyId || act.tenancyId === primaryId;
      let methodUsed = null;
      if (isPrimary) {
        if (act.paid === false) {
          /* אם קיימים entries של תקופה משנית — הם לא נמחקים, רק השדות
             הרגילים (הראשיים) מתאפסים. מחיקת המפתח כולו הייתה מוחקת גם
             את התשלום של הדייר הנכנס. */
          const rest = A.payments[k]?.entries;
          if (rest && rest.length) A.payments[k] = { entries: rest };
          else delete A.payments[k];
        } else {
          methodUsed = payMethodOf(act.method, A.settings, "rent");
          A.payments[k] = { ...A.payments[k], amount: Number(act.amount) || apt.rent, date: act.date || TODAY, method: methodUsed };
        }
      } else {
        const rec = A.payments[k] || {};
        const entries = (rec.entries || []).filter((e) => e.tenancyId !== act.tenancyId);
        if (act.paid !== false) {
          methodUsed = payMethodOf(act.method, A.settings, "rent");
          entries.push({ tenancyId: act.tenancyId, amount: Number(act.amount) || apt.rent, date: act.date || TODAY, method: methodUsed });
        }
        /* לא נשאר כלום — לא entries ולא תשלום ראשי: מוחקים את המפתח
           לגמרי. רשומה ריקה ({entries: []}) היא אובייקט truthy, וכל
           מי שבודק קיום תשלום ב-!!payments[key] היה רואה "שולם". */
        if (!entries.length && rec.amount == null) delete A.payments[k];
        else A.payments[k] = { ...rec, entries };
      }
      return {
        data: A,
        log: act.paid === false
          ? `${apt.name} — שכ״ד ${monthLabel(month)} בוטל`
          : `${apt.name} — שכ״ד ${monthLabel(month)} סומן כשולם (${methodLabel(methodUsed)})`,
      };
    }
    /**
     * "עריכת חשבונות עבר" — דייר שכבר גר בנכס תקופה ורק עכשיו נרשם,
     * ושכר הדירה בפועל היה שונה בתקופות שונות בעבר (לא רק מה שהוא היום).
     *
     * בניגוד ל-set_rent_paid: זו לא רק "מה התקבל" אלא גם "מה היה מחויב" —
     * מוסיפה מדרגת שכר דירה מתאריך from ואילך (בדיוק כמו שינוי שכר דירה
     * רגיל, ראו update_apartment למעלה), ומסמנת כל חודש בטווח כשולם
     * באותו סכום. בלי המדרגה, effectiveRent לחודשי הטווח היה ממשיך
     * להחזיר את שכר הדירה של היום, וכרטיס החוב היה מציג חוב שמעולם
     * לא נוצר — בדיוק התקלה שגילינו בנתונים אמיתיים.
     */
    case "set_rent_history": {
      if (!apt) return need();
      const t = A.tenancies.find((x) => x.id === act.tenancyId);
      if (!t) return { data: d, log: "התקופה לא נמצאה", fail: true };
      const from = String(act.from || "");
      const to = String(act.to || from);
      const rent = Number(act.rent);
      if (!from || !(rent > 0) || from > to) return { data: d, log: "טווח או סכום לא תקינים", fail: true };

      /* מדרגה בתאריך תחילת הטווח, ומדרגת "שחזור" מיד אחרי סוף הטווח —
         בלעדיה, בלי מדרגה קיימת שכבר ממתינה שם, הסכום החדש היה ממשיך
         לחול לנצח קדימה (rentAt לוקחת את המדרגה האחרונה שחלה, וזו
         הייתה נשארת האחרונה). rentAt על t המקורי (לפני העדכון) מחשבת
         מה היה חל שם באמת, כדי לשחזר בדיוק את זה. */
      const stepFrom = `${from}-01`;
      const afterKey = nextMonth(to);
      const afterDate = `${afterKey}-01`;
      const prevSteps = t.rentSteps || [];
      const restoreRent = rentAt(t, `${afterKey}-28`);
      const rentSteps = [
        ...prevSteps.filter((x) => x.from !== stepFrom && x.from !== afterDate),
        { from: stepFrom, rent },
        { from: afterDate, rent: restoreRent },
      ].sort((x, y) => (x.from < y.from ? -1 : 1));
      A.tenancies = A.tenancies.map((x) => (x.id === t.id ? { ...x, rentSteps } : x));

      let mk_ = from;
      for (let guard = 0; guard < 240 && mk_ <= to; guard++, mk_ = nextMonth(mk_)) {
        const k = rowKey(apt.id, mk_);
        const primaryId = tenancyForMonth(A, apt.id, mk_)?.id;
        const methodUsed = payMethodOf(act.method, A.settings, "rent");
        if (act.tenancyId === primaryId) {
          A.payments[k] = { ...A.payments[k], amount: rent, date: act.date || TODAY, method: methodUsed };
        } else {
          const rec = A.payments[k] || {};
          const entries = (rec.entries || []).filter((e) => e.tenancyId !== act.tenancyId);
          entries.push({ tenancyId: act.tenancyId, amount: rent, date: act.date || TODAY, method: methodUsed });
          A.payments[k] = { ...rec, entries };
        }
      }

      return {
        data: A,
        log: `${apt.name} — שכר דירה ${nis(rent)} ל${monthLabel(from)}${to !== from ? `–${monthLabel(to)}` : ""} (${t.tenant})`,
      };
    }
    case "set_meter": {
      if (!apt) return need();
      const meter = meterOf(act.meter);
      const date = act.date || (month === CUR_KEY ? TODAY : `${month}-15`);
      A.readings = [...(A.readings || []), { id: uid(), aptId: apt.id, meter, value: Number(act.reading), date, photoId: act.photoId || null, note: act.notes ?? act.note ?? "", tenancyId: (tenancyAt(A, apt.id, date) || {}).id || null }]
        .sort((x, y) => (x.date < y.date ? -1 : 1));
      return { data: A, log: `${apt.name} — קריאת ${METER_LABEL[meter]} ב-${heDate(date)}: ${act.reading}` };
    }
    /* דיווח העברה בנקאית מהדייר, עם תמונת אסמכתא. לא נוגע ב-payments/bills
       בכוונה — זו טענה של הדייר, לא אישור: בעל הנכס רואה את הסכום
       והתמונה (בתיבת ההודעות ובכרטיס החוב) ומסמן בעצמו ששולם, בדיוק כמו
       שקריאת מונה לא יוצרת חיוב לבד אלא רק מציגה למה שהוא צריך לאשר. */
    case "report_payment": {
      if (!apt) return need();
      const date = act.date || TODAY;
      const category = (act.cat ?? act.category) === "bills" ? "bills" : "rent";
      const method = ["transfer", "bit", "paybox"].includes(act.method) ? act.method : "transfer";
      const rec = {
        id: uid(), aptId: apt.id, tenancyId: (tenancyAt(A, apt.id, date) || {}).id || null,
        category, method,
        amount: Number(act.amount) || 0, date, note: act.notes ?? act.note ?? "", photoId: act.photoId || null,
        createdAt: new Date().toISOString(),
      };
      A.paymentReports = [...(A.paymentReports || []), rec];
      return {
        data: A,
        log: `${apt.name} — דווח תשלום ${category === "rent" ? "שכר דירה" : "חשבונות"} (${methodLabel(method)}) ${nis(rec.amount)}`,
        focus: apt.id,
      };
    }
    case "fix_meter": {
      if (!apt) return need();
      const meter = meterOf(act.meter);
      const list = (A.readings || []).filter((r) => r.aptId === apt.id && r.meter === meter).sort((x, y) => (x.date < y.date ? -1 : 1));
      const target = act.date ? list.find((r) => r.date === act.date) : list[list.length - 1];
      if (!target) return { data: d, log: "לא נמצאה קריאה לתיקון", fail: true };
      A.readings = A.readings.map((r) => (r.id === target.id ? { ...r, value: Number(act.reading ?? r.value), date: act.newDate || r.date } : r));
      return { data: A, log: `${apt.name} — הקריאה מ-${heDate(target.date)} תוקנה ל-${act.reading}` };
    }
    case "set_bill": {
      if (!apt) return need();
      if (!BILLS.some((x) => x.k === act.bill)) return { data: d, log: `סוג חשבון לא מוכר: ${act.bill}`, fail: true };
      const k = rowKey(apt.id, month);
      const b = { ...(A.bills[k] || emptyBill()) };
      const cur = b[act.bill];
      const primaryId = tenancyForMonth(A, apt.id, month)?.id;
      const isPrimary = !act.tenancyId || act.tenancyId === primaryId;
      const target = isPrimary ? cur : ((cur.entries || []).find((e) => e.tenancyId === act.tenancyId) || emptyItem());

      const wasPaid = !!target.paid;
      const nowPaid = act.paid !== undefined ? !!act.paid : wasPaid;
      const amount = act.amount !== undefined ? Number(act.amount) : Number(target.amount || 0);

      /* אותה לוגיקה בדיוק כמו לפני השינוי (paidAmount נגזר מ-paid, לא
         נשמר כמו שהוא; שינוי סכום בלבד לא נוגע במה ששולם) — רק מופעלת
         על target (הפריט הראשי או הפלח של התקופה המשנית) במקום על cur. */
      const touchesPayment = act.paid !== undefined || act.paidAmount !== undefined;
      const paidAmount = act.paidAmount !== undefined
        ? Number(act.paidAmount) || 0
        : act.paid !== undefined
          ? (nowPaid ? amount : 0)
          : Number(target.paidAmount || 0);

      const updated = {
        ...(isPrimary ? target : { tenancyId: act.tenancyId }),
        amount,
        paidAmount,
        paid: touchesPayment ? (nowPaid || (amount > 0 && paidAmount >= amount)) : (amount > 0 && paidAmount >= amount),
        method: paidAmount > 0 ? payMethodOf(act.method, A.settings, act.bill, target.method, apt) : null,
      };

      if (isPrimary) {
        /* paidThrough הוא חותמת ברמת המונה/הנכס (התקדמות קריאות), לא לכל
           תקופה — נשאר רק על הפריט הראשי, בדיוק כמו היום. */
        b[act.bill] = touchesPayment ? withPaidThrough(A, apt, month, act.bill, updated) : updated;
      } else {
        const entries = (cur.entries || []).filter((e) => e.tenancyId !== act.tenancyId);
        entries.push(updated);
        b[act.bill] = { ...cur, entries };
      }
      A.bills[k] = b;
      return { data: A, log: `${apt.name} — ${BILLS.find((x) => x.k === act.bill).label} ${monthLabel(month)}: ${nis(amount)}${updated.paid ? " (שולם)" : ""}` };
    }

    /**
     * "חשבונות הצריכה התקבלו" — מקבילה של set_rent_paid לכל הסעיפים יחד.
     *
     * בלי זה סימון קבלת חשבונות דורש מעבר על חמישה שדות נפרדים בכל חודש,
     * בזמן ששכר הדירה נסגר בלחיצה אחת. ברירת המחדל היא לסגור את כל
     * החודשים הפתוחים ולא רק את הנוכחי: חוב על חשבונות נגרר בין חודשים,
     * ו"התקבל" שמשאיר מאחור חודש פתוח הוא בדיוק מה שמסתיר חוב.
     */
    case "set_bills_paid": {
      if (!apt) return need();
      const paid = act.paid !== false;

      // חודש מפורש = רק הוא. אחרת כל מה שפתוח (או כל מה ששולם, בביטול).
      const months = act.month
        ? [act.month]
        : Object.keys(A.bills)
          .filter((key) => String(key).split("|")[0] === apt.id)
          .filter((key) => (paid ? billsDue(A.bills[key]) > 0 : billsPaidTotal(A.bills[key]) > 0))
          .map((key) => String(key).split("|")[1]);

      if (!months.length) {
        return { data: d, log: `${apt.name} — אין חשבונות ${paid ? "פתוחים" : "מסומנים"}`, fail: true };
      }

      let touched = 0;
      for (const mk_ of months) {
        const key = rowKey(apt.id, mk_);
        const b = { ...(A.bills[key] || emptyBill()) };
        /* סעיף קבוע (ועד בית, ארנונה, היטל) שמעולם לא "נקבע" ידנית לחודש
           הזה מגיע לכאן עם amount=0, למרות שהוא מוצג כחוב ב-debtStatement
           (אותו חישוב בדיוק, סביב שורה 780 למעלה) — בלעדי הנפילה חזרה הזו
           "סימון כשולם" לא עושה כלום על חודש ותיק שאף אחד לא נגע בו. */
        const ten = tenancyForMonth(A, apt.id, mk_);
        const scope = ten ? { ...apt, ...ten, id: apt.id } : apt;
        const [y, m] = mk_.split("-").map(Number);
        const lived = activeIn(scope, y, m);
        for (const { k } of BILLS) {
          let amount = Number(b[k]?.amount || 0);
          if (!amount && paid && lived) {
            const fixed = FIXED_BILLS.find((x) => x.k === k);
            if (fixed && scope[fixed.activeField] !== false) {
              amount = effectiveFixedBill(scope, mk_, fixed.amountField);
            }
          }
          if (!amount) continue;
          b[k] = withPaidThrough(A, apt, mk_, k, {
            ...b[k], amount, paid, paidAmount: paid ? amount : 0,
            method: paid ? payMethodOf(act.method, A.settings, k, b[k].method, apt) : null,
          });
          touched++;
        }
        A.bills[key] = b;
      }

      const label = months.length === 1 ? monthLabel(months[0]) : `${months.length} חודשים`;
      return {
        data: A,
        log: `${apt.name} — חשבונות ${label} ${paid ? "סומנו כהתקבלו" : "בוטל סימון התשלום"}`,
        ...(touched ? {} : { fail: true }),
      };
    }
    case "add_expense":
      A.expenses = [{ id: uid(), aptId: apt?.id || "", month, cat: act.cat || "אחר", amount: Number(act.amount) || 0, desc: act.desc || "" }, ...A.expenses];
      return { data: A, log: `נרשמה הוצאה ${nis(act.amount)} — ${act.cat || "אחר"}` };
    /* ---- הוצאות קבועות (ועד בית, ארנונה, הלוואה מוגבלת בזמן...) ---- */
    case "set_recurring_expense": {
      const list = [...(d.recurringExpenses || [])];
      const idx = act.id ? list.findIndex((r) => r.id === act.id) : -1;
      const old = idx >= 0 ? list[idx] : null;
      const aptIds = Array.isArray(act.aptIds)
        ? act.aptIds.filter((id) => findApt(A.apartments, id))
        : old?.aptIds || [];
      const next = {
        id: old?.id || uid(),
        aptIds,
        cat: act.cat !== undefined ? (act.cat || "אחר") : old?.cat || "אחר",
        amount: act.amount !== undefined ? Number(act.amount) || 0 : old?.amount || 0,
        desc: act.desc !== undefined ? String(act.desc || "") : old?.desc || "",
        startMonth: act.startMonth !== undefined ? String(act.startMonth || CUR_KEY) : old?.startMonth || CUR_KEY,
        endMonth: act.endMonth !== undefined ? (act.endMonth || null) : old?.endMonth ?? null,
      };
      if (idx >= 0) list[idx] = next; else list.push(next);
      A.recurringExpenses = list;
      materializeRecurringExpenses(A);
      return {
        data: A,
        log: `${idx >= 0 ? "עודכנה" : "נוספה"} הוצאה קבועה: ${next.cat}${next.amount ? ` · ${nis(next.amount)}` : ""}`,
        recurringId: next.id,
      };
    }
    case "remove_recurring_expense": {
      const list = d.recurringExpenses || [];
      const gone = list.find((r) => r.id === act.id);
      if (!gone) return { data: d, log: "ההוצאה הקבועה לא נמצאה", fail: true };
      A.recurringExpenses = list.filter((r) => r !== gone);
      return { data: A, log: `נמחקה הוצאה קבועה: ${gone.cat}` };
    }
    case "set_tariff": {
      const map = { elec: "elecRate", water: "waterSewageRate", sewage: "waterSewageRate", gas: "gasFixed", index: "indexPct", late: "lateFeePct", deposit: "depositMonths", bills: "billsDay", cycle: "billsCycle" };
      const f = map[act.tariff] || act.tariff;
      if (!(f in defTariffs())) return { data: d, log: `תעריף לא מוכר: ${act.tariff}`, fail: true };
      const oldVal = A.settings[f];

      /* לא כל הגדרה היא מספר. אמצעי התשלום הם מחרוזות, ו-Number עליהן
         מחזיר NaN — ההגדרה נשמרה כ-NaN, defaultMethod לא זיהה אותה
         וחזר לברירת המחדל. כלומר הבחירה במסך פשוט לא נתפסה, בלי שגיאה.
         הטיפוס נקבע לפי ברירת המחדל של אותו שדה. */
      const isText = typeof defTariffs()[f] === "string";
      const val = isText ? String(act.value ?? "") : Number(act.value);
      if (!isText && Number.isNaN(val)) {
        return { data: d, log: `ערך לא מספרי ל-${f}: ${act.value}`, fail: true };
      }
      A.settings = { ...A.settings, [f]: val };
      // מועדי תשלום ומחזור חיוב הם ברירת מחדל של נכס — מחילים גם על נכסים
      // שלא שינית להם ידנית (כלומר עדיין מחזיקים את הערך הישן)
      const perApt = { paymentDay: "rentDay", billsDay: "billsDay", billsCycle: "billsCycle" }[f];
      let touched = 0;
      if (perApt) {
        const same = (v) => v === undefined || v === null || v === "" || Number(v) === Number(oldVal);
        /* השדה חי על תקופת השכירות (TEN_FIELDS), והנכס רק משקף אותה —
           כתיבה לנכס בלבד נמחקה ב-syncApartments בפעולה הבאה, ו"הוחל על 5
           נכסים" החזיק מעמד עד הלחיצה הבאה. לכן גם התקופה שאליה כותבים
           (הפעילה, או הקרובה) מתעדכנת, לפי אותו תנאי. */
        const touchedIds = new Set();
        A.apartments = A.apartments.map((a) => {
          if (same(a[perApt])) { touched++; touchedIds.add(a.id); return { ...a, [perApt]: val }; }
          return a;
        });
        const targets = new Set([...touchedIds].map((id) => targetTenancy(A, id)?.id).filter(Boolean));
        A.tenancies = (A.tenancies || []).map((t) => (targets.has(t.id) && same(t[perApt]) ? { ...t, [perApt]: val } : t));
      }
      return { data: A, log: `${f} עודכן ל-${val}${touched ? ` (הוחל על ${touched} נכסים)` : ""}` };
    }
    case "new_contract": {
      if (!apt) return need();
      const from = act.from || apt.leaseStart || TODAY;
      const to = act.to || apt.leaseEnd || addYears(from, 1);
      /* חוזה מותאם: customText = נוסח חופשי שנכתב או נערך בפלטפורמה;
         fileId = קובץ חוזה פרטי שהועלה (PDF או תמונה). שניהם רשומות חוזה
         רגילות — שליחה לחתימה, חתימת הדייר ומחיקה עובדות עליהן כרגיל. */
      const custom = act.customText != null && String(act.customText).trim() ? String(act.customText) : null;
      const c = {
        id: uid(), type: "original", from, to, createdAt: TODAY, rent: act.rent !== undefined ? Number(act.rent) : apt.rent,
        // c.deposit הוא סך הביטחונות, לתצוגה בכרטיס החוזה. הפירוט עצמו
        // נגזר מ-apt.securities בזמן יצירת נוסח החוזה.
        deposit: act.deposit !== undefined ? Number(act.deposit) : securityTotal(apt, A.settings),
        paymentDay: rentDayOf(apt, A.settings), status: "draft", signedAt: null, signatureId: null, scanId: null,
        notes: act.notes || "",
        ...(custom ? { customText: custom } : {}),
        ...(act.fileId ? {
          fileId: String(act.fileId),
          fileName: act.fileName ? String(act.fileName) : "",
          fileMime: act.fileMime ? String(act.fileMime) : "",
        } : {}),
      };
      const tid2 = act.tenancyId || targetTenancy(A, apt.id)?.id;
      if (tid2) A.tenancies = A.tenancies.map((t) => (t.id === tid2 ? { ...t, contracts: [...(t.contracts || []), c] } : t));
      A.apartments = A.apartments.map((x) => x.id === apt.id ? { ...x, contracts: [...(x.contracts || []), c] } : x);
      const flavor = act.fileId ? "קובץ חוזה" : custom ? "חוזה בנוסח אישי" : "חוזה";
      return { data: A, log: `נוצר ${flavor} ל${apt.name} לתקופה ${heDate(from)}–${heDate(to)}`, focus: apt.id };
    }
    /**
     * עריכת נוסח החוזה בפלטפורמה — שמירת נוסח מותאם על חוזה קיים.
     * טקסט ריק (או null) מוחק את הנוסח המותאם וחוזר לתבנית של המערכת.
     * חוזה חתום אינו ניתן לעריכה: מה שנחתם הוא המסמך.
     */
    case "set_contract_text": {
      if (!apt) return need();
      const cid4 = act.contractId || act.contract;
      const all4 = [
        ...(apt.contracts || []),
        ...(A.tenancies || []).filter((t) => t.aptId === apt.id).flatMap((t) => t.contracts || []),
      ];
      const hit4 = all4.find((x) => x.id === cid4);
      if (!hit4) return { data: d, log: `לא נמצא חוזה לעריכה ב${apt.name}`, fail: true };
      if (hit4.status === "signed") {
        return { data: d, log: `החוזה ב${apt.name} חתום — הנוסח שנחתם אינו ניתן לעריכה`, fail: true };
      }
      const text4 = act.text != null && String(act.text).trim() ? String(act.text) : null;
      const patch4 = (list) => (list || []).map((x) => (x.id === cid4 ? { ...x, customText: text4 } : x));
      A.tenancies = (A.tenancies || []).map((t) => (t.aptId === apt.id ? { ...t, contracts: patch4(t.contracts) } : t));
      A.apartments = A.apartments.map((x) => (x.id === apt.id ? { ...x, contracts: patch4(x.contracts) } : x));
      return {
        data: A,
        log: text4 ? `נשמר נוסח מותאם לחוזה ב${apt.name}` : `החוזה ב${apt.name} חזר לנוסח המערכת`,
        focus: apt.id,
      };
    }
    /**
     * מחיקת חוזה — לרוב טיוטה שנוצרה בלחיצה מיותרת על "חוזה חדש".
     * מוסר גם מהתקופה (מקור האמת) וגם מהנכס, אחרת syncApartments היה מחזיר
     * אותו בפעולה הבאה. חוזה חתום נמחק רק עם force — המסך שואל לפני.
     */
    case "remove_contract": {
      if (!apt) return need();
      const cid = act.contractId || act.contract;
      const hit = (apt.contracts || []).find((c) => c.id === cid);
      if (!hit) return { data: d, log: `לא נמצא חוזה למחיקה ב${apt.name}`, fail: true };
      if (hit.status === "signed" && !act.force) {
        return { data: d, log: `החוזה ב${apt.name} חתום — מחיקה דורשת אישור מפורש`, fail: true };
      }
      const drop = (list) => (list || []).filter((c) => c.id !== cid);
      A.tenancies = (A.tenancies || []).map((t) => (t.apartmentId === apt.id || t.aptId === apt.id
        ? { ...t, contracts: drop(t.contracts) } : t));
      A.apartments = A.apartments.map((x) => (x.id === apt.id ? { ...x, contracts: drop(x.contracts) } : x));
      return { data: A, log: `נמחק ${hit.type === "extension" ? "נספח הארכה" : "חוזה"} ב${apt.name} (${heDate(hit.from)}–${heDate(hit.to)})`, focus: apt.id };
    }
    case "extend_contract": {
      if (!apt) return need();
      const last = (apt.contracts || []).slice(-1)[0];
      /* בקשת הארכה של הדייר שהנספח הזה עונה עליה. תאריך הסיום שביקש
         הוא ברירת המחדל, ובעל הנכס יכול לשנות אותו בחלון לפני ההפקה. */
      const tid3 = act.tenancyId || targetTenancy(A, apt.id)?.id;
      const reqList = (tid3 ? (A.tenancies || []).find((t) => t.id === tid3)?.extensionRequests : apt.extensionRequests) || [];
      const req = act.requestId ? reqList.find((r) => r.id === act.requestId) : null;
      if (act.requestId && !req) return { data: d, log: `לא נמצאה בקשת הארכה ב${apt.name}`, fail: true };
      const from = act.from || (apt.leaseEnd ? dayAfter(apt.leaseEnd) : TODAY);
      const to = act.to || req?.to || addYears(from, 1);
      const rent = act.rent !== undefined ? Number(act.rent) : apt.rent;
      const c = { id: uid(), type: "extension", from, to, createdAt: TODAY, rent, prevRent: Number(apt.rent) || 0, deposit: last?.deposit || securityTotal({ ...apt, rent }, A.settings), paymentDay: last?.paymentDay || 1, status: "draft", signedAt: null, signatureId: null, scanId: null, notes: act.notes || "", ...(req ? { requestId: req.id } : {}) };
      A.apartments = A.apartments.map((a) => a.id === apt.id ? { ...a, contracts: [...(a.contracts || []), c], leaseEnd: to, rent } : a);
      /* גם לתקופה עצמה. contracts הוא TEN_FIELD, ולכן כתיבה לנכס בלבד
         נמחקת ב-syncApartments בטעינה הבאה — ההארכה פשוט נעלמת. */
      if (tid3) {
        A.tenancies = A.tenancies.map((t) => (t.id === tid3
          ? { ...t, contracts: [...(t.contracts || []), c], leaseEnd: to, rent }
          : t));
      }
      /* הבקשה מסומנת כמאושרת ומצביעה על הנספח — כך הדייר רואה באזור
         האישי "אושר, הנספח ממתין לחתימתך" ולא "ממתין" לנצח. */
      if (req) {
        writeExtensionRequests(A, apt.id, tid3, reqList.map((r) => (r.id === req.id
          ? { ...r, status: "approved", decidedAt: TODAY, contractId: c.id, to }
          : r)));
      }
      return { data: A, log: `נוצרה הארכת חוזה ל${apt.name} עד ${heDate(to)}${req ? " (בקשת הדייר אושרה)" : ""}`, contractId: c.id };
    }
    /**
     * בקשת הארכה מהדייר. נשלחת מהאזור האישי (השרת מריץ את הפעולה הזו
     * בדיוק), ומהתצוגה המקדימה של בעל הנכס. תאריך הסיום המבוקש חייב
     * להיות אחרי סיום החוזה הנוכחי, ויש לכל היותר בקשה אחת ממתינה.
     */
    case "request_extension": {
      if (!apt) return need();
      const ten = targetTenancy(A, apt.id);
      const holder = ten || apt;
      if (!holder.tenant) return { data: d, log: `אין דייר ב${apt.name} — אין חוזה להאריך`, fail: true };
      if (!holder.leaseEnd) return { data: d, log: `לחוזה של ${holder.tenant} אין מועד סיום — אין מה להאריך`, fail: true };
      const list = holder.extensionRequests || [];
      if (list.some((r) => r.status === "pending")) {
        return { data: d, log: `כבר יש בקשת הארכה ממתינה ל${apt.name}`, fail: true, code: "already_pending" };
      }
      const to = String(act.to || "");
      if (!/^\d{4}-\d{2}-\d{2}$/.test(to) || Number.isNaN(new Date(to).getTime())) {
        return { data: d, log: "תאריך סיום מבוקש לא תקין", fail: true, code: "bad_date" };
      }
      if (to <= holder.leaseEnd) {
        return { data: d, log: `תאריך הסיום המבוקש (${heDate(to)}) חייב להיות אחרי סיום החוזה הנוכחי (${heDate(holder.leaseEnd)})`, fail: true, code: "bad_date" };
      }
      const req = {
        id: uid(), createdAt: TODAY, status: "pending",
        from: dayAfter(holder.leaseEnd), to,
        // normalizeAction ממפה note → notes; שני השמות מתקבלים.
        note: String(act.notes ?? act.note ?? "").slice(0, 500),
        by: act.by === "owner" ? "owner" : "tenant",
        decidedAt: null, reason: "", contractId: null,
      };
      writeExtensionRequests(A, apt.id, ten?.id, [...list, req]);
      return {
        data: A,
        log: `${holder.tenant} ביקש/ה להאריך את החוזה ב${apt.name} עד ${heDate(to)}`,
        focus: apt.id, requestId: req.id,
      };
    }
    /** הדייר חוזר בו מבקשה שעדיין ממתינה. */
    case "withdraw_extension_request": {
      if (!apt) return need();
      const ten = targetTenancy(A, apt.id);
      const list = (ten || apt).extensionRequests || [];
      const r = list.find((x) => x.id === act.requestId);
      if (!r) return { data: d, log: `לא נמצאה בקשת הארכה ב${apt.name}`, fail: true, code: "not_found" };
      if (r.status !== "pending") return { data: d, log: `הבקשה כבר ${EXTENSION_STATUS[r.status] || "טופלה"}`, fail: true, code: "not_pending" };
      writeExtensionRequests(A, apt.id, ten?.id, list.map((x) => (x.id === r.id ? { ...x, status: "withdrawn", decidedAt: TODAY } : x)));
      return { data: A, log: `בקשת ההארכה של ${(ten || apt).tenant} ב${apt.name} בוטלה`, focus: apt.id };
    }
    /** בעל הנכס דוחה את הבקשה, עם סיבה שהדייר רואה. */
    case "decline_extension_request": {
      if (!apt) return need();
      const ten = targetTenancy(A, apt.id);
      const list = (ten || apt).extensionRequests || [];
      const r = list.find((x) => x.id === act.requestId);
      if (!r) return { data: d, log: `לא נמצאה בקשת הארכה ב${apt.name}`, fail: true, code: "not_found" };
      if (r.status !== "pending") return { data: d, log: `הבקשה כבר ${EXTENSION_STATUS[r.status] || "טופלה"}`, fail: true, code: "not_pending" };
      const reason = String(act.reason || "").slice(0, 500);
      writeExtensionRequests(A, apt.id, ten?.id, list.map((x) => (x.id === r.id ? { ...x, status: "declined", decidedAt: TODAY, reason } : x)));
      return { data: A, log: `בקשת ההארכה של ${(ten || apt).tenant} ב${apt.name} נדחתה${reason ? ` (${reason})` : ""}`, focus: apt.id };
    }
    /* ---- שווי נכס ---- */
    case "set_property_value": {
      if (!apt) return need();
      const amount = Number(act.amount) || 0;
      if (!amount) return { data: d, log: "חסר סכום השווי", fail: true };
      const date = act.date && /^\d{4}-\d{2}-\d{2}$/.test(act.date) ? act.date : TODAY;
      // רשומה קיימת לאותו תאריך מוחלפת — שני שוויים לאותו יום הם תיקון, לא היסטוריה.
      const rest = (apt.values || []).filter((v) => v.date !== date);
      A.apartments = A.apartments.map((a) => (a.id !== apt.id ? a : {
        ...a, values: [...rest, { id: uid(), date, amount, notes: act.notes || "" }],
      }));
      return { data: A, log: `שווי ${apt.name} נקבע ל-${nis(amount)} נכון ל-${heDate(date)}` };
    }
    case "remove_property_value": {
      if (!apt) return need();
      const vid = act.valueId || null;
      const before = (apt.values || []).length;
      A.apartments = A.apartments.map((a) => (a.id !== apt.id ? a : {
        ...a,
        values: (a.values || []).filter((v) => (vid ? v.id !== vid : v.date !== act.date)),
      }));
      const after = (A.apartments.find((a) => a.id === apt.id).values || []).length;
      if (after === before) return { data: d, log: "רשומת השווי לא נמצאה", fail: true };
      return { data: A, log: `נמחקה רשומת שווי של ${apt.name}` };
    }

    /* ---- הלוואות ומשכנתאות ---- */
    case "set_loan": {
      /* עדכון לפי loanId, או לפי שם זהה — כדי ש"עדכן את משכנתא הרצל 12"
         מהעוזר לא יפתח הלוואה כפולה. בלי התאמה — נוצרת חדשה. */
      const loans = [...(d.loans || [])];
      const name = String(act.name || "").trim();
      const idx = act.loanId
        ? loans.findIndex((l) => l.id === act.loanId)
        : name ? loans.findIndex((l) => String(l.name || "").trim() === name) : -1;
      const old = idx >= 0 ? loans[idx] : null;
      // שדה שלא נשלח לא משתנה; years נוחות שמתורגמת לחודשים.
      const months = act.months !== undefined ? Math.round(Number(act.months) || 0)
        : act.years !== undefined ? Math.round((Number(act.years) || 0) * 12)
          : old?.months || 0;
      const next = {
        id: old?.id || uid(),
        name: name || old?.name || "הלוואה",
        aptId: act.apartment !== undefined ? (apt?.id || "") : old?.aptId || "",
        lender: act.lender !== undefined ? String(act.lender || "") : old?.lender || "",
        principal: act.principal !== undefined ? Number(act.principal) || 0 : old?.principal || 0,
        rate: act.rate !== undefined ? Number(act.rate) || 0 : old?.rate || 0,
        months,
        start: act.start !== undefined ? String(act.start || "") : old?.start || "",
        rateType: act.rateType !== undefined ? String(act.rateType || "") : old?.rateType || "",
        linked: act.linked !== undefined ? !!act.linked : !!old?.linked,
        balance: act.balance !== undefined ? act.balance : old?.balance ?? null,
        balanceDate: act.balanceDate !== undefined ? act.balanceDate : old?.balanceDate ?? null,
        notes: act.notes !== undefined ? String(act.notes || "") : old?.notes || "",
      };
      if (idx >= 0) loans[idx] = next; else loans.push(next);
      A.loans = loans;
      return {
        data: A,
        log: `${idx >= 0 ? "עודכנה" : "נוספה"} הלוואה: ${next.name}${next.principal ? ` · ${nis(next.principal)}` : ""}${next.rate ? ` · ריבית ${next.rate}%` : ""}`,
        loanId: next.id,
      };
    }
    case "remove_loan": {
      const loans = d.loans || [];
      const name = String(act.name || "").trim();
      const gone = loans.find((l) => (act.loanId ? l.id === act.loanId : name && String(l.name || "").trim() === name));
      if (!gone) return { data: d, log: "ההלוואה לא נמצאה", fail: true };
      A.loans = loans.filter((l) => l !== gone);
      return { data: A, log: `נמחקה ההלוואה ${gone.name}` };
    }

    default:
      return { data: d, log: `פעולה לא מוכרת: ${act.type}`, fail: true };
  }
}

/* מפעיל פעולה עם "נכס בפוקוס": אם ההוראה לא ציינה נכס, או ציינה כינוי
   שלא מזוהה ("אותו דייר", "הוא"), נופלים חזרה לנכס שטופל לאחרונה. */
function applyActionFocused(d, rawAct, focusId) {
  const r = applyActionFocusedInner(d, rawAct, focusId);
  return r.fail ? r : { ...r, data: syncApartments(r.data) };
}

function applyActionFocusedInner(d, rawAct, focusId) {
  const act = normalizeAction(rawAct);
  const a2 = { ...act };
  if (act.type !== "add_apartment" && focusId) {
    if (!a2.apartment || !findApt(d.apartments, a2.apartment)) a2.apartment = focusId;
  }
  const r = applyAction(d, a2);
  if (r.fail || r.focus !== undefined) return r;
  if (act.type === "remove_apartment") return { ...r, focus: null };
  const hit = findApt(d.apartments, a2.apartment);
  return { ...r, focus: hit ? hit.id : focusId || null };
}

/* ---------------- וואטסאפ ---------------- */
const waPhone = (p) => { const d = String(p || "").replace(/\D/g, ""); return d.startsWith("0") ? "972" + d.slice(1) : d; };

/* ============================================================================
   כתב ערבות אישי — הנוסח המלא שהערב קורא בדף הקישור וחותם עליו.

   נפרד מנספח ב' של החוזה: הנספח מרכז את כל הערבים בתוך מסמך החוזה,
   וזה מסמך העומד בפני עצמו לערב אחד — עם פרטי הנכס, השוכר והמשכיר.
   אותה פונקציה רצה בדפדפן (תצוגה חיה בזמן מילוי הפרטים) ובשרת (הנוסח
   הסופי שנשמר על רשומת הערב ברגע החתימה) — כך מה שמוצג הוא בדיוק מה
   שנחתם ונשמר.
   ============================================================================ */
const GUARANTOR_CONSENT_TEXT =
  "אני מאשר/ת שקראתי את הסכם השכירות ואת כתב הערבות, הבנתי את התחייבויותיי כערב/ה, ואני חותם/ת מרצוני החופשי.";

function guaranteeDeedText({ apt, guarantor: g, contract: c, settings: s = {} }) {
  const dash = (v) => (String(v || "").trim() ? String(v).trim() : "________");
  return [
    "כתב ערבות",
    "",
    `אני החתום/ה מטה, ${dash(g?.name)}, ת.ז. ${dash(g?.idNumber)}, מכתובת ${dash(g?.address)}, טלפון ${dash(g?.phone)},`,
    `ערב/ה בזאת כלפי המשכיר ${dash(s.ownerName)}${s.ownerId ? ` ת.ז. ${s.ownerId}` : ""}, יחד ולחוד עם השוכר ובנפרד ממנו,`,
    `לכל התחייבויות השוכר ${dash(apt?.tenant)} על פי הסכם השכירות לנכס ${dash(apt?.name)},`,
    c?.from && c?.to
      ? `לתקופת השכירות שמיום ${heDate(c.from)} ועד ליום ${heDate(c.to)}, ולכל הארכה שלה,`
      : "לתקופת השכירות ולכל הארכה שלה,",
    `לרבות דמי שכירות${Number(c?.rent) > 0 ? ` (${nis(c.rent)} לחודש)` : ""}, תשלומי צריכה וחשבונות, פיצויים, נזקים ופינוי המושכר במועד.`,
    "",
    "ידוע לי כי:",
    "1. הערבות תעמוד בתוקפה עד למילוי מלוא התחייבויות השוכר ולהחזרת המושכר בהתאם להסכם, ולא תפקע עקב שינוי בתנאי ההסכם או הארכת תקופת השכירות.",
    "2. הערבות אינה מותנית במיצוי הליכים נגד השוכר.",
    "3. המשכיר רשאי לפנות אליי לאחר שהשוכר לא תיקן הפרה בתוך 14 ימים מקבלת התראה בכתב.",
    "4. חתימתי האלקטרונית על מסמך זה מחייבת אותי כחתימה בכתב יד.",
  ].join("\n");
}

/* הודעה לדייר. הנוסח נגזר ממצב התשלום:
   receipt — התקבל תשלום (מלא או חלקי)
   reminder — עבר מועד התשלום ויש יתרה
   notice — פירוט לפני מועד התשלום */
/**
 * גוף הפירוט של חודש אחד — הקריאות, החישוב וכל סעיף.
 *
 * הופרד מ-billsText כי statementText צריך בדיוק את החלק הזה בתוך הודעה
 * גדולה יותר. הוא חתך אותו קודם מהמחרוזת המוגמרת, וזה נשבר בשקט בכל
 * שינוי ניסוח.
 */
/* ============================================================================
   ההודעות לדייר (וואטסאפ / הודעה באזור האישי).

   העיקרון: *הסכום לתשלום קודם, ההסבר אחר כך* — ושורות קצרות. ההודעה
   נקראת בטלפון, בעברית, עם מספרים בתוכה: שורה ארוכה נשברת באמצע, וסימנים
   כמו ×, סוגריים ו-₪ צמוד למספר מתהפכים בתצוגה. לכן: כל מספר חשוב בשורה
   משלו, בלי נוסחאות, עם "·" ו-":" בלבד. "איך משלמים" — ולא "לתשלום",
   שהיא אותה מילה כמו הסכום — סוגר את ההודעה.
   ============================================================================ */

/* פירוט הסעיפים. לכל סעיף שורה ראשונה קצרה (שם, סכום, מה שולם) ולמונים
   שורה שנייה עם הצריכה והקריאות. מחזיר גם את הסכומים לשורה הראשונה. */
function billsBody(apt, bill, month, includeRent, settings, d) {
  const s = settings || defTariffs();
  const b = bill || emptyBill();
  const num = (n) => Number(n).toLocaleString("he-IL");
  const L = [];
  let charged = 0, paid = 0;
  let rentCharged = 0, rentPaid = 0, billsCharged = 0, billsPaid = 0, hasVaad = false;
  /* שכר דירה: "נותר" על השורה. חשבונות: "שולם X" בלבד על הסעיף — היתרה
     היא של כל החשבונות יחד (billsDue מקזז בין סעיפים), ומופיעה בשורת
     "סה"כ חשבונות" שאחריהם. */
  /* ההודעה ממוקדת בחוב: סעיף ששולם במלואו לא מופיע בפירוט בכלל; "שולם"
     מופיע רק כשהוא מסביר יתרה (תשלום חלקי). מה שהתקבל נאמר פעם אחת
     בראש אישור התשלום, לא בכל שורה. */
  const paidNote = (p, amt, withRemain = true) => {
    if (!(p > 0)) return "";
    return withRemain ? ` · שולם ${nis(p)} · *נותר ${nis(amt - p)}*` : ` · שולם ${nis(p)}`;
  };
  /* היתרה נספרת לכל סעיף בנפרד (max 0) — כמו debtStatement וכמו כרטיס
     החוב: תשלום יתר על חשמל לא מקזז מים. קודם השורה הראשונה חישבה
     "חויב פחות שולם" נטו, ולכן הציגה יתרה שונה מזו שבפירוט ובאפליקציה. */
  let rentOwed = 0, billsOwed = 0;

  if (includeRent) {
    const rent = Number(effectiveRent(apt, month)) || 0;
    const rp = d ? rentPaidOf(d, apt.id, month) : 0;
    if (rent - rp > 0) L.push(`• שכר דירה: ${nis(rent)}${paidNote(rp, rent)}`);
    charged += rent; paid += rp; rentCharged += rent; rentPaid += rp;
    rentOwed += Math.max(0, rent - rp);
  }

  /* סעיפי החשבונות נאספים קודם, ורק אז נכתבים — כי צורת ההצגה תלויה במצב:
     כשלא שולם כלום — שורה לכל סעיף עם הצריכה והקריאות (כך הדייר רואה
     איך חושב כל סכום); כששולם חלק — שורת קבוצה אחת עם חשבון פשוט
     (חויב · שולם · נותר) ורשימת הסעיפים מתחתיה. תשלום שנרשם על סעיף אחד
     מעל הסכום שלו (חשמל ששולם 130 ואחר כך עודכן ל-65) לא מוצג כ"עודף"
     וכ"נותר" בשני סעיפים שונים — זה מה שגרם לחשבון להיראות לא מובן. */
  const items = [];
  const meterItem = (meter, title, k, rate) => {
    const amt = itemAmount(b[k]);
    const p = itemPaid(b[k]);
    const cons = d ? consumptionForMonth(d, apt, s, meter, month) : null;
    const last = cons ? cons.to : (d ? lastReading(d, apt.id, meter) : null);
    const prev = cons
      ? (cons.diff ? cons.from : null)
      : (last && d ? readingBefore(d, apt.id, meter, last.date, last.id) : null);
    if (!amt && !(last && prev)) return;
    let diff = null;
    if (last && prev) {
      diff = Math.round((last.value - prev.value) * 10) / 10;
      if (!amt && !diff) return;
    }
    const shown = amt || (diff != null ? Math.round(diff * rate) : 0);
    let detail = null;
    if (diff != null && diff > 0) {
      const matches = amt > 0 && Math.abs(diff * rate - amt) <= Math.max(1, amt * 0.02);
      detail = `${num(diff)} ${UNIT[meter]} · קריאה ${num(prev.value)} עד ${num(last.value)}${matches ? ` · ₪${rate} ל${UNIT[meter]}` : ""}`;
    }
    items.push({ label: title, amt, shown, p, detail });
  };
  meterItem("elec", "חשמל", "elec", rateElec(apt, s));
  meterItem("water", "מים וביוב", "water", rateWS(apt, s));
  const flatItem = (k, label) => {
    const amt = itemAmount(b[k]);
    if (!amt) return;
    if (k === "vaad") hasVaad = true;
    items.push({ label, amt, shown: amt, p: itemPaid(b[k]), detail: null });
  };
  flatItem("sewage", "ביוב");
  flatItem("vaad", "ועד בית");
  flatItem("gas", "גז");

  items.forEach((it) => { charged += it.amt; paid += it.p; billsCharged += it.amt; billsPaid += it.p; });
  billsOwed = Math.max(0, billsCharged - billsPaid);

  if (items.length && billsPaid === 0) {
    // עוד לא שולם דבר — כל סעיף בנפרד, עם ההסבר איך חושב.
    items.forEach((it) => {
      L.push(`• ${it.label}: ${nis(it.shown)}`);
      if (it.detail) L.push(`   ${it.detail}`);
    });
  } else if (items.length && billsOwed > 0) {
    if (items.length === 1) {
      const it = items[0];
      L.push(`• ${it.label}: ${nis(it.amt)} · שולם ${nis(it.p)} · *נותר ${nis(billsOwed)}*`);
      if (it.detail) L.push(`   ${it.detail}`);
    } else {
      // חשבון פשוט: חויב, שולם, נותר — והסעיפים שמרכיבים את החיוב.
      L.push(`• חשבונות: ${nis(billsCharged)} · שולם ${nis(billsPaid)} · *נותר ${nis(billsOwed)}*`);
      L.push(`   ${items.map((it) => `${it.label} ${nis(it.amt)}`).join(" · ")}`);
    }
  }
  // שולם במלואו — לא חלק מהחוב, לא מופיע.

  return {
    lines: L, charged, paid, balance: rentOwed + billsOwed,
    rent: { charged: rentCharged, paid: rentPaid, balance: rentOwed },
    bills: {
      charged: billsCharged, paid: billsPaid, balance: billsOwed,
      label: hasVaad ? "חשבונות צריכה וועד בית" : "חשבונות צריכה",
    },
  };
}

/* קו מתחת לסה"כ. בוואטסאפ אין קו תחתון, ולכן שורת קו: הסה"כ הוא השורה
   שמעליה, והיא מפרידה אותו מהפירוט. */
const RULE = "━━━━━━━━━━━━━━━";

/* איך משלמים — מפרטי התשלום שבהגדרות, בשורות קצרות. ריק כשלא הוגדרו. */
function payHow(s) {
  const lines = [
    s.payBank && `בנק ${s.payBank}${s.payBranch ? `, סניף ${s.payBranch}` : ""}${s.payAccount ? `, חשבון ${s.payAccount}` : ""}`,
    s.payBank && s.ownerName && `ע״ש ${s.ownerName}`,
    s.payBit && `ביט / פייבוקס ${s.payBit}`,
  ].filter(Boolean);
  return lines.length ? `\n\nאיך משלמים:\n${lines.join("\n")}` : "";
}

/* ============================================================================
   הודעת החודש לדייר — המבנה הקצר (25.8.2026, לבקשת בעל הנכס):

     שלום {דייר},
     שכר הדירה התקבל, תודה.   ‹או›   טרם התקבל — נא להעביר בהקדם:
     *שכר דירה לתשלום: ₪X*  + קו מתחת
     חשבונות הצריכה:
     חשמל: קריאה קודמת A · נוכחית B · צריכה C קוט״ש × ₪תעריף
     *לתשלום חשמל: ₪X*
     …וכך לכל מונה, סעיף ששולם מקבל שורת "שולם, תודה"…
     *סה"כ לתשלום חשבונות צריכה: ₪X*  + קו מתחת

   בוואטסאפ אין קו תחתון — הקו הוא שורת ━ מתחת לסכום. הנוסחה כתובה
   במילים ("קריאה קודמת… נוכחית…") ולא ב"12160−12550": מספרים משני צצי
   סימן חשבון מתהפכים בתצוגת RTL והחשבון נראה הפוך.
   ============================================================================ */
function billsText(apt, bill, month, includeRent, settings, d, mode) {
  const s = settings || defTariffs();
  const b = bill || emptyBill();
  const num = (n) => Number(n).toLocaleString("he-IL");
  const out = [`שלום ${apt.tenant},`];
  if (mode === "reminder") out.push("תזכורת לתשלום:");
  out.push(`${monthLabel(month)} · ${apt.name}`, "");

  /* ---- שכר הדירה ---- */
  let rentOwed = 0;
  if (includeRent && Number(apt.rent || 0) > 0) {
    const rent = Number(effectiveRent(apt, month)) || 0;
    const rp = d ? rentPaidOf(d, apt.id, month) : 0;
    rentOwed = Math.max(0, rent - rp);
    if (rentOwed <= 0) {
      out.push("שכר הדירה התקבל, תודה.");
    } else if (rp > 0) {
      out.push(`שכר הדירה התקבל חלקית (${nis(rp)} מתוך ${nis(rent)}) — נא להשלים בהקדם:`);
      out.push(`*יתרת שכר דירה: ${nis(rentOwed)}*`, RULE);
    } else {
      out.push("שכר הדירה טרם התקבל — נא להעביר בהקדם:");
      out.push(`*שכר דירה לתשלום: ${nis(rent)}*`, RULE);
    }
    out.push("");
  }

  /* ---- חשבונות הצריכה ---- */
  const lines = [];
  let billsOwed = 0, billsPaid = 0, hasVaad = false;
  const push = (label, amt, p, formula = null) => {
    billsPaid += p;
    const owed = Math.max(0, amt - p);
    billsOwed += owed;
    if (amt > 0 && owed <= 0) { lines.push(`${label}: שולם, תודה.`); return; }
    if (formula) lines.push(formula);
    lines.push(p > 0
      ? `שולם ${nis(p)} · *נותר לתשלום ${label}: ${nis(owed)}*`
      : `*לתשלום ${label}: ${nis(amt || 0)}*`);
  };
  const meterLine = (meter, label, k, rate) => {
    const amt = itemAmount(b[k]);
    const p = itemPaid(b[k]);
    if (!amt && !p) return;
    const cons = d ? consumptionForMonth(d, apt, s, meter, month) : null;
    const formula = cons && cons.diff > 0 && cons.from && cons.to
      ? `${label}: קריאה קודמת ${num(cons.from.value)} · נוכחית ${num(cons.to.value)} · צריכה ${num(Math.round(cons.diff * 10) / 10)} ${UNIT[meter]} × ₪${rate}`
      : `${label}:`;
    push(label, amt, p, amt > 0 && p < amt ? formula : null);
  };
  meterLine("elec", "חשמל", "elec", rateElec(apt, s));
  meterLine("water", "מים", "water", rateWS(apt, s));
  meterLine("gas", "גז", "gas", rateGas(apt, s));
  const flat = (k, label) => {
    const amt = itemAmount(b[k]);
    const p = itemPaid(b[k]);
    if (!amt && !p) return;
    if (k === "vaad") hasVaad = true;
    push(label, amt, p, amt > 0 && p < amt ? `${label}: ${nis(amt)}` : null);
  };
  flat("sewage", "ביוב");
  flat("vaad", "ועד בית");

  if (lines.length) {
    out.push("חשבונות הצריכה:");
    /* סעיף עם יתרה — הנוסחה והסכום המודגש; ששולם — שורת תודה אחת. */
    out.push(...lines);
    out.push(billsOwed > 0
      ? `*סה"כ לתשלום חשבונות ${hasVaad ? "צריכה וועד בית" : "צריכה"}: ${nis(billsOwed)}*`
      : "*כל החשבונות שולמו, תודה.*");
    if (billsOwed > 0) out.push(RULE);
    out.push("");
  }

  /* אישור תשלום — לומר גם איך נרשם: דייר ששילם במזומן צריך לראות מזומן. */
  if (mode === "receipt" && d) {
    const via = [...new Set([
      d.payments?.[rowKey(apt.id, month)]?.method,
      ...BILLS.map((x) => b?.[x.k]?.method),
    ].filter(Boolean))].map(methodLabel);
    if (via.length) out.push(`התשלום נרשם: ${via.join(", ")}.`, "");
  }

  const how = (rentOwed + billsOwed) > 0 ? payHow(s) : "";
  return `${out.join("\n").replace(/\n{3,}/g, "\n\n").trim()}${how}\nתודה!`;
}

/* ============================================================================
   הודעת חשבונות הצריכה לדייר (1.9.2026 — נוסח מדויק לפי בקשת המשתמש):
   שם פרטי בלבד בפתיחה, כותרת+נוסחה נפרדת לכל סעיף עם מונה (הקריאה
   הישנה-חדשה=הפרש*תעריף=סכום, באותיות ומספרים — לא במילים כמו קודם),
   סה״כ מודגש עם קו כפול, ובלי הוראות תשלום בכלל. rentText היא בדיוק
   אותו ניסוח ששכר הדירה קיבל בתוך billsText, רק עצמאית.
   ============================================================================ */
const CONS_HEADER = { elec: "חשבון החשמל", water: "חשבון המים והביוב", gas: "חשבון הגז" };
function consumptionBillsText(apt, bill, month, settings, d, mode) {
  const s = settings || defTariffs();
  const b = bill || emptyBill();
  const num = (n) => Number(n).toLocaleString("he-IL", { maximumFractionDigits: 2 });
  /* בלי בידוד כיווניות, נוסחה כמו "650-600=50*0.65=32.5" בתוך טקסט עברי
     עלולה להיהפך על ידי אלגוריתם ה-bidi (כל מקטע מספרים מוצג בסדר הפוך
     ביחס לשכניו). U+2066/U+2069 עוטפים אותה כך שתישאר ברצף אחד. */
  const iso = (s) => `⁦${s}⁩`;
  /* וואטסאפ מזהה "170 - 166.4" כמספר טלפון ומציג אותו ככחול ומקושר, גם
     עם רווחים סביב המקף (נצפה בפועל בהודעה אמיתית). תו רוחב-אפס אחרי
     המספר הראשון שובר את הרצף שהזיהוי בודק, בלי שום שינוי נראה לעין. */
  const ZWSP = "​";
  const firstName = String(apt.tenant || "").trim().split(/\s+/)[0] || "";
  const out = [`היי ${firstName}`];
  if (mode === "reminder") out.push("תזכורת לתשלום:");

  const lines = [];
  let owedTotal = 0;
  /* סעיף עם מונה: הנוסחה מחושבת מחדש מהקריאות (חדשה-ישנה=הפרש*תעריף),
     לא מהסכום השמור — כדי שהמספר בהודעה יהיה בדיוק מה שהדייר יכול
     לאמת בעצמו מול המונה, גם אם השמור מעוגל לשקל שלם. */
  const meterLine = (meter, header, k, rate) => {
    const amt = itemAmount(b[k]);
    const p = itemPaid(b[k]);
    if (!amt && !p) return;
    const cons = d ? consumptionForMonth(d, apt, s, meter, month) : null;
    if (cons && cons.diff > 0 && cons.from && cons.to) {
      /* "לחודש X" מתאר את החודש שהקריאה הסוגרת נופלת בסופו, לא את חודש
         החיוב שלה במערכת: קריאה שנלקחת ב-1 לחודש סוגרת את תקופת החיוב
         של אותו חודש במערכת, אבל בפועל מודדת בעיקר את החודש שעבר — יום
         לפניה. לפי הקריאה הקודמת (cons.from) במקום זה היה שובר כשהקריאה
         הקודמת ישנה מאוד (מונה שלא נקרא זמן רב): התאריך שלה, לא זה של
         הצריכה הנוכחית, קפץ להודעה כ"לחודש נובמבר 2023". */
      lines.push(`${header} שלך לחודש ${monthLabel(dayBefore(cons.to.date).slice(0, 7))}`);
      const diff = Math.round(cons.diff * 10) / 10;
      const cost = Math.round(diff * rate * 100) / 100;
      const owed = Math.max(0, cost - p);
      owedTotal += owed;
      /* שתי שורות-חישוב נפרדות (חיסור ואז כפל), כל אחת עם התוצאה שלה
         בשורה משלה — לפי הפורמט שהמשתמש ביקש (בהשראת שיתוף חישוב
         ממחשבון). רווח סביב כל אופרטור ו-× במקום * לכפל: (1) בלי רווחים,
         "170-166.4" נראה לוואטסאפ כמו מספר טלפון ומודגש/מקושר בכחול
         בטעות. (2) * הוא גם תו ההדגשה של וואטסאפ — שורה עם מספר אי-זוגי
         של כוכביות גורמת לזיווג שגוי שמדגיש קטע לא נכון. */
      lines.push(
        iso(`${num(cons.to.value)}${ZWSP} - ${num(cons.from.value)}`),
        iso(`= ${num(diff)}`),
        "",
        iso(`${num(diff)} × ${rate}`),
        p > 0 ? iso(`= ${num(cost)} ש"ח`) : iso(`= *${num(cost)} ש"ח*`),
      );
      if (p > 0) lines.push(`שולם ${num(p)} · נותר *${num(owed)} ש"ח*`);
    } else {
      lines.push(`${header} שלך לחודש ${monthLabel(month)}`);
      if (amt > 0 && Math.max(0, amt - p) <= 0) {
        lines.push("שולם, תודה.");
      } else {
        const owed = Math.max(0, amt - p);
        owedTotal += owed;
        lines.push(p > 0 ? `${num(amt)} ש"ח · שולם ${num(p)} · נותר *${num(owed)} ש"ח*` : `*${num(amt)} ש"ח*`);
      }
    }
    lines.push(RULE, "");
  };
  meterLine("elec", CONS_HEADER.elec, "elec", rateElec(apt, s));
  meterLine("water", CONS_HEADER.water, "water", rateWS(apt, s));
  meterLine("gas", CONS_HEADER.gas, "gas", rateGas(apt, s));
  /* סעיפים בלי מונה (ביוב ישן, ועד בית) — אין נוסחה, רק סכום. */
  const flatLine = (k, header) => {
    const amt = itemAmount(b[k]);
    const p = itemPaid(b[k]);
    if (!amt && !p) return;
    lines.push(`${header} שלך לחודש ${monthLabel(month)}`);
    const owed = Math.max(0, amt - p);
    if (amt > 0 && owed <= 0) {
      lines.push("שולם, תודה.");
    } else {
      owedTotal += owed;
      lines.push(p > 0 ? `${num(amt)} ש"ח · שולם ${num(p)} · נותר *${num(owed)} ש"ח*` : `*${num(amt)} ש"ח*`);
    }
    lines.push(RULE, "");
  };
  flatLine("sewage", "חשבון הביוב");
  flatLine("vaad", "חשבון ועד הבית");

  if (lines.length) {
    out.push("", ...lines);
    out.push(`*סה"כ לתשלום: ${num(owedTotal)} ש"ח*`);
    out.push(RULE, RULE);
  } else {
    out.push("", "אין חשבון פתוח החודש.");
  }
  out.push("תודה");
  return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

function rentText(apt, month, settings, d, mode, tenancyId) {
  const s = settings || defTariffs();
  const rent = Number(effectiveRent(apt, month)) || 0;
  const rp = d ? rentPaidOf(d, apt.id, month, tenancyId) : 0;
  const owed = Math.max(0, rent - rp);
  const out = [`שלום ${apt.tenant},`];
  if (mode === "reminder") out.push("תזכורת לתשלום:");
  out.push(`${monthLabel(month)} · ${apt.name}`, "");
  if (owed <= 0) {
    out.push("שכר הדירה התקבל, תודה.");
  } else if (rp > 0) {
    out.push(`שכר הדירה התקבל חלקית (${nis(rp)} מתוך ${nis(rent)}) — נא להשלים בהקדם:`);
    out.push(`*יתרת שכר דירה: ${nis(owed)}*`);
  } else {
    out.push("שכר הדירה טרם התקבל — נא להעביר בהקדם:");
    out.push(`*שכר דירה לתשלום: ${nis(rent)}*`);
  }
  const how = owed > 0 ? payHow(s) : "";
  return `${out.join("\n").replace(/\n{3,}/g, "\n\n").trim()}${how}\nתודה!`;
}

/* תזכורות חוב קצרות ללוחות הגבייה (CollectionBoards) — ליד כפתור
   "לחיצה — התקבל" של כל דייר, לא ליד שמו. בכוונה קצרות בהרבה מ-rentText/
   billsText: לפי בקשת המשתמש, רק סכום החוב הפתוח הכולל (לא רק החודש
   המוצג) ובקשה להסדיר אותו — בלי פירוט חודש-חודש ובלי "איך משלמים".
   tenancyId חובה (לא אופציונלי כמו ב-rentText) כי כאן תמיד יש שורה
   קונקרטית של תקופה — לוחות הגבייה בנויים כך. */
function rentDebtReminderText(apt, d, tenancyId) {
  const amount = debtStatement(d, apt, CUR_KEY, { tenancyId }).rentDue;
  return `שלום ${apt.tenant},\nאני מזכיר לך שיש לך חוב פתוח על סך *${nis(amount)}*.\nאבקש להסדיר את החוב בהקדם.\nתודה.`;
}

function vaadDebtReminderText(apt, d, tenancyId) {
  const st = debtStatement(d, apt, CUR_KEY, { tenancyId });
  const amount = st.months.reduce((t, x) => t + Math.max(0, itemDue(x.bills.record?.vaad)), 0);
  return `שלום ${apt.tenant},\nאני מזכיר לך שיש לך חוב פתוח בוועד הבית על סך *${nis(amount)}*.\nאבקש להסדיר את החוב בהקדם.\nתודה.`;
}

/* ============================================================================
   ההודעה לדייר עם חוב נגרר.

   billsText מתאר חודש אחד. כשיש חוב מחודשים קודמים, הודעה שמציגה רק את
   החודש הנוכחי מציגה סכום קטן מהאמת — הדייר משלם אותו, מרגיש שסגר, וממשיך
   לגרור את הישן. כאן הסכום הכולל הוא השורה הראשונה, אחריו חלוקה לחוב
   קודם ולחודש, ורק אז הפירוט. כשאין חוב קודם — ההודעה החודשית הרגילה.
   ============================================================================ */
function statementText(apt, d, month = CUR_KEY, settings = null, mode = null) {
  const s = settings || d?.settings || defTariffs();
  const st = debtStatement(d, apt, month);
  const bill = d?.bills?.[rowKey(apt.id, month)] || emptyBill();
  const m = mode || messageMode(apt, d, month);

  if (!st.arrears.length) return billsText(apt, bill, month, true, s, d, m);

  const rows = st.arrears.map((x) => {
    const parts = [
      x.rent.due > 0 && `שכר דירה ${nis(x.rent.due)}`,
      x.bills.due > 0 && `חשבונות ${nis(x.bills.due)}`,
    ].filter(Boolean);
    return `• ${monthLabel(x.month)}: ${nis(x.due)}${parts.length > 1 ? `\n   ${parts.join(" · ")}` : ""}`;
  });
  const body = billsBody(apt, bill, month, true, s, d);
  const currentDue = st.current?.due || 0;

  return `שלום ${apt.tenant},
ריכוז חוב · ${apt.name}

${st.rentDue > 0 && st.billsDue > 0 ? `*שכר דירה — חוב פתוח: ${nis(st.rentDue)}*\n*חשבונות — חוב פתוח: ${nis(st.billsDue)}*\n` : ""}*סה"כ לתשלום: ${nis(st.total)}*
${RULE}
מזה חוב קודם ${nis(st.arrearsTotal)} · ${monthLabel(month)} ${nis(currentDue)}

חודשים קודמים שטרם שולמו:
${rows.join("\n")}

${monthLabel(month)}${currentDue > 0 ? `: ${nis(currentDue)}` : ": שולם במלואו"}
${body.lines.join("\n")}

אם כבר העברת חלק מהסכום — תודה, נשמח לאישור ונעדכן.${payHow(s)}`;
}

/* בחירת נוסח ההודעה לפי מצב התשלום בפועל */
function messageMode(apt, d, mk_) {
  const b = d.bills[rowKey(apt.id, mk_)] || emptyBill();
  const chargedRent = Number(effectiveRent(apt, mk_)) || 0, paidRent = rentPaidOf(d, apt.id, mk_);
  const balance = (chargedRent - paidRent) + billsDue(b);
  const anyPaid = paidRent > 0 || BILLS.some((x) => itemPaid(b[x.k]) > 0);
  if (balance <= 0 || (anyPaid && balance <= 0)) return "receipt";
  if (anyPaid) return "receipt";
  const overdue = now.getDate() > Math.max(rentDayOf(apt, d.settings), billsDayOf(apt, d.settings));
  return overdue ? "reminder" : "notice";
}

const waLink = (phone, text) => `https://wa.me/${waPhone(phone)}?text=${encodeURIComponent(text)}`;

/* ============ סוגי נכס ============
   מקור אחד לכל מה שנגזר מסוג הנכס: הניסוח בחוזה, מי מקבל את חשבונות
   הצריכה, והאם מדובר במגורים בכלל.

   metered = החשבונות עוברים דרך המשכיר לפי מונים. ביחידת דיור זה המצב
   הרגיל, בדירה שלמה השוכר פותח חשבונות על שמו, ובנכס מסחרי זה תלוי אם
   יש מונה משנה — ברירת המחדל שם היא כן, כי לרוב אין חיבור נפרד.

   שימו לב: תבנית החוזה נכתבה לשכירות מגורים. לנכס מסחרי היא מתאימה את
   הניסוח הבסיסי בלבד, ואינה מחליפה חוזה מסחרי שנבדק על ידי עורך דין. */
const PROPERTY_TYPES = [
  { k: "unit", label: "יחידת דיור", noun: "יחידת דיור", feminine: true, residential: true, metered: true, hint: "החשבונות דרכי, לפי מונים" },
  { k: "apartment", label: "דירה", noun: "דירה", feminine: true, residential: true, metered: false, hint: "החשבונות ישירות מול הספקים" },
  { k: "shop", label: "חנות", noun: "חנות", feminine: true, residential: false, metered: true, purpose: "לשימוש מסחרי", hint: "נכס מסחרי" },
  { k: "warehouse", label: "מחסן", noun: "מחסן", feminine: false, residential: false, metered: true, purpose: "לאחסנה", hint: "אחסנה בלבד" },
  { k: "office", label: "משרד", noun: "משרד", feminine: false, residential: false, metered: true, purpose: "לשימוש משרדי", hint: "נכס מסחרי" },
  { k: "other", label: "אחר", noun: "מושכר", feminine: false, residential: false, metered: true, purpose: "לשימוש שהוסכם בין הצדדים", hint: "כל סוג אחר" },
];

const propertyType = (apt) => {
  const found = PROPERTY_TYPES.find((t) => t.k === (apt?.propertyType || "unit"));
  const t = found || PROPERTY_TYPES[0];
  return { ...t, purpose: t.purpose || "למגורים" };
};

/* ---------------- חוזה ---------------- */
/* טווח שכר דירה מדורג — הסכום שבתוקף בתאריך נתון */
const rentAt = (a, date) => {
  const steps = (a.rentSteps || []).filter((x) => x.from && x.from <= date).sort((x, y) => (x.from < y.from ? -1 : 1));
  return steps.length ? Number(steps[steps.length - 1].rent) : Number(a.rent || 0);
};
/* חודש ראשון חלקי: כשהתקופה מתחילה באמצע החודש, שכר הדירה של החודש
   הראשון שלה יכול להיקבע בשתי דרכים — גוברות זו על זו לפי הסדר:
   1. firstMonthAmount — סכום מפורש שהוסכם בעל פה ואינו בהכרח החישוב
      המדויק לפי ימים (למשל סכום עגול).
   2. prorateFirstMonth — חיוב יחסי לפי מספר הימים שהדייר גר בו מתוך
      סך ימי החודש, כשלא סוכם סכום מפורש אחר.
   ברירת המחדל (שני השדות ריקים) ממשיכה להיות חודש מלא — כמו לפני
   שהשדות האלה נוספו, ולכן כל התקופות הקיימות לא משתנות. */
const effectiveRent = (a, mk_) => {
  const full = rentAt(a, `${mk_}-28`);
  if (!a.leaseStart) return full;
  const leaseMonth = a.leaseStart.slice(0, 7);
  if (mk_ !== leaseMonth) return full;
  if (a.firstMonthAmount != null) return Number(a.firstMonthAmount) || 0;
  if (!a.prorateFirstMonth) return full;
  const startDay = Number(a.leaseStart.slice(8, 10));
  if (!(startDay > 1)) return full;
  const [y, m] = mk_.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const occupiedDays = daysInMonth - startDay + 1;
  return Math.round(full * occupiedDays / daysInMonth);
};

/* אותו רעיון כמו effectiveRent, לסעיפים קבועים (ועד בית, ארנונה, היטל
   עירוני): amountField הוא שם השדה עם הסכום המלא (למשל "vaad"), וליד
   כל אחד שני שדות תאומים בשם ${amountField}ProrateFirstMonth ו-
   ${amountField}FirstMonthAmount — בדיוק כמו prorateFirstMonth/
   firstMonthAmount של שכר הדירה. ברירת המחדל (שני השדות ריקים) נשארת
   הסכום המלא, כמו תמיד — תקופות קיימות לא משתנות. */
const effectiveFixedBill = (a, mk_, amountField) => {
  const full = Number(a[amountField] || 0);
  if (!a.leaseStart) return full;
  const leaseMonth = a.leaseStart.slice(0, 7);
  if (mk_ !== leaseMonth) return full;
  const firstAmount = a[`${amountField}FirstMonthAmount`];
  if (firstAmount != null) return Number(firstAmount) || 0;
  if (!a[`${amountField}ProrateFirstMonth`]) return full;
  const startDay = Number(a.leaseStart.slice(8, 10));
  if (!(startDay > 1)) return full;
  const [y, m] = mk_.split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  const occupiedDays = daysInMonth - startDay + 1;
  return Math.round(full * occupiedDays / daysInMonth);
};
const effectiveVaad = (a, mk_) => effectiveFixedBill(a, mk_, "vaad");

/* נוסח סעיף שמירת שבת ומועדי ישראל — קבוע ומיוצא כך שתצוגה מקדימה באשף
   כניסת הדייר (לפני שעונים כן/לא) תציג בדיוק את מה שייכנס לחוזה, בלי
   סיכון שהתצוגה תסטה מהנוסח האמיתי. */
const SHABBAT_CLAUSE_TITLE = "שמירת השבת ומועדי ישראל";
const SHABBAT_CLAUSE_TEXT = [
  "   א. השוכר מתחייב לכבד את השבת ואת מועדי ישראל, ולהימנע במהלכם מכל עבודה רועשת במושכר או בסביבתו, לרבות קדיחה, נגרות, שיפוצים, תיקונים או הפעלת כלי עבודה חשמליים.",
  "   ב. השוכר מתחייב שלא להדליק אש, מנגל או נרגילה בשבת, ביום הכיפורים ובמועדי ישראל.",
  "   ג. השוכר מתחייב שלא להשמיע מוזיקה בשבת, ביום הכיפורים ובמועדי ישראל בעוצמה הנשמעת מחוץ למושכר.",
  "   ד. הצדדים מסכימים כי סעיף זה הוא תנאי מהותי בהסכם, ולהפרתו החוזרת ונשנית לאחר התראה בכתב יראו כהפרה יסודית.",
].join("\n");

function contractText(apt, c, settings) {
  /* נוסח מותאם אישית — בעל הנכס ערך את החוזה או כתב אותו מאפס. מוחזר
     כלשונו: התבנית לא נוגעת בו, וכך מה שנחתם הוא בדיוק מה שנכתב. */
  if (c && typeof c.customText === "string" && c.customText.trim()) return c.customText;
  const s = settings || defTariffs();
  const k = apt.intake || {};
  const kind = propertyType(apt);
  const vaadMode = utilityMode(apt, "vaad");
  const vaadOn = vaadMode === "landlord";
  /* ארנונה: ברירת המחדל בישראל היא שהשוכר משלם ישירות לרשות. החוזה חייב
     לומר במפורש מי משלם — אחרת זו בדיוק המחלוקת שמגיעה לבית משפט. */
  const arnonaOn = fixedBillOn(apt, "arnona");
  const municipalOn = fixedBillOn(apt, "municipal");
  const municipalName = String(apt.municipalLabel || "").trim() || "היטל עירוני";
  const steps = (apt.rentSteps || []).filter((x) => x.from && x.rent);
  /* מה שנמסר לדייר הזה; אם לא נרשם — רשימת הציוד של הנכס. כך נכס
     שהמקרר והתנור שלו רשומים פעם אחת לא דורש הקלדה מחדש בכל שכירות. */
  const contents = String(apt.contents || apt.inventory || "").split("\n").map((x) => x.trim()).filter(Boolean);
  const houseRules = String(s.houseRules || "").split("\n").map((x) => x.trim()).filter(Boolean);
  const guarantors = apt.guarantors || [];
  const persons = Number(k.persons) || 0;
  /* סעיף שבת: קביעה ברמת הנכס (clauseShabbat על הדירה) גוברת על ההגדרה
     הכללית. נכס בלי קביעה ממשיך לפי ההגדרות — חוזים קיימים לא משתנים. */
  const shabbatOn = apt.clauseShabbat != null ? apt.clauseShabbat === true : s.clauseShabbat !== false;

  /* ---------------------------------------------- סעיף 4: תשלומי צריכה ---
     נבנה סעיף-סעיף לפי מי משלם למי, ולא כשני נוסחים קבועים "הכול דרך
     המשכיר" / "הכול ישירות". תמהיל מעורב הוא המצב הרגיל, וחוזה שמצהיר
     על אחיד כשבפועל הוא מעורב הוא בדיוק המחלוקת שמגיעה לבית משפט. */

  const entryOf = { elec: k.entryElec, water: k.entryWater, gas: k.entryGas };
  const entryNote = (u) => (entryOf[u.k] ? ` קריאת המונה במועד המסירה: ${entryOf[u.k]}.` : "");
  const authorityName = apt.authority || "הרשות המקומית";

  const waterExtra = s.waterQuota && persons
    ? `\n      מכסת המים המוזלת היא ${s.waterQuota} מ"ק לנפש לחודש עבור ${persons} נפשות; צריכה מעבר למכסה תחויב בתעריף של ${s.waterRateAbove || rateWS(apt, s)} ₪ למ"ק.`
    : s.waterRateAbove
      ? `\n      צריכה מעבר למכסה המוזלת תחויב בתעריף של ${s.waterRateAbove} ₪ למ"ק.`
      : "";

  const balloonNote = k.gasBalloon
    ? ` במעמד קבלת המפתח קיבל השוכר בלון גז מלא${k.gasAmount ? ` בעלות ${nis(k.gasAmount)}` : ""}${k.gasSmallAmount ? `, וכן בלון קטן מלא בעלות ${nis(k.gasSmallAmount)}` : ""}. השוכר אחראי להחלפת הבלונים מול ספק הגז לפי הצורך.`
    : "";

  /** שורת סעיף אחד — הניסוח נקבע לפי מי משלם למי. */
  function utilityLine(u) {
    const mode = utilityMode(apt, u.k);

    /* "ללא חיוב" — לא שותקים על הסעיף אלא אומרים אותו. שתיקה בחוזה על
       חשמל היא בדיוק המקום שבו צד אחד יטען אחר כך שהתכוונו אחרת. */
    if (mode === "none") {
      return u.k === "arnona"
        ? `ארנונה — המשכיר נושא בתשלום הארנונה ל${authorityName}, ולא יחול על השוכר תשלום נפרד בגינה.`
        : `${u.label} — לא יחול על השוכר תשלום נפרד בגין ${u.label} במהלך תקופת השכירות; העלות על המשכיר.${entryNote(u)}`;
    }

    if (u.k === "arnona") {
      return mode === "direct"
        ? `ארנונה — השוכר יירשם כמחזיק במושכר בספרי ${authorityName} וישלם את הארנונה ישירות לרשות, החל ממועד המסירה ועד למועד הפינוי בפועל.`
        : `ארנונה — המשכיר משלם את הארנונה ל${authorityName}, והשוכר משיב לו אותה בסך ${nis(apt.arnona)} לחודש. שינוי בשיעור הארנונה על ידי הרשות ישתקף בתשלום.`;
    }

    if (mode === "direct") {
      return `${u.label} — החשבון יירשם על שם השוכר, והוא ישלם אותו ישירות ל${u.supplier} לכל תקופת השכירות. אין חיוב על סעיף זה מול המשכיר.${entryNote(u)}${u.k === "gas" ? balloonNote : ""}`;
    }

    // דרך המשכיר. בלי תעריף אין מה לחייב — המונה מתועד בלבד.
    if (u.k === "gas" && !rateGas(apt, s)) {
      return `גז — באמצעות בלוני גז.${balloonNote}${entryNote(u)}`;
    }
    const rate = rateFor(apt, s, u.meter);
    return `${u.label} — לפי קריאת מונה, בתעריף של ${rate} ₪ ל${u.unit}${u.k === "water" ? " (מים וביוב יחד)" : ""}.${entryNote(u)}${u.k === "water" ? waterExtra : ""}`;
  }

  const utilityItems = [
    ...UTILITIES.map(utilityLine),
    `התשלומים החלים מול המשכיר ישולמו עד ה-${billsDayOf(apt, s)} בכל חודש${billsCycleOf(apt, s) === 2 ? ", בחיוב דו-חודשי" : ""}.`,
    ...(municipalOn ? [`${municipalName} — בסך ${nis(apt.municipal)} לחודש, ישולם למשכיר יחד עם יתר התשלומים.`] : []),
    `מעבר לתשלומים המפורטים לעיל${vaadOn ? " ולוועד הבית" : ""} לא יחולו על השוכר תשלומים נוספים בגין המושכר.`,
  ];

  const utilitiesClause = `4. תשלומים שוטפים
${utilityItems.map((line, i) => `   ${HE_LETTERS[i] || i + 1}. ${line}`).join("\n")}`;

  /* --------------------------------- סעיף 5: אחריות מול ספקי הצריכה ---
     נכנס רק כשיש סעיף שמשולם ישירות. זה הסעיף שבו בעל נכס נכווה: השוכר
     לא העביר את החשבון על שמו, צבר חוב, עזב — והחוב רודף את הנכס ואת
     המשכיר. לכן הוא מפורש, ממוספר, ומגובה בביטחון. */

  const direct = directUtilities(apt);
  const directNames = direct.map((u) => `${u.label} (${u.supplier})`).join(", ");
  const utilityCheck = securityList(apt, s).find((v) => v.k === "utilityCheck");

  /* המספור של הסעיפים שאחרי זה קבוע לאורך כל המסמך, ולכן אחריות הספקים
     ושינוי התעריפים יושבים יחד בסעיף 5 ולא כשני סעיפים. */
  const directClause = direct.length
    ? `   הסעיפים הבאים משולמים על ידי השוכר ישירות לספק ואינם עוברים דרך המשכיר: ${directNames}.
   השוכר מצהיר כי ידוע לו שהאחריות לתשלומם היא שלו בלבד, ומתחייב:
   א. להעביר את החשבונות על שמו בתוך 14 ימים ממועד קבלת החזקה, ולהמציא למשכיר אישור על כך.
   ב. לשלם את החשבונות במלואם ובמועדם לכל תקופת השכירות, לרבות חובות שנצברו בתקופתו והתגלו לאחר מכן.
   ג. להמציא למשכיר בתום תקופת השכירות, ולא יאוחר ממועד הפינוי, אישורי סילוק חובות מכל אחד מהספקים, ולהעביר את החשבונות בחזרה על שם המשכיר.
   ד. לא שילם השוכר חשבון שחל עליו והמשכיר נדרש לשלמו — ישפה השוכר את המשכיר מיד עם דרישה, בתוספת הפרשי הצמדה וריבית.
   ה. ידוע לשוכר כי ניתוק המושכר מחשמל, ממים או מגז עקב אי-תשלום ייחשב הפרה יסודית של הסכם זה.
   ${utilityCheck
    ? `ו. להבטחת האמור בסעיף זה מסר השוכר שיק ביטחון על סך ${nis(utilityCheck.amount)}, שיופקד רק כנגד חוב לספק שהמשכיר נדרש לשלמו בפועל, וכנגד הצגת אסמכתה.`
    : `ו. הצדדים רשאים להסכים על ביטחון ייעודי להבטחת האמור בסעיף זה.`}

`
    : "";

  const utilitiesSection = `${utilitiesClause}

5. אחריות מול ספקי הצריכה ושינוי תעריפים
${directClause}   ידוע לשוכר כי תעריפי החשמל, המים, הביוב, הארנונה והגז נקבעים על ידי הספקים והרשויות ועשויים להשתנות מעת לעת במהלך תקופת השכירות, והשוכר יישא בהם כפי שיהיו בפועל.
   ככל שסעיף נגבה דרך המשכיר, שינוי תעריף יחול על החיובים ממועד כניסתו לתוקף אצל הספק וללא צורך בתיקון הסכם זה. התעריפים הנקובים בסעיף 4 משקפים את המחירים הידועים במועד החתימה בלבד.`;

  /* איך משלמים את שכר הדירה — לפי ברירת המחדל שנבחרה בהגדרות. הנוסח
     הקודם התחייב על שיקים חודשיים גם כשבפועל סוכמה הוראת קבע, וזה
     סעיף שאפשר להיתלות בו. */
  const months = Math.max(1, Math.round((new Date(c.to) - new Date(c.from)) / 2629800000));
  /* מה שסוכם מול הדייר הזה (payMethodRent על התקופה) גובר על ברירת המחדל
     מההגדרות; מספר השיקים — כפי שנרשם (rentChecks), ואם לא — אחד לכל
     חודש בחוזה. */
  const rentMethod = rentMethodOf(apt, s);
  const checksGiven = Math.round(Number(apt.rentChecks) || 0);
  const checks = checksGiven > 0 ? checksGiven : months;
  const rentPayLine = rentMethod === "check"
    ? `עם חתימת הסכם זה ימסור השוכר למשכיר ${checks} שיקים חודשיים${checks === months ? ", אחד לכל חודש שכירות" : ""}.`
    : rentMethod === "standing"
      ? "התשלום ייעשה בהוראת קבע לחשבון המשכיר, שאותה יסדיר השוכר עם חתימת הסכם זה."
      : rentMethod === "transfer"
        ? "התשלום ייעשה בהעברה בנקאית לחשבון המשכיר עד המועד האמור בכל חודש."
        : `התשלום ייעשה ב${methodLabel(rentMethod)} עד המועד האמור בכל חודש.`;

  const rentClause = `2. דמי השכירות
   דמי השכירות החודשיים הם ${nis(c.rent)}, אשר ישולמו מראש עד ה-${rentDayOf(apt, s)} בכל חודש.${steps.length ? `\n   הצדדים הסכימו על עדכון מדורג של דמי השכירות במהלך תקופת השכירות:\n${steps.map((x) => `      החל מ-${heDate(x.from)} — ${nis(x.rent)} לחודש`).join("\n")}\n   העדכון ייעשה אוטומטית במועדים אלה וללא צורך בהודעה נוספת.` : ""}${s.indexPct ? `\n   דמי השכירות צמודים לעליית מדד המחירים לצרכן, עד ${s.indexPct}% לשנה.` : "\n   דמי השכירות אינם צמודים למדד."}${s.lateFeePct ? `\n   על איחור בתשלום יחול פיצוי מוסכם של ${s.lateFeePct}% לחודש.` : ""}
   ${rentPayLine}`;

  /* הביטחונות נבחרים לכל שכירות בנפרד, והמספור נגזר ממה שנבחר בפועל.
     הגרסה הקודמת קידדה א/ב/ג/ד קשיח, ולכן חוזה בלי הסעיף הראשון יצא
     ממוספר מ-ב׳ והלאה. */
  const secLines = securityClauseLines(apt, s);
  const securityClause = secLines.length
    ? `3. ביטחונות
   להבטחת קיום התחייבויותיו ימסור השוכר למשכיר במעמד החתימה:
${secLines.join("\n")}
   הביטחונות יוחזרו לשוכר במעמד החזרת המושכר, לאחר שהוחזר צבוע, נקי ופנוי לשביעות רצון המשכיר, ובקיזוז חובות או נזקים אם יהיו.`
    : `3. ביטחונות
   הצדדים הסכימו כי לא יימסרו ביטחונות בגין הסכם זה.`;

  /* ועד בית משתנה בהחלטת ועד הבניין ולא בהסכמת הצדדים, וזו הפתעה שכיחה
     לשוכר באמצע שנה. החוזה אומר את זה מראש ובמפורש. שלושה מצבים: דרך
     המשכיר (הרגיל), ישירות לוועד (הסכום ידוע אך לא עובר דרך המשכיר),
     ואין ועד בכלל. */
  const vaadClause = vaadMode === "landlord"
    ? `6. ועד בית
   השוכר יישא בתשלום ועד הבית, שסכומו במועד חתימת הסכם זה הוא ${nis(apt.vaad)} לחודש.
   מובהר לשוכר כי סכום ועד הבית נקבע על ידי ועד הבניין או אסיפת הדיירים, ואינו נתון להסכמת הצדדים להסכם זה. הסכום עשוי להשתנות במהלך תקופת השכירות בעקבות החלטת הוועד, לרבות בשל תקציב שוטף, עבודות או היטל חד-פעמי — והשוכר יישא בו כפי שיהיה בפועל, ללא צורך בתיקון הסכם זה.
   שינוי כאמור יחול ממועד כניסתו לתוקף לפי החלטת הוועד. המשכיר ימסור לשוכר הודעה על השינוי בסמוך לאחר שנודע לו עליו.`
    : vaadMode === "direct"
    ? `6. ועד בית
   השוכר ישלם את דמי ועד הבית ישירות לוועד הבית/לנציגות הבניין, בסך ${nis(apt.vaad)} לחודש נכון למועד חתימת הסכם זה; אין חיוב על סעיף זה מול המשכיר.
   מובהר לשוכר כי סכום ועד הבית נקבע על ידי ועד הבניין או אסיפת הדיירים, ואינו נתון להסכמת הצדדים להסכם זה, ועשוי להשתנות במהלך תקופת השכירות בעקבות החלטת הוועד.`
    : `6. ועד בית
   לא חל על השוכר תשלום ועד בית בגין המושכר. ככל שיוחל תשלום כאמור במהלך תקופת השכירות בהחלטת ועד הבניין, יישא בו השוכר מהמועד שנקבע בהחלטה, והצדדים יעדכנו זאת בכתב.`;

  /* מסעיף 7 והלאה הסעיפים נבנים כרשימה וממוספרים לפי מה שנכלל בפועל.
     קודם המספרים היו קשיחים בטקסט, ולכן חוזה עם ערבים וגם עם הערות
     יצא עם שני סעיפים שמספרם 19, וסעיף שהוסר השאיר חור במספור. */
  const tail = [
    ["אי תחולת הגנת הדייר",
      `   השוכר מצהיר כי ידוע לו שאינו דייר מוגן, וכי הוא שוכר את המושכר בשכירות שאינה מוגנת לפי חוק הגנת הדייר (נוסח משולב), התשל"ב-1972, וכי לא שילם ולא ישלם דמי מפתח.`],
    ["ביטוח ואחריות",
      `   המשכיר אינו אחראי לכל נזק גוף או רכוש שייגרם לשוכר, לבני ביתו או לאורחיו במושכר או בסביבתו, למעט נזק שנגרם ברשלנותו של המשכיר. השוכר אחראי לתכולת המושכר שבבעלותו ורשאי לבטחה על חשבונו.`],
    ["השימוש במושכר",
      [
        /* השימוש נגזר מסוג הנכס. "למגורי השוכר בלבד" נכתב גם בחוזה של
           חנות, וסתר את המבוא של אותו חוזה עצמו ("לשימוש מסחרי"). */
        `   א. השימוש במושכר ${kind.residential ? "למגורי השוכר בלבד" : `${kind.purpose} בלבד`}. הפרת תנאי זה תהווה הפרה יסודית.`,
        `   ב. השוכר לא יעביר את החזקה או השימוש במושכר, כולו או חלקו, לאדם אחר, במישרין או בעקיפין, ללא הסכמת המשכיר בכתב.`,
        `   ג. השוכר מצהיר שראה את המושכר ומסכים לשוכרו במצבו הנוכחי${k.keys ? `, וכי קיבל ${k.keys} מפתחות שאותם ישיב בתום התקופה` : ""}.`,
        `   ד. השוכר ישמור על המושכר ועל המטלטלין שבו במצב תקין ונקי, וישיבם בתום התקופה במצב טוב למעט בלאי סביר.`,
        ...(kind.residential ? [`   ה. אין להכניס בעלי חיים למושכר ללא הסכמת המשכיר בכתב.`] : []),
        `   ${kind.residential ? "ו" : "ה"}. אין לזרוק מגבונים או חומרים שאינם מתכלים לאסלה ולמערכת הביוב, ואין לשפוך שמן או שומן לכיור ולמערכת הביוב.`,
        `   ${kind.residential ? "ז" : "ו"}. השוכר ישמור על ניקיון המושכר, החצר והמרפסות המשותפות.`,
      ].join("\n")],
    /* כללי בית — נוסח פרטי שהיה קשיח לכל המשתמשים. עכשיו בחירה
       בהגדרות: דלוק בחשבונות קיימים, כבוי בחשבון חדש. */
    ...(shabbatOn ? [[SHABBAT_CLAUSE_TITLE, SHABBAT_CLAUSE_TEXT]] : []),
    ...(s.clauseYard !== false ? [["השימוש במרפסות ובחצר",
      [
        `   א. השוכר מתחייב שלא לעשות על האש, שלא להדליק נרגילה ושלא להשליך בדלי סיגריות על הדשא.`,
        `   ב. אין להדליק אש בקרבת בלוני הגז או מתקני החשמל.`,
        `   ג. אין להשאיר פסולת או ציוד בחצר ובמרפסות.`,
        `   ד. ידוע לשוכר כי המשכיר עושה שימוש מעת לעת במערכת הגברה בחצר או בבית.`,
      ].join("\n")]] : []),
    ...(houseRules.length ? [["כללי הבית",
      houseRules.map((x, i) => `   ${HE_LETTERS[i] || i + 1}. ${x}`).join("\n")]] : []),
    ["תחזוקה ותיקונים",
      [
        `   א. השוכר יודיע למשכיר מיד על כל ליקוי שיתגלה במושכר או באביזריו, והמשכיר יתקנו בהקדם הסביר.`,
        `   ב. תיקוני בלאי סביר ותשתית — לרבות צנרת, מערכת חשמל, דוד ומזגנים — יבוצעו על ידי המשכיר ועל חשבונו.`,
        `   ג. נזק שנגרם בשל שימוש בלתי סביר של השוכר, בני ביתו או אורחיו — יתוקן על חשבון השוכר.`,
        `   ד. המשכיר רשאי לבצע את התיקון בעצמו או באמצעות מי מטעמו, בתיאום עם השוכר.`,
      ].join("\n")],
    ["שינויים במושכר",
      `   השוכר לא יבצע כל שינוי, תוספת או קדיחה במבנה ללא אישור המשכיר בכתב. כל שינוי שאושר יהפוך לקניין המשכיר ללא תמורה, אלא אם הוסכם אחרת בכתב.`],
    ["כניסת המשכיר למושכר",
      `   השוכר יאפשר למשכיר להיכנס למושכר בתיאום מראש ובשעה סבירה, לצורך בדיקת מצבו, ולקראת תום התקופה גם לצורך הצגתו לשוכרים פוטנציאליים.`],
    ["הודעה מוקדמת וסיום",
      `   השוכר יודיע למשכיר ${s.noticeMonths || 3} חודשים לפני תום התקופה אם אינו מעוניין להאריכה. פינה השוכר את המושכר לפני תום התקופה, יישא בדמי השכירות עד תום התקופה, אלא אם יימצא שוכר חלופי המקובל על המשכיר.`],
    ["פינוי המושכר",
      `   בתום התקופה יחזיר השוכר את המושכר צבוע, נקי ופנוי מכל חפציו. כל עיכוב בפינוי יחייב את השוכר בפיצוי מוסכם של ${nis(s.lateVacateFee || 500)} לכל יום איחור, מבלי לגרוע מכל סעד אחר.`],
    ["הפרות",
      `   הפרה יסודית של הסכם זה מזכה את המשכיר בביטולו לאלתר ובדרישה לפינוי המושכר, וכן בפיצוי בגין נזקיו. איחור בתשלום העולה על 14 ימים ייחשב הפרה יסודית.`],
    ["כללי",
      [
        `   א. פרוטוקול מסירה ותמונות מצב המושכר במועד הכניסה מהווים חלק בלתי נפרד מהסכם זה.`,
        `   ב. כל שינוי בהסכם ייעשה בכתב ובחתימת שני הצדדים.`,
        `   ג. כתובות הצדדים לצורך הסכם זה הן כמפורט במבוא.`,
      ].join("\n")],
    ...(guarantors.length ? [["ערבות",
      [
        `   להבטחת מילוי כל התחייבויות השוכר על פי הסכם זה, חתמו הערבים המפורטים בנספח ב' על כתב ערבות.`,
        `   הערבים ערבים יחד ולחוד, ביחד עם השוכר ובנפרד ממנו, לכל התחייבויותיו הכספיות והאחרות לפי הסכם זה, לרבות דמי שכירות, תשלומי צריכה ונזקים.`,
        `   הערבות תעמוד בתוקפה עד למילוי מלוא התחייבויות השוכר ולהחזרת המושכר בהתאם להסכם, ולא תפקע עקב שינוי בתנאי ההסכם או הארכת תקופת השכירות.`,
        `   המשכיר רשאי לפנות לערבים לאחר שהשוכר לא תיקן הפרה בתוך 14 ימים מקבלת התראה בכתב.`,
      ].join("\n")]] : []),
    ...(c.notes ? [["תנאים נוספים שסוכמו",
      `   ${String(c.notes).split("\n").filter(Boolean).join("\n   ")}`]] : []),
  ];
  // הסעיפים 1–6 קבועים; מכאן ממשיכים ברצף.
  const numbered = tail.map(([title, body], i) => `${i + 7}. ${title}\n${body}`).join("\n\n");

  /* ---------------------------------------------------------- הנספחים ---
     נספח ב' נושא עכשיו את פרטי הערב המלאים ואת מצב החתימה שלו. עד כאן
     הוא רשם שם ות"ז בשורה אחת, בלי מקום לחתימה ובלי לומר אם נחתם —
     ומי שהדפיס את החוזה קיבל כתב ערבות שאי אפשר לחתום עליו. */
  const guaranteeBlock = (g, i) => {
    const lines = [
      `ערב ${i + 1}: ${g.name || "________"}`,
      `   ת.ז.: ${g.idNumber || "________"}`,
      `   טלפון: ${g.phone || "________"}`,
      `   כתובת: ${g.address || "________"}`,
    ];
    lines.push(g.signature || g.signatureId
      ? `   חתם/ה דיגיטלית${g.signedAt ? ` ביום ${heDate(g.signedAt)}` : ""}${g.idPhoto ? ", וצירף/ה צילום תעודת זהות" : ""}.`
      : "   חתימה: ______________________     תאריך: ____________");
    return lines.join("\n");
  };

  const appendixB = guarantors.length
    ? `\n\n\nנספח ב' — כתב ערבות

אנו החתומים מטה, ערבים בזאת יחד ולחוד לכל התחייבויות השוכר ${apt.tenant || ""} על פי הסכם השכירות שבנדון, לרבות דמי שכירות, תשלומי צריכה, נזקים ופינוי המושכר במועד.
ידוע לנו כי הערבות תישאר בתוקפה עד למילוי מלוא התחייבויות השוכר, וכי היא אינה מותנית במיצוי הליכים כנגדו.
הערבות מתייחסת לתקופת השכירות שמיום ${heDate(c.from)} ועד ליום ${heDate(c.to)}, ולכל הארכה שלה.

${guarantors.map(guaranteeBlock).join("\n\n")}`
    : "";

  const appendices = `נספח א' — מטלטלין שנמסרו לשוכר במעמד קבלת המושכר
${contents.length ? contents.map((x, i) => `${i + 1}. ${x}`).join("\n") : "1. ______________________\n2. ______________________\n3. ______________________"}

השוכר מאשר כי קיבל את המטלטלין המפורטים לעיל במצב תקין, וכי ישיבם בתום תקופת השכירות במצב טוב למעט בלאי סביר.${appendixB}`;

  /* ======================================================================
     נספח הארכה.

     עד כאן "הארכה" הפיקה את החוזה המלא מחדש, עם כל שמונה-עשר הסעיפים —
     ובסעיף 1 כתבה "יתר תנאי ההסכם המקורי נותרים בתוקפם". מסמך שאומר
     "השאר נשאר" ואז חוזר על השאר סותר את עצמו, ומי שקורא אותו לא יודע
     אם מדובר בחוזה חדש או בתוספת.

     נספח הוא מסמך קצר: מי, על מה, מה משתנה, מה מוסיפים, וחתימות. שאר
     ההסכם ממשיך לחול בלי שנגעו בו.
     ====================================================================== */
  if (c.type === "extension") {
    const extra = String(c.notes || "").split("\n").map((x) => x.trim()).filter(Boolean);
    const prevRent = Number(c.prevRent) || 0;
    const changes = [
      `א. תקופת השכירות מוארכת מיום ${heDate(c.from)} ועד ליום ${heDate(c.to)} (כולל).`,
      prevRent && Number(c.rent) !== prevRent
        ? `ב. דמי השכירות בתקופה המוארכת יהיו ${nis(c.rent)} לחודש, במקום ${nis(prevRent)}.`
        : `ב. דמי השכירות בתקופה המוארכת יהיו ${nis(c.rent)} לחודש, ללא שינוי.`,
      `ג. מועד התשלום, אופן החיוב על הצריכה והביטחונות נותרים כפי שנקבעו בהסכם המקורי, אלא אם נאמר אחרת בנספח זה.`,
    ];

    return `בס"ד

נספח הארכה להסכם שכירות בלתי מוגנת

שנערך ונחתם ב${s.ownerAddress || "________"} ביום ${heDate(c.signedAt || c.createdAt || c.from)}

בין: ${s.ownerName || "________"}${s.ownerId ? `, ת.ז. ${s.ownerId}` : ""}${s.ownerPhone ? `, טלפון ${s.ownerPhone}` : ""}
     להלן "המשכיר", מצד אחד

לבין: ${apt.tenant || "________"}${k.tenantId ? `, ת.ז. ${k.tenantId}` : ""}${apt.phone ? `, טלפון ${apt.phone}` : ""}
     להלן "השוכר", מצד שני

הואיל והצדדים התקשרו בהסכם שכירות בלתי מוגנת ביחס ל${kind.noun} הנמצא${kind.feminine ? "ת" : ""} ב${apt.name}, להלן "ההסכם המקורי";
והואיל והצדדים מעוניינים להאריך את תקופת השכירות;

לפיכך הוסכם בין הצדדים כדלקמן:

1. השינויים המוסכמים
${changes.map((x) => `   ${x}`).join("\n")}${extra.length ? `

2. תנאים נוספים שסוכמו
${extra.map((x, i) => `   ${HE_LETTERS[i] || i + 1}. ${x}`).join("\n")}` : ""}

${extra.length ? 3 : 2}. תוקף ההסכם המקורי
   כל יתר תנאי ההסכם המקורי — לרבות השימוש במושכר, התשלומים השוטפים, הביטחונות, התחזוקה והפינוי — נותרים בתוקפם ללא שינוי, ויחולו גם על התקופה המוארכת.
   נספח זה מהווה חלק בלתי נפרד מההסכם המקורי.${guarantors.length ? `

${extra.length ? 4 : 3}. ערבות
   הערבים החתומים על כתב הערבות שבהסכם המקורי מאשרים בחתימתם כי ערבותם חלה גם על התקופה המוארכת ועל תנאיה.` : ""}

ולראיה באו הצדדים על החתום:


המשכיר: ______________________          השוכר: ______________________${guarantors.length ? `\n\n${guarantors.map((g, i) => `ערב ${i + 1} (${g.name || "________"}${g.idNumber ? `, ת.ז. ${g.idNumber}` : ""}): ______________________`).join("\n")}` : ""}`;
  }

  return `בס"ד

${"הסכם שכירות בלתי מוגנת"}

שנערך ונחתם ב${s.ownerAddress || "________"} ביום ${heDate(c.signedAt || c.createdAt || c.from)}

בין: ${s.ownerName || "________"}${s.ownerId ? `, ת.ז. ${s.ownerId}` : ""}${s.ownerAddress ? `, כתובת ${s.ownerAddress}` : ""}${s.ownerPhone ? `, טלפון ${s.ownerPhone}` : ""}
     להלן "המשכיר", מצד אחד

לבין: ${apt.tenant || "________"}${k.tenantId ? `, ת.ז. ${k.tenantId}` : ""}${apt.phone ? `, טלפון ${apt.phone}` : ""}${k.partnerName ? `\n      ו-${k.partnerName}${k.partnerId ? `, ת.ז. ${k.partnerId}` : ""}${k.partnerPhone ? `, טלפון ${k.partnerPhone}` : ""}` : ""}
     להלן "השוכר", מצד שני

הואיל והמשכיר הוא בעל הזכויות ב${kind.noun} ${apt.rooms ? `${kind.residential ? "בת" : "בגודל"} ${apt.rooms} ` : ""}הנמצא${kind.feminine ? "ת" : ""} ב${apt.name}, להלן "המושכר";
והואיל ובמושכר מצויים מטלטלין כמפורט בנספח א' המהווה חלק בלתי נפרד מהסכם זה;
והואיל והשוכר מעוניין לשכור את המושכר ${kind.purpose} בלבד, והצדדים מסכימים כי השכירות אינה מוגנת;

לפיכך הוסכם והותנה בין הצדדים כדלקמן:

1. תקופת השכירות
   ${c.type === "extension" ? "תקופת השכירות מוארכת בזאת" : "תקופת השכירות היא"} מיום ${heDate(c.from)} ועד ליום ${heDate(c.to)} (כולל).${c.type === "extension" ? "\n   יתר תנאי ההסכם המקורי נותרים בתוקפם, למעט השינויים המפורטים בנספח זה." : ""}

${rentClause}

${securityClause}

${utilitiesSection}

${vaadClause}

${numbered}

ולראיה באו הצדדים על החתום:


${appendices}`;
}


/* ============ קריאות מונה ============
   כל קריאה נשמרת כרשומה עם תאריך: קריאת הכניסה של הדייר, קריאות חודשיות
   וקריאות שהדייר שולח בצילום. הצריכה תמיד מחושבת מול הקריאה הקודמת
   בפועל — גם אם עברו חודשיים — וכל קריאה ניתנת לתיקון או למחיקה. */
const readingsOf = (d, aptId, meter) =>
  (d.readings || []).filter((r) => r.aptId === aptId && r.meter === meter)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

const lastReading = (d, aptId, meter) => {
  const floor = readingFloor(d, aptId, TODAY);
  const l = readingsOf(d, aptId, meter).filter((r) => !floor || r.date >= floor);
  return l[l.length - 1] || null;
};
/* הקריאה שלפני קריאה נתונה — לפי הסדר ברשימה, כך שגם שתי קריאות
   באותו יום (למשל קריאת כניסה וקריאה שהוזנה מיד אחריה) עובדות. */
/* גבול תחתון להשוואת קריאות: תחילת התקופה שבתוקף בתאריך הקריאה.
   כך הצריכה של דייר חדש נמדדת מקריאת הכניסה שלו ולא מזו של קודמו.

   אותו דייר ברצף — חידוש חוזה, או רישום שנמחק ונוצר מחדש יום אחרי —
   אינו "דייר חדש": הרצפה נמשכת אחורה עד תחילת הרצף שלו. בלי זה קריאת
   הכניסה שנרשמה לפני תחילת התקופה החדשה נחתכת, הקריאה הבאה מוצגת
   כ"קריאה ראשונה" והצריכה שביניהן לא מחויבת לעולם. */
const readingFloor = (d, aptId, date) => {
  const list = (d.tenancies || []).filter((x) => x.aptId === aptId && x.tenant && x.leaseStart <= date)
    .sort((a, b) => (a.leaseStart < b.leaseStart ? -1 : 1));
  let t = list.pop();
  if (!t) return null;
  const sameTenant = (a, b) => String(a.tenant).trim() === String(b.tenant).trim();
  while (list.length) {
    const p = list[list.length - 1];
    /* רצף: אותו שם דייר, והתקופה הקודמת נמשכה לפחות עד יום לפני תחילת
       הבאה. leaseEnd קודם ל-closedAt — ברישום שנמחק closedAt הוא יום
       המחיקה, לא סוף מגורים. פער אמיתי (עזב וחזר) שובר את הרצף. */
    const end = p.leaseEnd || p.closedAt || null;
    if (!sameTenant(p, t) || (end && end < dayBefore(t.leaseStart))) break;
    t = list.pop();
  }
  return t.leaseStart;
};
const dayBefore = (iso) => {
  const x = new Date(iso);
  x.setDate(x.getDate() - 1);
  return x.toISOString().slice(0, 10);
};

const readingBefore = (d, aptId, meter, date, skipId) => {
  const floor = readingFloor(d, aptId, date);
  const l = readingsOf(d, aptId, meter).filter((r) => !floor || r.date >= floor);
  const i = skipId ? l.findIndex((r) => r.id === skipId) : l.length;
  if (i > 0) return l[i - 1];
  const before = l.filter((r) => r.id !== skipId && r.date <= date);
  return before.length > 1 ? before[before.length - 2] : null;
};
const daysBetween = (a, b) => Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000));
const UNIT = { elec: "קוט״ש", water: "מ״ק", gas: "מ״ק" };

/** כמות צריכה לתצוגה — ספרה אחת אחרי הנקודה, בלי שברי נקודה צפה. */
const fmtQty = (n) => {
  const v = Math.round((Number(n) || 0) * 10) / 10;
  return v.toLocaleString("he-IL", { maximumFractionDigits: 1 });
};

/**
 * עיגול סכום חשבון בלחיצה. dir: 1 = חץ למעלה, ‎-1 = למטה.
 *
 * חיוב לפי מונה יוצא עם אגורות, והדייר מעביר שקלים שלמים. הלחיצה
 * הראשונה מוחקת את האגורות (לשקל השלם הקרוב בכיוון שנבחר), וכל לחיצה
 * נוספת מזיזה שקל שלם אחד באותו כיוון — לחיצה = שקל, עד לכל סכום
 * שנוח לבעל הנכס. (עד 25.8.2026 שקל שלם קפץ לעשרות, וכפולה של 10
 * נתקעה — אי אפשר היה לכוון סכום מדויק.) סכום חיובי לעולם לא מתעגל
 * לאפס — עיגול אינו מחיקת חשבון.
 */
function roundBillAmount(value, dir) {
  const n = Number(value) || 0;
  if (n <= 0) return 0;
  const up = dir > 0;
  const out = !Number.isInteger(n)
    ? (up ? Math.ceil(n) : Math.floor(n))
    : n + (up ? 1 : -1);
  return out > 0 ? out : n;
}

/**
 * חודש החיוב שקריאה בתאריך נתון שייכת אליו: החודש עצמו כשהוא חודש
 * חיוב, אחרת חודש החיוב הבא (בגבייה דו-חודשית הקריאה "נשמרת לחיוב הבא").
 */
function billingMonthFor(a, s, date) {
  const mk_ = String(date || TODAY).slice(0, 7);
  return isBillingMonth(a, s, mk_) ? mk_ : nextBillingMonth(a, s, mk_) || mk_;
}

/**
 * הצריכה שמחויבת בחודש חיוב נתון.
 *
 * עד כאן החשבון היה "ההפרש בין שתי הקריאות האחרונות" — ולכן קריאה שנייה
 * באותו חודש (הדייר שלח צילום אחרי שבעל הנכס כבר קרא) דרסה את החשבון
 * והשאירה רק את הקטע האחרון; הצריכה שבין הקריאה הראשונה לשנייה נעלמה.
 *
 * הכלל כאן: תקופת החיוב של חודש M מתחילה בראשון לחודש שאחרי חודש החיוב
 * הקודם (בגבייה חודשית — M עצמו; בדו-חודשית — גם החודש שלפניו), והצריכה
 * היא מהקריאה האחרונה שלפני התקופה ועד הקריאה האחרונה בתוכה. כמה שלא
 * יהיו קריאות בתוך התקופה, הסכום תמיד מכסה את כולה.
 *
 * מחזירה null כשאין קריאה בתוך התקופה (אין ממה לחשב), ו-diff 0 כשיש רק
 * קריאה אחת בלי קודמת (תחילת שכירות).
 */
function consumptionForMonth(d, apt, s, meter, mk_) {
  if (!apt || !isBillingMonth(apt, s, mk_)) return null;
  const cycle = billsCycleOf(apt, s);
  let startKey = mk_;
  for (let i = 1; i < cycle; i++) startKey = prevMonth(startKey);
  const periodStart = `${startKey}-01`;
  const periodEndExcl = `${nextMonth(mk_)}-01`;

  /* גבול תחתון — תחילת התקופה שבתוקף: קריאת הכניסה של דייר חדש, ולא
     קריאת היציאה של קודמו. כשאין קריאה מתחילת השכירות נופלים לקריאה
     האחרונה שיש (ההתנהגות של היום). */
  const floor = readingFloor(d, apt.id, `${mk_}-28`);
  const all = readingsOf(d, apt.id, meter);
  const inPeriod = all.filter((r) => r.date >= periodStart && r.date < periodEndExcl && (!floor || r.date >= floor));
  if (!inPeriod.length) return null;
  const to = inPeriod[inPeriod.length - 1];

  const before = all.filter((r) => r.date < periodStart);
  const beforeInTenancy = floor ? before.filter((r) => r.date >= floor) : before;
  /* אין קריאה בתקופת המגורים לפני תקופת החיוב: כשהמגורים התחילו בתוך
     תקופת החיוב עצמה הבסיס הוא קריאת הכניסה (הראשונה בתוך התקופה) — לא
     קריאה של קודמו. כשהמגורים ותיקים יותר נופלים לקריאה האחרונה שיש
     (ההתנהגות של היום). */
  const from = beforeInTenancy.length
    ? beforeInTenancy[beforeInTenancy.length - 1]
    : (floor && floor >= periodStart ? inPeriod[0] : (before[before.length - 1] || inPeriod[0]));

  const diff = from.id === to.id ? 0 : Number(to.value) - Number(from.value);
  return { from, to, diff, days: from.id === to.id ? 0 : daysBetween(from.date, to.date), periodStart, periodEnd: `${mk_}-${pad(new Date(Number(mk_.slice(0, 4)), Number(mk_.slice(5, 7)), 0).getDate())}` };
}

/* קריאת מונה + חישוב מיידי של החשבון, בעדכון אחד כדי שכלום לא ידרוס כלום.
   פונקציה משותפת לכל מסך שמזין קריאות (מונים וחשבונות, היסטוריית
   תשלומים וחובות, לוחות הגבייה) — עברה הנה מ-web/src/App.jsx (1.9.2026)
   כדי שקומפוננטות בקבצים שונים ישתמשו באותה לוגיקה בדיוק, בלי לשכפל
   אותה או ליצור import מעגלי בין קבצי web. מקבלת setData (ה-state
   setter של React) כפרמטר — לא React בעצמה, לכן מותר לה לחיות כאן. */
function recordMeterReading(setData, aptId, meter, value, date, photoId = null) {
  setData((d) => {
    const apt = d.apartments.find((x) => x.id === aptId);
    const rec = { id: uid(), aptId, meter, value: Number(value), date, photoId: photoId || null, note: "" };
    const nd = { ...d, readings: [...(d.readings || []), rec].sort((x, y) => (x.date < y.date ? -1 : 1)) };
    /* החשבון מתעדכן לחודש החיוב שהקריאה שייכת אליו (לא לחודש המוצג), ועל
       פני כל תקופת החיוב — קריאה שנייה באותו חודש לא מוחקת את הראשונה.
       billsMeter — סעיף שהשוכר משלם ישירות לספק מתועד ואינו מחויב; הקריאה
       עצמה תמיד נשמרת: היא הראיה למצב המונה בכניסה וביציאה. מה ששולם
       לא משתנה: אם הסכום עלה יופיע "נותרו". */
    const bm = apt ? billingMonthFor(apt, d.settings, date) : null;
    if (apt && bm && bm <= CUR_KEY && billsMeter(apt, d.settings, meter)) {
      const cons = consumptionForMonth(nd, apt, d.settings, meter, bm);
      if (cons && cons.diff > 0) {
        const k = rowKey(aptId, bm);
        const b = { ...(nd.bills[k] || emptyBill()) };
        const amount = Math.round(cons.diff * rateFor(apt, d.settings, meter, cons.to.date));
        const bill = meter === "elec" ? "elec" : meter === "gas" ? "gas" : "water";
        const it = { ...b[bill], amount };
        it.paid = amount > 0 && itemPaid(it) >= amount;
        b[bill] = it;
        nd.bills = { ...nd.bills, [k]: b };
      }
    }
    return nd;
  });
}
function updateMeterReading(setData, id, patch) {
  setData((d) => ({ ...d, readings: (d.readings || []).map((r) => (r.id === id ? { ...r, ...patch } : r)) }));
}
function deleteMeterReading(setData, id) {
  setData((d) => ({ ...d, readings: (d.readings || []).filter((r) => r.id !== id) }));
}

/**
 * נקודת "שולם עד" על סעיף צריכה — הקריאה האחרונה של תקופת החיוב ברגע
 * שנרשם התשלום. היא שקובעת אילו קריאות נעולות לעריכה: מה שקדם לה נגבה
 * ונעול; קריאה שנוספה אחריה (והגדילה את החשבון ל"נותרו") פתוחה לתיקון
 * ולמחיקה עד שגם היתרה שלה תיגבה — ואז הנקודה מתקדמת אליה.
 *
 * נכתבת רק כשפעולת התשלום עצמה רצה — עדכון סכום בלבד לא מזיז אותה;
 * ביטול התשלום מוחק אותה. ביוב נגזר ממונה המים; סעיף בלי מונה (ועד,
 * ארנונה, גז קבוע) נשאר בלי הנקודה. חשבון שנגבה לפני שהשדה היה קיים —
 * בלי הנקודה, וכל קריאות החודש שלו נעולות (ההתנהגות השמרנית).
 */
function withPaidThrough(d, apt, mk_, billKey, item) {
  const meter = billKey === "sewage" ? "water" : billKey;
  if (!apt || !METERS.includes(meter)) return item;
  if (itemPaid(item) > 0) {
    const cons = consumptionForMonth(d, apt, d.settings || {}, meter, mk_);
    if (cons?.to) return { ...item, paidThrough: { value: Number(cons.to.value), date: cons.to.date } };
    return item;
  }
  if (item.paidThrough === undefined) return item;
  const { paidThrough, ...rest } = item;
  return rest;
}

const METERS = ["elec", "water", "gas"];
const METER_LABEL = { elec: "חשמל", water: "מים", gas: "גז" };
/* ברירת המחדל היא חשמל — כך התנהגו הפעולות מאז ומתמיד כשהגיע ערך לא מוכר. */
const meterOf = (v) => (METERS.includes(String(v)) ? String(v) : "elec");

/* ============ שפיות של קריאה חדשה ============
   ספרה אחת שנקראה לא נכון מהתמונה — 4310 במקום 431 — נכנסת בשקט ומייצרת
   חשבון של אלפי שקלים, כי הבדיקה היחידה שהייתה כאן היא "לא נמוכה מהקודמת".
   לכן קריאה שמשמעותה צריכה יומית חריגה נעצרת לאישור מפורש. היא לעולם לא
   נדחית — יש חודשים חריגים באמת — אבל היא לא נכנסת בלי שמישהו ראה אותה.

   אותה פונקציה רצה בדפדפן ובשרת, כדי שהדייר לא יקבל אישור על מסך אחד
   ושגיאה מהשרת רגע אחרי. */

/* צריכה יומית סבירה כשאין עדיין היסטוריה. גבוה בכוונה: התפקיד הוא לתפוס
   טעות קריאה של סדר גודל, לא לשפוט הרגלי צריכה. */
const DAILY_CAP = { elec: 60, water: 3, gas: 5 };
/* מתחת להפרש הזה לא בודקים יחס — בקריאות קטנות כל רעש נראה כמו קפיצה. */
const JUMP_FLOOR = { elec: 100, water: 5, gas: 5 };
const JUMP_RATIO = 4;

/**
 * @returns {{ok:boolean, code:string|null, ...detail}} code:
 *  not_a_number | future_date | below_previous | jump | null (תקין)
 */
function readingSanity(d, aptId, meter, value, date = TODAY) {
  const m = meterOf(meter);
  const n = Number(value);

  if (value === "" || value == null || !Number.isFinite(n) || n < 0) {
    return { ok: false, code: "not_a_number" };
  }
  if (date > TODAY) return { ok: false, code: "future_date", date };

  const floor = readingFloor(d, aptId, date);
  const list = readingsOf(d, aptId, m)
    .filter((r) => (!floor || r.date >= floor) && r.date <= date);
  const prev = list[list.length - 1] || null;

  // קריאה ראשונה בתקופה — אין מול מה להשוות, וזה תקין.
  if (!prev) return { ok: true, code: null, previous: null };

  const detail = { previous: prev.value, previousDate: prev.date };
  if (n < prev.value) return { ok: false, code: "below_previous", ...detail };

  const days = daysBetween(prev.date, date);
  const diff = n - prev.value;
  const perDay = diff / days;

  // הממוצע היומי במרווחים הקודמים של אותה תקופת שכירות
  const rates = [];
  for (let i = 1; i < list.length; i++) {
    const step = list[i].value - list[i - 1].value;
    if (step > 0) rates.push(step / daysBetween(list[i - 1].date, list[i].date));
  }
  const usable = rates.slice(-6);
  const baseline = usable.length ? usable.reduce((t, r) => t + r, 0) / usable.length : null;

  // רצפה מוחלטת גם כשהממוצע ההיסטורי אפסי (דירה שעמדה ריקה חודש).
  const limit = Math.max(baseline ? baseline * JUMP_RATIO : DAILY_CAP[m], DAILY_CAP[m] / 3);
  const suspicious = perDay > limit && diff >= (JUMP_FLOOR[m] || 0);

  return { ok: !suspicious, code: suspicious ? "jump" : null, ...detail, days, diff, perDay, baseline };
}

/* ============================================================================
   אומדן מס על הכנסות משכר דירה.

   שלושת מסלולי המס על שכירות למגורים בישראל, מחושבים על מה שנגבה בפועל
   (payments) בשנת מס קלנדרית. ההשוואה נעשית תמיד על כל הנכסים יחד — המס
   הוא של בעל הנכס, לא של נכס בודד. נכסים שאינם למגורים (חנות, מחסן,
   משרד) אינם זכאים לפטור ולא למסלול 10%, ולכן נספרים בנפרד (other).
   אומדן בלבד — לא ייעוץ מס.
   ============================================================================ */
const TAX_TRACKS = [
  { k: "exempt", label: "מסלול הפטור", hint: "פטור עד התקרה החודשית; מעליה כל שקל עודף מקטין את הפטור בשקל, והחלק החייב ממוסה במס השולי" },
  { k: "flat10", label: "מסלול 10%", hint: "10% מכל ההכנסה ברוטו, בלי פטור ובלי ניכוי הוצאות; לתשלום עד 30 בינואר של השנה העוקבת" },
  { k: "marginal", label: "מס שולי", hint: "מס שולי מלא על ההכנסה בניכוי ההוצאות שנרשמו" },
];

function rentTax(d, year) {
  const s = d.settings || {};
  const ceiling = Number(s.taxCeiling) || 5654;
  const marginalPct = Number(s.taxMarginalRate) || 31;
  const rate = marginalPct / 100;

  const months = [];
  let residential = 0, other = 0, taxableExempt = 0;
  for (let m = 1; m <= 12; m++) {
    const mk_ = mKey(year, m);
    let res = 0, com = 0;
    (d.apartments || []).forEach((a) => {
      const amt = rentPaidOf(d, a.id, mk_);
      if (propertyType(a).residential) res += amt; else com += amt;
    });
    /* הפטור נבחן חודש-חודש, לא על הממוצע השנתי: עד התקרה פטור מלא;
       בין התקרה לכפולה שלה כל שקל עודף מוחק שקל פטור, ולכן החלק החייב
       הוא כפל העודף; מכפל התקרה ומעלה אין פטור כלל. */
    const taxable = res <= ceiling ? 0 : res >= ceiling * 2 ? res : (res - ceiling) * 2;
    months.push({ month: mk_, residential: res, other: com, taxable });
    residential += res; other += com; taxableExempt += taxable;
  }

  /* הוצאות שנרשמו השנה על נכסי מגורים — מוכרות במסלול המס השולי בלבד.
     במסלול 10% אין ניכוי, ובמסלול הפטור הניכוי חלקי ומורכב — לא נכלל. */
  const resIds = new Set((d.apartments || []).filter((a) => propertyType(a).residential).map((a) => a.id));
  const expenses = (d.expenses || [])
    .filter((e) => String(e.month || "").startsWith(`${year}-`) && resIds.has(e.aptId))
    .reduce((t, e) => t + Number(e.amount || 0), 0);

  const tracks = {
    exempt: { taxable: taxableExempt, tax: Math.round(taxableExempt * rate) },
    flat10: { taxable: residential, tax: Math.round(residential * 0.1) },
    marginal: { taxable: Math.max(0, residential - expenses), tax: Math.round(Math.max(0, residential - expenses) * rate) },
  };
  // שוויון (למשל שנה בלי הכנסה) נשאר אצל מסלול הפטור — הוא גם בלי דיווח שוטף.
  const best = TAX_TRACKS.map((t) => t.k).reduce((a, b) => (tracks[b].tax < tracks[a].tax ? b : a));

  return {
    year, ceiling, marginalPct, months, residential, other, expenses,
    overCeilingMonths: months.filter((x) => x.taxable > 0).map((x) => x.month),
    tracks, best,
  };
}


export {
  securityClauseLines,
  securityTotal,
  securityList,
  defaultSecurities,
  SECURITY_BY_KEY,
  SECURITIES,
  fixedBillOn,
  UTILITIES,
  UTILITY_BY_KEY,
  UTILITY_MODES,
  UTILITY_MODE_KEYS,
  tracksMeter,
  utilityMode,
  utilityOfMeter,
  paysDirect,
  directUtilities,
  billsUtility,
  billsMeter,
  FIXED_BILLS,
  UNIT,
  METERS,
  METER_LABEL,
  meterOf,
  readingSanity,
  daysBetween,
  readingBefore,
  readingFloor,
  lastReading,
  readingsOf,
  consumptionForMonth,
  billingMonthFor,
  fmtQty,
  roundBillAmount,
  BILLS,
  BILL_ALIASES,
  CATS,
  CUR_KEY,
  CUR_M,
  CUR_Y,
  FIELD_ALIASES,
  METER_ALIASES,
  MONTHS,
  MONTHS_SHORT,
  TARIFF_ALIASES,
  TEN_FIELDS,
  TODAY,
  TYPE_ALIASES,
  accessCode,
  activeIn,
  addYears,
  loanMonthly,
  loanBalanceAfter,
  loanState,
  loanFor,
  purchaseEquity,
  currentEquity,
  refinanceQuote,
  monthsBetween,
  projectValue,
  latestValue,
  applyAction,
  applyActionFocused,
  applyActionFocusedInner,
  billsAnchorOf,
  billsCycleOf,
  billsDayOf,
  billsDue,
  billsText,
  consumptionBillsText,
  contractText,
  SHABBAT_CLAUSE_TITLE,
  SHABBAT_CLAUSE_TEXT,
  currentTenancy,
  targetTenancy,
  contractsOf,
  EXTENSION_STATUS,
  dayAfter,
  extensionRequestsOf,
  pendingExtensionRequest,
  pendingExtensionRequests,
  defTariffs,
  effectiveRent,
  effectiveFixedBill,
  effectiveVaad,
  emptyBill,
  emptyItem,
  entryFor,
  findApt,
  heDate,
  dmy,
  parseDmy,
  isBillingMonth,
  itemDue,
  itemPaid,
  localDay,
  mKey,
  messageMode,
  migrate,
  missingFor,
  monthLabel,
  newTenancy,
  nextBillingMonth,
  activeBillMonth,
  billsOutstanding,
  unpaidBillMonths,
  PAY_METHODS,
  PAY_METHOD_BY_KEY,
  methodBreakdown,
  methodLabel,
  defaultMethod,
  payMethodOf,
  rentMethodOf,
  debtStatement,
  debtTotal,
  currentDebt,
  rentDebtRows,
  openBillsRows,
  vaadDebtRows,
  consDebtRows,
  rentExpectedRows,
  rentCollectedRows,
  vaadCollectedRows,
  consPaidRows,
  expensesRows,
  totalCollectedRows,
  totalDebtRows,
  pastDebts,
  resolvedDebts,
  DEBT_RESOLUTIONS,
  debtStillOwed,
  statementText,
  nextMonth,
  nis,
  norm,
  normalizeAction,
  now,
  pad,
  pastTenancies,
  prevMonth,
  PROPERTY_TYPES,
  propertyType,
  rateElec,
  rateFor,
  rateGas,
  rateWS,
  ratesAt,
  refreshClock,
  rentAt,
  rentDayOf,
  rentDue,
  rentOverdue,
  rentPaidOf,
  rentText,
  rentDebtReminderText,
  vaadDebtReminderText,
  rentTax,
  recordMeterReading,
  updateMeterReading,
  deleteMeterReading,
  withPaidThrough,
  TAX_TRACKS,
  rowKey,
  seed,
  resetDocument,
  setGuarantors,
  syncApartments,
  sortApartments, APT_SORTS,
  tenanciesOf,
  deletedTenancies,
  tenancyAt,
  tenancyEnd,
  tenancyForMonth,
  monthDebtOwed,
  uid,
  upcomingTenancies,
  waLink,
  waPhone,
  guaranteeDeedText,
  GUARANTOR_CONSENT_TEXT,
};
